var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useContext, useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { t } from 'i18next';
import ButtonComponent from '../components/ButtonComponent';
import { useDuplicateAnnounce, useCreationAnnounce } from '../helper/hooks/useDuplicateAnnounce';
import { announce } from '../helper/announcer';
import { buildColoredTilesExpressionAria, buildColoredTilesProductAria, buildColoredTilesDimensionLabelsAria, } from '../helper/coloredTilesAria';
import { v4 as uuidv4 } from 'uuid';
import '../styles/widgets/coloredTiles.scss';
import globalStore from '../store';
import { DataContext } from '../player/DataContext';
import { ColorEnum } from '../components/ColorPicker';
import customEventHandler from '../helper/customEvents';
import { saveState } from '../helper/stateManagerHelper';
import { registerMultiSelectSaveEntries } from '../helper/multiSelectSaveCoordinator';
import { playAudio } from '../helper/audioHelper';
import { detectManualTileSnap, findSnapTargetManualTile, isProperRectangle, trimRandomEmptyEdgeRowsAndCols, trimManualEmptyEdgeRowsAndCols, selectTool, } from '../helper/helper';
import { snapTileToNumberLine } from '../helper/numberLineHelper';
import { snapToBackgroundGrid } from '../helper/snapHelper';
export var TILE_COLORS = ['#f73530', '#2a90f7', '#1dac78', '#f4c500'];
var EXPRESSION_COLORS = [
    '#f73530',
    '#2a90f7',
    '#f5a623',
    '#1dac78',
    '#9b59b6',
    '#a0522d',
    '#808080',
    '#f4c500',
    '#222222',
];
var WORKSPACE_CELL_SIZE = 110;
var PREVIEW_SIZE = 418;
var LINEAR_PIECE_MARGIN = 4;
var ColoredTiles = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
    var data = _a.data, _s = _a.isGroup, isGroup = _s === void 0 ? false : _s;
    var isPreview = !!data.preview;
    var _t = globalStore.useStoreState(function (s) { return s.playerStore; }), zoomLevel = _t.zoomLevel, playerBgPattern = _t.playerBgPattern;
    var showExpression = (_b = data.showExpression) !== null && _b !== void 0 ? _b : true;
    var showProduct = (_c = data.showProduct) !== null && _c !== void 0 ? _c : true;
    // Independent mode flags - both can be active simultaneously
    var _u = useState((_d = data.partModeEnabled) !== null && _d !== void 0 ? _d : true), partModeEnabled = _u[0], setPartModeEnabled = _u[1];
    var _v = useState((_e = data.selectModeEnabled) !== null && _e !== void 0 ? _e : false), selectModeEnabled = _v[0], setSelectModeEnabled = _v[1];
    var dataRef = useRef(data);
    var _w = useContext(DataContext), manipulative = _w.manipulative, setManipulative = _w.setManipulative, panningEnable = _w.panningEnable, color = _w.color, selectedTool = _w.selectedTool, setSelectedTool = _w.setSelectedTool, setSelectedManipulativeId = _w.setSelectedManipulativeId, setContextMenu = _w.setContextMenu;
    var duplicateLabel = useDuplicateAnnounce(data.id, t('text.coloredTiles'));
    var creationLabel = useCreationAnnounce(data.id, t('text.coloredTiles'));
    var updateArray = function (updater) {
        if (isPreview)
            return;
        setManipulative(function (prev) {
            return prev.map(function (item) {
                return item.data.id === data.id && item.data.type === 'coloredTiles'
                    ? __assign(__assign({}, item), { data: updater(item.data) }) : item;
            });
        });
    };
    var selectedPartKeys = (_f = data.selectedPartKeys) !== null && _f !== void 0 ? _f : [];
    var setSelectedPartKeys = function (keys) {
        var _a;
        if (isPreview)
            return;
        var newKeys;
        if (typeof keys === 'function') {
            newKeys = keys((_a = data.selectedPartKeys) !== null && _a !== void 0 ? _a : []);
        }
        else {
            newKeys = keys;
        }
        updateArray(function (prev) { return (__assign(__assign({}, prev), { selectedPartKeys: newKeys })); });
    };
    var toggleSelectedPartKey = function (key) {
        setSelectedPartKeys(function (prev) {
            return prev.includes(key) ? prev.filter(function (k) { return k !== key; }) : __spreadArray(__spreadArray([], prev, true), [key], false);
        });
    };
    // Track if this array model is focused
    var _x = useState(false), isFocused = _x[0], setIsFocused = _x[1];
    var isFocusedRef = useRef(false);
    // Track hover state for arrow tooltips
    var _y = useState(null), hoveredArrow = _y[0], setHoveredArrow = _y[1];
    var scale = (_g = data.scale) !== null && _g !== void 0 ? _g : 1;
    var mode = (_h = data.mode) !== null && _h !== void 0 ? _h : 'grid';
    // Keep a ref to latest data for use in event handlers
    useEffect(function () {
        dataRef.current = data;
    }, [data]);
    // Drag tracking ref (similar to NumberCharts)
    var dragInfoRef = useRef(null);
    // Track initial center and scale for undo on position drag
    var initialCenterRef = useRef(null);
    var initialScaleRef = useRef(data.scale || 1);
    var manipulativeRef = useRef(manipulative);
    useEffect(function () {
        manipulativeRef.current = manipulative;
    }, [manipulative]);
    var selectedToolRef = useRef(selectedTool);
    useEffect(function () {
        selectedToolRef.current = selectedTool;
    }, [selectedTool]);
    var isGroupDragRef = useRef(false);
    // NMP-style direct-DOM updates during multi-select drag (avoids per-frame setManipulative).
    var wrapperRef = useRef(null);
    var multiSelectStartPositions = useRef([]);
    var dragStartCenter = useRef({ x: 0, y: 0 });
    // Snapshot of all group/multi-select member centers at drag start — applied directly by the
    // dragged tile's own setManipulative call (NMP pattern), avoiding event-bus timing races.
    var groupDragSnapshotRef = useRef({});
    // NMP-style direct-DOM path for the leader during drag — avoids per-mousemove
    // setManipulative re-renders so leader and follower DOM writes happen in the same
    // frame and stay perfectly in sync. Committed to React state once on drop.
    var leaderLatestCenterRef = useRef(null);
    var leaderLatestScaleRef = useRef(null);
    var leaderLatestNumberLineIdRef = useRef(undefined);
    // Keyboard controller refs
    var controllerBtnRef = useRef(null);
    var lastFocusWasScreenReader = useRef(false);
    var prevSelectedToolRef = useRef([]);
    var isTouchPrimary = typeof window !== 'undefined' && !!((_j = window.matchMedia) === null || _j === void 0 ? void 0 : _j.call(window, '(pointer: coarse)').matches);
    var keyMoveStartRef = useRef(null);
    var keyMoveCurPosRef = useRef(null);
    var keyMoveSaveTimer = useRef(null);
    // P4: roving tabindex for cell navigation (select mode only)
    var _z = useState(null), rovingCell = _z[0], setRovingCell = _z[1];
    var cellRefs = useRef([]);
    // True when a cell is visually empty / transparent. Used by the Unsnap
    // listener to ignore already-empty cells. (Cell navigation itself is now
    // handled by the separate ColoredTilesCellPicker overlay.)
    var isCellNavSkippable = useCallback(function (d, r, c) {
        var _a;
        var cellColor = (_a = d.partColors) === null || _a === void 0 ? void 0 : _a["".concat(r, "-").concat(c)];
        if (d.generationMode === 'manual' || d.generationMode === 'outline') {
            return !cellColor;
        }
        if (d.generationMode === 'random') {
            return cellColor === 'transparent';
        }
        return false;
    }, []);
    var properRectangle = useMemo(function () {
        return isProperRectangle(data);
    }, [data]);
    var effectiveControllerLabel = useMemo(function () {
        if (duplicateLabel)
            return duplicateLabel;
        if (creationLabel) {
            // When the tile was created WITH linear pieces (e.g. popup "Red linear
            // pieces"), the controller auto-focuses and VoiceOver reads THIS label —
            // which talks over the polite live-region announcement. So fold the piece
            // counts into the creation label itself, otherwise only "Colored Tiles
            // added to workspace" is heard and the pieces are missed.
            var countNonNull = function (arr) { return (arr !== null && arr !== void 0 ? arr : []).filter(Boolean).length; };
            var vCount = countNonNull(data.snappedVerticalPieceIds) +
                countNonNull(data.snappedRightVerticalPieceIds);
            var hCount = countNonNull(data.snappedHorizontalPieceIds) +
                countNonNull(data.snappedBottomHorizontalPieceIds);
            if (vCount + hCount > 0) {
                var parts = [];
                if (vCount > 0)
                    parts.push(t('text.coloredTilesVerticalPiecesN', { count: vCount }));
                if (hCount > 0)
                    parts.push(t('text.coloredTilesHorizontalPiecesN', { count: hCount }));
                return t('text.coloredTilesAddedWithLinearPieces', {
                    pieces: parts.join(" ".concat(t('text.and'), " ")),
                });
            }
            return creationLabel;
        }
        var base = t('text.coloredTiles');
        var rowsStr = t('text.coloredTilesAriaRows', { count: data.rows });
        var colsStr = t('text.coloredTilesAriaCols', { count: data.columns });
        var dims = t('text.coloredTilesAriaDimsConnector', { rowsStr: rowsStr, colsStr: colsStr });
        var modeStr = [
            selectModeEnabled ? t('text.coloredTilesSelectModeLabel') : '',
            partModeEnabled ? t('text.coloredTilesPartModeLabel') : '',
        ]
            .filter(Boolean)
            .join(' — ');
        return modeStr ? "".concat(base, ", ").concat(dims, " \u2014 ").concat(modeStr) : "".concat(base, ", ").concat(dims);
    }, [
        duplicateLabel,
        creationLabel,
        data.rows,
        data.columns,
        selectModeEnabled,
        partModeEnabled,
        data.snappedVerticalPieceIds,
        data.snappedRightVerticalPieceIds,
        data.snappedHorizontalPieceIds,
        data.snappedBottomHorizontalPieceIds,
    ]);
    // Track if an external block is being dragged
    var _0 = useState(false), isExternalBlockDragging = _0[0], setIsExternalBlockDragging = _0[1];
    var _1 = useState(null), dragHoverCell = _1[0], setDragHoverCell = _1[1];
    /* ================= TRACK FOCUSED STATE ================= */
    useEffect(function () {
        var focused = selectedTool.includes(data.id) || (!!data.groupId && selectedTool.includes(data.groupId));
        setIsFocused(focused);
        isFocusedRef.current = focused;
        if (!focused) {
            setSelectedPartKeys([]);
        }
    }, [selectedTool, data.id, data.groupId]);
    /* ================= AUTO-FOCUS CONTROLLER BUTTON ON SELECTION ================= */
    useEffect(function () {
        var wasFocused = prevSelectedToolRef.current.includes(data.id);
        var nowFocused = selectedTool.includes(data.id);
        prevSelectedToolRef.current = selectedTool;
        if (nowFocused && !wasFocused && !isGroup && !isPreview && !data.groupId) {
            setTimeout(function () {
                var _a, _b, _c;
                if ((_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.closest('.shape-context-menu'))
                    return;
                // Skip the redundant .focus() if focus is already on the controller or
                // any descendant of this tile — re-focusing the active element resets
                // :focus-visible and kills the orange focus ring on Tab navigation.
                if ((_b = wrapperRef.current) === null || _b === void 0 ? void 0 : _b.contains(document.activeElement))
                    return;
                (_c = controllerBtnRef.current) === null || _c === void 0 ? void 0 : _c.focus();
            }, 100);
        }
    }, [selectedTool, data.id, isGroup, isPreview, data.groupId]);
    /* ================= P4: RESET ROVING CELL WHEN SELECT MODE CHANGES ================= */
    useEffect(function () {
        if (!selectModeEnabled) {
            setRovingCell(null);
        }
        else {
            setRovingCell({ r: 0, c: 0 });
            requestAnimationFrame(function () { var _a, _b; (_b = (_a = cellRefs.current[0]) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.focus(); });
        }
    }, [selectModeEnabled]);
    /* ================= RESIZE LISTENER (context-menu entry, outline mode) ================= */
    // The "Add Row" / "Remove Row" / "Add Column" / "Remove Column" context-
    // menu items dispatch this event with a {dRows, dCols} delta. We anchor
    // at the top-left corner so the bottom-right edge of the grid moves —
    // same visual effect as dragging the bottom-right resize handle by one
    // cell. resizeByOneCell already announces success / "resize blocked".
    useEffect(function () {
        var onResize = function (_a) {
            var manipulativeId = _a.manipulativeId, dRows = _a.dRows, dCols = _a.dCols;
            if (manipulativeId !== data.id)
                return;
            if (dataRef.current.generationMode !== 'outline')
                return;
            if (isPreview || isGroup)
                return;
            resizeByOneCell('bottomRight', dRows, dCols);
        };
        customEventHandler.addEventListener('coloredTilesResize', onResize);
        return function () { return customEventHandler.removeEventListener('coloredTilesResize', onResize); };
    }, [data.id, isPreview, isGroup]);
    /* ================= CELL NAV: UNSNAP LISTENER ================= */
    // Entry into cell navigation is handled by ColoredTilesCellPicker (a dialog
    // of focusable buttons — VoiceOver-friendly). This listener performs the
    // actual extraction when the picker's cell context menu fires "Unsnap".
    useEffect(function () {
        var onUnsnapCell = function (payload) {
            var _a, _b, _c, _d, _e;
            if (payload.manipulativeId !== data.id)
                return;
            if (isPreview || isGroup)
                return;
            var r = payload.row, c = payload.col;
            var d = dataRef.current;
            if (r < 0 || r >= d.rows || c < 0 || c >= d.columns)
                return;
            if (isCellNavSkippable(d, r, c))
                return; // can't unsnap an already-empty cell
            var cellKey = "".concat(r, "-").concat(c);
            // Resolve the cell's effective color (explicit partColors entry, or
            // random-mode diagonal default).
            var color = (_b = (_a = d.partColors) === null || _a === void 0 ? void 0 : _a[cellKey]) !== null && _b !== void 0 ? _b : (d.generationMode === 'random' ? TILE_COLORS[(r * 2 + c) % 4] : '');
            // 1) Mutate source: remove cell from partColors (transparent for random,
            //    delete for manual/outline) + trim empty edges.
            var oldDataClone = structuredClone(d);
            var newDataClone = structuredClone(d);
            if (!newDataClone.partColors)
                newDataClone.partColors = {};
            if (newDataClone.generationMode === 'random') {
                newDataClone.partColors[cellKey] = 'transparent';
            }
            else {
                delete newDataClone.partColors[cellKey];
            }
            if (newDataClone.selectedPartKeys) {
                newDataClone.selectedPartKeys = newDataClone.selectedPartKeys.filter(function (k) { return k !== "cell-".concat(r, "-").concat(c); });
            }
            var finalDataClone = newDataClone;
            if (d.generationMode === 'random') {
                finalDataClone = trimRandomEmptyEdgeRowsAndCols(newDataClone, WORKSPACE_CELL_SIZE);
            }
            else if (d.generationMode === 'manual') {
                finalDataClone = trimManualEmptyEdgeRowsAndCols(newDataClone, WORKSPACE_CELL_SIZE);
            }
            var isEmpty = d.generationMode === 'random'
                ? Object.values(finalDataClone.partColors || {}).every(function (v) { return v === 'transparent'; })
                : Object.keys(finalDataClone.partColors || {}).length === 0;
            if (isEmpty && d.generationMode !== 'random' && d.generationMode !== 'outline') {
                // Manual tile became empty — delete it. Save delete entry.
                setManipulative(function (prev) { return prev.filter(function (item) { return item.data.id !== d.id; }); });
                saveState({
                    type: d.id,
                    id: d.id.split('_')[1],
                    propertyName: 'delete',
                    old: JSON.stringify(oldDataClone),
                    new: undefined,
                });
            }
            else {
                updateArray(function () { return finalDataClone; });
                saveState({
                    type: d.id,
                    id: d.id.split('_')[1],
                    propertyName: 'completeData',
                    old: JSON.stringify(oldDataClone),
                    new: JSON.stringify(finalDataClone),
                });
            }
            // 2) Compute drop position: 60px gap to the right of source tile's
            //    right edge (well beyond the 40px merge threshold), aligned with
            //    the source tile's top edge.
            var scale = (_c = d.scale) !== null && _c !== void 0 ? _c : 1;
            var sourceScaledCell = WORKSPACE_CELL_SIZE * scale;
            var sourceRightEdge = d.center.x + (d.columns * sourceScaledCell) / 2;
            var sourceTopEdge = d.center.y - (d.rows * sourceScaledCell) / 2;
            var dropX = sourceRightEdge + 60;
            var dropY = sourceTopEdge;
            // 3) Dispatch creation of the new 1×1 tile. openContextMenuOnCreate tells
            //    ColoredTileController to open the new tile's context menu after the
            //    tile is committed to React state.
            customEventHandler.dispatchEvent('bottomPanel', {
                id: 'createColoredTile',
                data: {
                    color: color,
                    x: dropX,
                    y: dropY,
                    sourceId: d.id,
                    sourceLatestData: finalDataClone,
                    topLinearSegments: ((_d = d.topLinearSegments) === null || _d === void 0 ? void 0 : _d.includes(c)) ? [0] : [],
                    leftLinearSegments: ((_e = d.leftLinearSegments) === null || _e === void 0 ? void 0 : _e.includes(r)) ? [0] : [],
                    openContextMenuOnCreate: true,
                },
            });
            // 4) Clear any roving state — focus transfers to the new tile's
            //    auto-opened context menu.
            setRovingCell(null);
            announce(t('text.coloredTilesCellUnsnapped'));
        };
        customEventHandler.addEventListener('coloredTilesUnsnapCell', onUnsnapCell);
        return function () {
            customEventHandler.removeEventListener('coloredTilesUnsnapCell', onUnsnapCell);
        };
    }, [data.id, isPreview, isGroup, isCellNavSkippable, setManipulative]);
    /* ================= EXTERNAL BLOCK DRAG LISTENER ================= */
    useEffect(function () {
        var onBlockDragging = function (evData) {
            var _a, _b, _c, _d;
            if (isPreview || evData.sourceId === dataRef.current.id)
                return;
            var pos = evData.data;
            // Scale-aware bounds: tile may be visually scaled via data.scale.
            var tileScale = (_a = dataRef.current.scale) !== null && _a !== void 0 ? _a : 1;
            var scaledCell = WORKSPACE_CELL_SIZE * tileScale;
            var w = dataRef.current.columns * scaledCell;
            var h = dataRef.current.rows * scaledCell;
            var left = dataRef.current.center.x - w / 2;
            var top = dataRef.current.center.y - h / 2;
            // Drag size from dispatch (fall back to one logical cell if not provided).
            var dragW = typeof pos.w === 'number' ? pos.w : WORKSPACE_CELL_SIZE;
            var dragH = typeof pos.h === 'number' ? pos.h : WORKSPACE_CELL_SIZE;
            // Only show INSIDE-cell indicator when the dragged block actually fits inside
            // a target cell. If dragged is larger than scaledCell, the drop will resolve
            // via edge-snap to a neighbor (handled by findSnapTargetManualTile), and the
            // cell highlight would be misleading.
            var fitsInCell = dragW <= scaledCell + 1 && dragH <= scaledCell + 1;
            if (fitsInCell &&
                pos.x >= left - 10 &&
                pos.x < left + w + 10 &&
                pos.y >= top - 10 &&
                pos.y < top + h + 10) {
                setIsExternalBlockDragging(true);
                // Match findSnapTargetManualTile (Math.round) so visual indicator and actual drop
                // resolve to the same cell index.
                var r = Math.round((pos.y - top) / scaledCell);
                var c = Math.round((pos.x - left) / scaledCell);
                if (r >= 0 &&
                    r < dataRef.current.rows &&
                    c >= 0 &&
                    c < dataRef.current.columns &&
                    ((dataRef.current.generationMode === 'random' &&
                        ((_b = dataRef.current.partColors) === null || _b === void 0 ? void 0 : _b["".concat(r, "-").concat(c)]) === 'transparent') ||
                        ((dataRef.current.generationMode === 'manual' ||
                            dataRef.current.generationMode === 'outline') &&
                            (!((_c = dataRef.current.partColors) === null || _c === void 0 ? void 0 : _c["".concat(r, "-").concat(c)]) ||
                                ((_d = dataRef.current.partColors) === null || _d === void 0 ? void 0 : _d["".concat(r, "-").concat(c)]) === 'transparent')))) {
                    setDragHoverCell({ r: r, c: c });
                }
                else {
                    setDragHoverCell(null);
                }
            }
            else {
                setIsExternalBlockDragging(false);
                setDragHoverCell(null);
            }
        };
        var blockDragEndedHandler = function () {
            if (isPreview)
                return;
            setIsExternalBlockDragging(false);
            setDragHoverCell(null);
        };
        var handler = function (data) {
            if (data.id === 'blockDragging')
                onBlockDragging(data);
            if (data.id === 'blockDragEnded')
                blockDragEndedHandler();
        };
        customEventHandler.addEventListener('blocksComponent', handler);
        return function () {
            customEventHandler.removeEventListener('blocksComponent', handler);
        };
    }, [isPreview]);
    /* ================= RIGHT PANEL EVENT LISTENER ================= */
    useEffect(function () {
        var onBottomPanelClick = function (eventData) {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
            if (isPreview)
                return;
            if (!isFocusedRef.current)
                return;
            switch (eventData.id) {
                case 'addRow':
                    addRow();
                    break;
                case 'addColumn':
                    addColumn();
                    break;
                case 'removeRow':
                    removeRow();
                    break;
                case 'removeColumn':
                    removeColumn();
                    break;
                case 'toggleExpression':
                    toggleExpression();
                    break;
                case 'toggleProduct':
                    toggleProduct();
                    break;
                case 'dimensionLabels':
                    toggleDimensionLabels();
                    break;
                case 'partMode':
                    onPartClick();
                    break;
                case 'selectMode':
                    onSelectClick();
                    break;
                case 'scaleMinus':
                case 'scalePlus': {
                    // Group scaling is handled by GameController — skip here to avoid double-scaling
                    if (dataRef.current.groupId)
                        break;
                    var isPlus = eventData.id === 'scalePlus';
                    var ctData = dataRef.current;
                    var oldScale = (_a = ctData.scale) !== null && _a !== void 0 ? _a : 1;
                    var newScale_1 = isPlus
                        ? Math.min(5, Math.round((oldScale + 0.1) * 10) / 10)
                        : Math.max(0.5, Math.round((oldScale - 0.1) * 10) / 10);
                    var scaledCell_1 = WORKSPACE_CELL_SIZE * newScale_1;
                    var newTileLeft_1 = ctData.center.x - (ctData.columns * scaledCell_1) / 2;
                    var newTileTop_1 = ctData.center.y - (ctData.rows * scaledCell_1) / 2;
                    var newTileRight_1 = newTileLeft_1 + ctData.columns * scaledCell_1;
                    var newTileBottom_1 = newTileTop_1 + ctData.rows * scaledCell_1;
                    var leftVIds_1 = (_b = ctData.snappedVerticalPieceIds) !== null && _b !== void 0 ? _b : [];
                    var rightVIds_1 = (_c = ctData.snappedRightVerticalPieceIds) !== null && _c !== void 0 ? _c : [];
                    var topHIds_1 = (_d = ctData.snappedHorizontalPieceIds) !== null && _d !== void 0 ? _d : [];
                    var bottomHIds_1 = (_e = ctData.snappedBottomHorizontalPieceIds) !== null && _e !== void 0 ? _e : [];
                    var pieceStateSaves_1 = [];
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            if (item.data.type === 'coloredTiles' && item.data.id === data.id) {
                                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { scale: newScale_1 }) });
                            }
                            if (item.data.type === 'linearPiece') {
                                var pid_1 = item.data.id;
                                var leftRow = leftVIds_1.indexOf(pid_1);
                                var rightRow = rightVIds_1.indexOf(pid_1);
                                var topCol = topHIds_1.indexOf(pid_1);
                                var bottomCol = bottomHIds_1.indexOf(pid_1);
                                // Helper: emit center + scale entries for the piece.
                                var pushPieceEntries = function (newCenter) {
                                    var _a;
                                    pieceStateSaves_1.push({
                                        type: pid_1,
                                        id: pid_1.split('_')[1],
                                        propertyName: 'center',
                                        old: JSON.stringify(item.data.center),
                                        new: JSON.stringify(newCenter),
                                    }, {
                                        type: pid_1,
                                        id: pid_1.split('_')[1],
                                        propertyName: 'scale',
                                        old: JSON.stringify((_a = item.data.scale) !== null && _a !== void 0 ? _a : 1),
                                        new: JSON.stringify(newScale_1),
                                    });
                                };
                                if (leftRow >= 0) {
                                    var newCenter = {
                                        x: newTileLeft_1 - 22 * newScale_1,
                                        y: newTileTop_1 + leftRow * scaledCell_1 + scaledCell_1 / 2,
                                    };
                                    pushPieceEntries(newCenter);
                                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: newCenter, scale: newScale_1 }) });
                                }
                                if (rightRow >= 0) {
                                    var newCenter = {
                                        x: newTileRight_1 + 22 * newScale_1,
                                        y: newTileTop_1 + rightRow * scaledCell_1 + scaledCell_1 / 2,
                                    };
                                    pushPieceEntries(newCenter);
                                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: newCenter, scale: newScale_1 }) });
                                }
                                if (topCol >= 0) {
                                    var newCenter = {
                                        x: newTileLeft_1 + topCol * scaledCell_1 + scaledCell_1 / 2,
                                        y: newTileTop_1 - 21 * newScale_1,
                                    };
                                    pushPieceEntries(newCenter);
                                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: newCenter, scale: newScale_1 }) });
                                }
                                if (bottomCol >= 0) {
                                    var newCenter = {
                                        x: newTileLeft_1 + bottomCol * scaledCell_1 + scaledCell_1 / 2,
                                        y: newTileBottom_1 + 21 * newScale_1,
                                    };
                                    pushPieceEntries(newCenter);
                                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: newCenter, scale: newScale_1 }) });
                                }
                            }
                            return item;
                        });
                    });
                    saveState(__spreadArray([
                        {
                            type: data.id,
                            id: data.id.split('_')[1],
                            propertyName: 'scale',
                            old: JSON.stringify(oldScale),
                            new: JSON.stringify(newScale_1),
                        }
                    ], pieceStateSaves_1, true));
                    announce(t('text.manipulativeScaled', {
                        name: t('text.coloredTiles'),
                        direction: t(isPlus ? 'text.up' : 'text.down'),
                        value: newScale_1.toFixed(1),
                    }));
                    break;
                }
                case 'addAllVertical': {
                    var cellSize_1 = WORKSPACE_CELL_SIZE;
                    var ctData_1 = dataRef.current;
                    var tileScale_1 = (_f = ctData_1.scale) !== null && _f !== void 0 ? _f : 1;
                    var scaledCell_2 = cellSize_1 * tileScale_1;
                    var tileLeft_1 = ctData_1.center.x - (ctData_1.columns * scaledCell_2) / 2;
                    var tileTop_1 = ctData_1.center.y - (ctData_1.rows * scaledCell_2) / 2;
                    var rows = Array.from({ length: Math.ceil(ctData_1.rows) }, function (_, i) { return i; });
                    if (ctData_1.generationMode === 'manual') {
                        rows = rows.filter(function (i) { var _a; return (_a = ctData_1.partColors) === null || _a === void 0 ? void 0 : _a["".concat(i, "-0")]; });
                    }
                    else if (ctData_1.generationMode === 'random') {
                        rows = rows.filter(function (i) {
                            var _a;
                            var c = (_a = ctData_1.partColors) === null || _a === void 0 ? void 0 : _a["".concat(i, "-0")];
                            return !c || c !== 'transparent';
                        });
                    }
                    var snappedV_1 = (_g = ctData_1.snappedVerticalPieceIds) !== null && _g !== void 0 ? _g : Array(Math.ceil(ctData_1.rows)).fill(null);
                    var existingPieceIds_1 = new Set(manipulativeRef.current.map(function (p) { return p.data.id; }));
                    var emptyRows = rows.filter(function (rowIdx) {
                        var slotId = snappedV_1[rowIdx];
                        return !slotId || !existingPieceIds_1.has(slotId);
                    });
                    var newVerticalPieces_1 = emptyRows.map(function (rowIdx) {
                        var newId = "linearPiece_".concat(uuidv4().replace(/-/g, ''));
                        return {
                            rowIdx: rowIdx,
                            type: 'linearPiece',
                            widgetId: "linearPiece:".concat(uuidv4()),
                            data: {
                                id: newId,
                                type: 'linearPiece',
                                orientation: 'vertical',
                                center: {
                                    x: tileLeft_1 - 22 * tileScale_1,
                                    y: tileTop_1 + rowIdx * scaledCell_2 + scaledCell_2 / 2,
                                },
                                scale: tileScale_1,
                                dimensions: { width: 10, height: 102 },
                                lock: { positioning: false, editing: false },
                            },
                        };
                    });
                    if (newVerticalPieces_1.length > 0) {
                        var updatedVIds_1 = __spreadArray([], snappedV_1, true);
                        newVerticalPieces_1.forEach(function (p) {
                            updatedVIds_1[p.rowIdx] = p.data.id;
                        });
                        setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev.map(function (item) {
                            return item.data.id === data.id && item.data.type === 'coloredTiles'
                                ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { snappedVerticalPieceIds: updatedVIds_1 }) }) : item;
                        }), true), newVerticalPieces_1, true); });
                        saveState(__spreadArray([
                            {
                                type: data.id,
                                id: data.id.split('_')[1],
                                propertyName: 'snappedVerticalPieceIds',
                                old: JSON.stringify(snappedV_1),
                                new: JSON.stringify(updatedVIds_1),
                            }
                        ], newVerticalPieces_1.map(function (p) { return ({
                            type: p.data.id,
                            id: p.data.id.split('_')[1],
                            propertyName: 'create',
                            old: JSON.stringify(p),
                        }); }), true));
                    }
                    break;
                }
                case 'addAllHorizontal': {
                    var cellSize_2 = WORKSPACE_CELL_SIZE;
                    var ctData_2 = dataRef.current;
                    var tileScale_2 = (_h = ctData_2.scale) !== null && _h !== void 0 ? _h : 1;
                    var scaledCell_3 = cellSize_2 * tileScale_2;
                    var tileLeft_2 = ctData_2.center.x - (ctData_2.columns * scaledCell_3) / 2;
                    var tileTop_2 = ctData_2.center.y - (ctData_2.rows * scaledCell_3) / 2;
                    var cols = Array.from({ length: Math.ceil(ctData_2.columns) }, function (_, i) { return i; });
                    if (ctData_2.generationMode === 'manual') {
                        cols = cols.filter(function (i) { var _a; return (_a = ctData_2.partColors) === null || _a === void 0 ? void 0 : _a["0-".concat(i)]; });
                    }
                    else if (ctData_2.generationMode === 'random') {
                        cols = cols.filter(function (i) {
                            var _a;
                            var c = (_a = ctData_2.partColors) === null || _a === void 0 ? void 0 : _a["0-".concat(i)];
                            return !c || c !== 'transparent';
                        });
                    }
                    var snappedH_1 = (_j = ctData_2.snappedHorizontalPieceIds) !== null && _j !== void 0 ? _j : Array(Math.ceil(ctData_2.columns)).fill(null);
                    var existingIds_1 = new Set(manipulativeRef.current.map(function (p) { return p.data.id; }));
                    var emptyCols = cols.filter(function (colIdx) {
                        var slotId = snappedH_1[colIdx];
                        return !slotId || !existingIds_1.has(slotId);
                    });
                    var newHorizontalPieces_1 = emptyCols.map(function (colIdx) {
                        var newId = "linearPiece_".concat(uuidv4().replace(/-/g, ''));
                        return {
                            colIdx: colIdx,
                            type: 'linearPiece',
                            widgetId: "linearPiece:".concat(uuidv4()),
                            data: {
                                id: newId,
                                type: 'linearPiece',
                                orientation: 'horizontal',
                                center: {
                                    x: tileLeft_2 + colIdx * scaledCell_3 + scaledCell_3 / 2,
                                    y: tileTop_2 - 21 * tileScale_2,
                                },
                                scale: tileScale_2,
                                dimensions: { width: 102, height: 10 },
                                lock: { positioning: false, editing: false },
                            },
                        };
                    });
                    if (newHorizontalPieces_1.length > 0) {
                        var updatedHIds_1 = __spreadArray([], snappedH_1, true);
                        newHorizontalPieces_1.forEach(function (p) {
                            updatedHIds_1[p.colIdx] = p.data.id;
                        });
                        setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev.map(function (item) {
                            return item.data.id === data.id && item.data.type === 'coloredTiles'
                                ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { snappedHorizontalPieceIds: updatedHIds_1 }) }) : item;
                        }), true), newHorizontalPieces_1, true); });
                        saveState(__spreadArray([
                            {
                                type: data.id,
                                id: data.id.split('_')[1],
                                propertyName: 'snappedHorizontalPieceIds',
                                old: JSON.stringify(snappedH_1),
                                new: JSON.stringify(updatedHIds_1),
                            }
                        ], newHorizontalPieces_1.map(function (p) { return ({
                            type: p.data.id,
                            id: p.data.id.split('_')[1],
                            propertyName: 'create',
                            old: JSON.stringify(p),
                        }); }), true));
                    }
                    break;
                }
                case 'delete': {
                    if ((_k = dataRef.current.lock) === null || _k === void 0 ? void 0 : _k.editing)
                        break;
                    var currentSelectedPartKeys = (_l = dataRef.current.selectedPartKeys) !== null && _l !== void 0 ? _l : [];
                    if (currentSelectedPartKeys.length === 0)
                        break;
                    var oldData = structuredClone(dataRef.current);
                    var newData_1 = __assign({}, oldData);
                    var deletedAny_1 = false;
                    currentSelectedPartKeys.forEach(function (selectedKey) {
                        if (selectedKey.startsWith('red-top-')) {
                            var colIdx_1 = parseInt(selectedKey.split('-')[2]);
                            if (newData_1.topLinearSegments) {
                                newData_1.topLinearSegments = newData_1.topLinearSegments.filter(function (idx) { return idx !== colIdx_1; });
                                deletedAny_1 = true;
                            }
                        }
                        else if (selectedKey.startsWith('red-left-')) {
                            var rowIdx_1 = parseInt(selectedKey.split('-')[2]);
                            if (newData_1.leftLinearSegments) {
                                newData_1.leftLinearSegments = newData_1.leftLinearSegments.filter(function (idx) { return idx !== rowIdx_1; });
                                deletedAny_1 = true;
                            }
                        }
                        else if (selectedKey.startsWith('cell-')) {
                            var _a = selectedKey.split('-'), _ = _a[0], r = _a[1], c = _a[2];
                            if (newData_1.partColors) {
                                delete newData_1.partColors["".concat(r, "-").concat(c)];
                                deletedAny_1 = true;
                            }
                        }
                        else {
                            // Simple "r-c" key used for regions or manual blocks
                            if (newData_1.partColors && newData_1.partColors[selectedKey]) {
                                delete newData_1.partColors[selectedKey];
                                deletedAny_1 = true;
                            }
                        }
                    });
                    if (deletedAny_1) {
                        newData_1.selectedPartKeys = [];
                        playAudio('PatternDelete');
                        announce(t('text.coloredTilesCellsDeleted', { count: currentSelectedPartKeys.length }));
                        var finalData_1 = newData_1;
                        if (newData_1.generationMode === 'random') {
                            finalData_1 = trimRandomEmptyEdgeRowsAndCols(newData_1, cellSize);
                        }
                        else if (newData_1.generationMode === 'manual') {
                            finalData_1 = trimManualEmptyEdgeRowsAndCols(newData_1, cellSize);
                        }
                        var isEmpty = finalData_1.generationMode === 'random'
                            ? Object.values(finalData_1.partColors || {}).every(function (v) { return v === 'transparent'; })
                            : Object.keys(finalData_1.partColors || {}).length === 0;
                        if (isEmpty &&
                            finalData_1.generationMode !== 'random' &&
                            finalData_1.generationMode !== 'outline') {
                            setManipulative(function (prev) { return prev.filter(function (item) { return item.data.id !== data.id; }); });
                            saveState({
                                type: data.id,
                                id: data.id.split('_')[1],
                                propertyName: 'delete',
                                old: JSON.stringify(oldData),
                                new: undefined,
                            });
                        }
                        else {
                            setManipulative(function (prev) {
                                return prev.map(function (item) { return (item.data.id === data.id ? __assign(__assign({}, item), { data: finalData_1 }) : item); });
                            });
                            saveState({
                                type: data.id,
                                id: data.id.split('_')[1],
                                propertyName: 'completeData',
                                old: JSON.stringify(oldData),
                                new: JSON.stringify(finalData_1),
                            });
                        }
                    }
                    break;
                }
                default:
                    break;
            }
        };
        customEventHandler.addEventListener('bottomPanel', onBottomPanelClick);
        return function () {
            customEventHandler.removeEventListener('bottomPanel', onBottomPanelClick);
        };
    }, [data.id, isPreview]);
    var cutRows = useMemo(function () { var _a; return __spreadArray([], ((_a = data.cutRows) !== null && _a !== void 0 ? _a : []), true).sort(function (a, b) { return a - b; }); }, [data.cutRows]);
    var cutColumns = useMemo(function () { var _a; return __spreadArray([], ((_a = data.cutColumns) !== null && _a !== void 0 ? _a : []), true).sort(function (a, b) { return a - b; }); }, [data.cutColumns]);
    /* ================= APPLY COLOR TO SELECTED PART ================= */
    var applyColorToSelection = useCallback(function (newColorKey) {
        var _a;
        if (isPreview)
            return;
        if (selectModeEnabled && selectedPartKeys.length > 0 && isFocusedRef.current) {
            var oldData = structuredClone(dataRef.current);
            var newColor_1 = ColorEnum[newColorKey];
            var newData_2 = __assign(__assign({}, oldData), { partColors: __assign({}, ((_a = oldData.partColors) !== null && _a !== void 0 ? _a : {})) });
            // Apply color to each selected key
            selectedPartKeys.forEach(function (selectedKey) {
                if (selectedKey.startsWith('cell-')) {
                    var _a = selectedKey.split('-'), _ = _a[0], rStr = _a[1], cStr = _a[2];
                    newData_2.partColors["".concat(rStr, "-").concat(cStr)] = newColor_1;
                }
                else if ((data.generationMode === 'manual' || data.generationMode === 'outline') &&
                    !selectedKey.startsWith('red-')) {
                    var _b = selectedKey.split('-').map(Number), rIdx = _b[0], cIdx = _b[1];
                    var rCutPoints = __spreadArray(__spreadArray([0], cutRows, true), [data.rows], false);
                    var cCutPoints = __spreadArray(__spreadArray([0], cutColumns, true), [data.columns], false);
                    var rStart = rCutPoints[rIdx];
                    var rEnd = rCutPoints[rIdx + 1];
                    var cStart = cCutPoints[cIdx];
                    var cEnd = cCutPoints[cIdx + 1];
                    for (var r = rStart; r < rEnd; r++) {
                        for (var c = cStart; c < cEnd; c++) {
                            newData_2.partColors["".concat(r, "-").concat(c)] = newColor_1;
                        }
                    }
                }
                else if (!selectedKey.startsWith('red-')) {
                    newData_2.partColors[selectedKey] = newColor_1;
                }
            });
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.id === data.id && item.data.type === 'coloredTiles'
                        ? __assign(__assign({}, item), { data: newData_2 }) : item;
                });
            });
            // Deselect parts after color application
            setSelectedPartKeys([]);
            // Save color change for undo
            saveState({
                type: data.id,
                id: data.id.split('_')[1],
                propertyName: 'completeData',
                old: JSON.stringify(oldData),
                new: JSON.stringify(newData_2),
            });
            announce(t('text.coloredTilesCellsColored', {
                count: selectedPartKeys.length,
                color: t("text.".concat(newColorKey.toLowerCase())),
            }));
        }
    }, [
        isPreview,
        selectModeEnabled,
        selectedPartKeys,
        data.id,
        data.rows,
        data.columns,
        data.generationMode,
        cutRows,
        cutColumns,
        setManipulative,
    ]);
    useEffect(function () {
        var onColorChange = function (eventData) {
            if (eventData.colorKey) {
                applyColorToSelection(eventData.colorKey);
            }
        };
        customEventHandler.addEventListener('colourChange', onColorChange);
        return function () {
            customEventHandler.removeEventListener('colourChange', onColorChange);
        };
    }, [applyColorToSelection]);
    /* ================= PERSIST MODE CHANGES ================= */
    useEffect(function () {
        if (isPreview)
            return;
        if (data.partModeEnabled !== partModeEnabled || data.selectModeEnabled !== selectModeEnabled) {
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.id === data.id && item.data.type === 'coloredTiles'
                        ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { partModeEnabled: partModeEnabled, selectModeEnabled: selectModeEnabled }) }) : item;
                });
            });
        }
    }, [
        partModeEnabled,
        selectModeEnabled,
        data.id,
        data.partModeEnabled,
        data.selectModeEnabled,
        setManipulative,
        isPreview,
    ]);
    useEffect(function () {
        if (!selectModeEnabled) {
            setSelectedPartKeys([]);
        }
    }, [selectModeEnabled]);
    /* ---------------- SIZE ---------------- */
    var cellSize = isPreview
        ? Math.floor(Math.min(PREVIEW_SIZE / data.columns, PREVIEW_SIZE / data.rows))
        : WORKSPACE_CELL_SIZE;
    // --- FOCUS OUTLINE PATH CALCULATION (MANUAL MODE) ---
    var focusOutlinePath = useMemo(function () {
        if (data.generationMode !== 'manual' || !isFocused || isPreview)
            return '';
        var filledCells = Object.entries(data.partColors || {})
            .filter(function (_a) {
            var _ = _a[0], color = _a[1];
            return color && color !== 'transparent';
        })
            .map(function (_a) {
            var key = _a[0];
            var _b = key.split('-').map(Number), r = _b[0], c = _b[1];
            return { r: r, c: c };
        });
        if (filledCells.length === 0)
            return '';
        // Collect all unique boundary edges
        var edges = {};
        filledCells.forEach(function (_a) {
            var _b, _c, _d, _e;
            var r = _a.r, c = _a.c;
            var s = cellSize;
            var x = c * s;
            var y = r * s;
            var neighborKeys = {
                top: "".concat(r - 1, "-").concat(c),
                bottom: "".concat(r + 1, "-").concat(c),
                left: "".concat(r, "-").concat(c - 1),
                right: "".concat(r, "-").concat(c + 1),
            };
            var hasTop = ((_b = data.partColors) === null || _b === void 0 ? void 0 : _b[neighborKeys.top]) && data.partColors[neighborKeys.top] !== 'transparent';
            var hasBottom = ((_c = data.partColors) === null || _c === void 0 ? void 0 : _c[neighborKeys.bottom]) &&
                data.partColors[neighborKeys.bottom] !== 'transparent';
            var hasLeft = ((_d = data.partColors) === null || _d === void 0 ? void 0 : _d[neighborKeys.left]) &&
                data.partColors[neighborKeys.left] !== 'transparent';
            var hasRight = ((_e = data.partColors) === null || _e === void 0 ? void 0 : _e[neighborKeys.right]) &&
                data.partColors[neighborKeys.right] !== 'transparent';
            if (!hasTop)
                edges["".concat(x, ",").concat(y, "-").concat(x + s, ",").concat(y)] = [x, y, x + s, y];
            if (!hasBottom)
                edges["".concat(x, ",").concat(y + s, "-").concat(x + s, ",").concat(y + s)] = [x, y + s, x + s, y + s];
            if (!hasLeft)
                edges["".concat(x, ",").concat(y, "-").concat(x, ",").concat(y + s)] = [x, y, x, y + s];
            if (!hasRight)
                edges["".concat(x + s, ",").concat(y, "-").concat(x + s, ",").concat(y + s)] = [x + s, y, x + s, y + s];
        });
        return Object.values(edges)
            .map(function (e) { return "M ".concat(e[0], " ").concat(e[1], " L ").concat(e[2], " ").concat(e[3]); })
            .join(' ');
    }, [data.partColors, data.generationMode, isFocused, isPreview, cellSize]);
    var width = mode === 'open'
        ? data.columns * (isPreview ? cellSize : WORKSPACE_CELL_SIZE)
        : data.columns * cellSize;
    var height = mode === 'open'
        ? data.rows * (isPreview ? cellSize : WORKSPACE_CELL_SIZE)
        : data.rows * cellSize;
    /* ================= GRID DRAG ================= */
    var _2 = useState(null), gridDrag = _2[0], setGridDrag = _2[1];
    var onGridMouseDown = function (e) {
        var _a, _b, _c, _d, _e, _f;
        if (isPreview || panningEnable)
            return;
        if (cutDragRef.current)
            return;
        // Select the manipulative — use groupId when grouped (same pattern as NMP/PatternShapes).
        // If this tile sits inside another colored tile (e.g. a manual 1×1 dropped inside an outline),
        // select the enclosing tile so the user can interact with the larger frame from the inside.
        var enclosingTile = manipulative.find(function (item) {
            var _a, _b, _c;
            if (item.data.type !== 'coloredTiles' || item.data.id === data.id)
                return false;
            var otherData = item.data;
            if (otherData.isInvisible)
                return false;
            var otherScale = (_a = otherData.scale) !== null && _a !== void 0 ? _a : 1;
            var otherW = otherData.columns * WORKSPACE_CELL_SIZE * otherScale;
            var otherH = otherData.rows * WORKSPACE_CELL_SIZE * otherScale;
            var otherLeft = otherData.center.x - otherW / 2;
            var otherTop = otherData.center.y - otherH / 2;
            var myW = data.columns * WORKSPACE_CELL_SIZE * ((_b = data.scale) !== null && _b !== void 0 ? _b : 1);
            var myH = data.rows * WORKSPACE_CELL_SIZE * ((_c = data.scale) !== null && _c !== void 0 ? _c : 1);
            var myLeft = data.center.x - myW / 2;
            var myTop = data.center.y - myH / 2;
            // Fully enclosed: this tile's bounds are inside the other's bounds.
            return (myLeft >= otherLeft &&
                myTop >= otherTop &&
                myLeft + myW <= otherLeft + otherW &&
                myTop + myH <= otherTop + otherH);
        });
        var selectId = (_d = (_c = (_b = (_a = enclosingTile === null || enclosingTile === void 0 ? void 0 : enclosingTile.data) === null || _a === void 0 ? void 0 : _a.groupId) !== null && _b !== void 0 ? _b : enclosingTile === null || enclosingTile === void 0 ? void 0 : enclosingTile.data.id) !== null && _c !== void 0 ? _c : data.groupId) !== null && _d !== void 0 ? _d : data.id;
        setSelectedTool(selectTool(e, selectId, selectedTool, false));
        setSelectedManipulativeId((_e = enclosingTile === null || enclosingTile === void 0 ? void 0 : enclosingTile.data.id) !== null && _e !== void 0 ? _e : data.id);
        if ((_f = data.lock) === null || _f === void 0 ? void 0 : _f.positioning)
            return;
        // B7: only single-finger touches start a drag — let pinch-zoom / multi-touch
        // bubble up to the parent gesture handler.
        if ('touches' in e && e.touches.length !== 1)
            return;
        var x = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var y = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        // Track drag start for click detection
        dragInfoRef.current = {
            startX: x,
            startY: y,
            hasMoved: false,
        };
        // Reset NMP-style leader fast-path refs at the start of every drag.
        leaderLatestCenterRef.current = null;
        leaderLatestScaleRef.current = null;
        leaderLatestNumberLineIdRef.current = undefined;
        // Track initial center for undo on position drag
        initialCenterRef.current = __assign({}, data.center);
        initialScaleRef.current = data.scale || 1;
        setGridDrag({
            x: x - data.center.x * zoomLevel,
            y: y - data.center.y * zoomLevel,
        });
        // Multi-select or group drag: capture all affected tiles' start positions
        var isGroupDrag = (selectedTool.length > 1 && selectedTool.includes(data.id)) || !!data.groupId;
        isGroupDragRef.current = isGroupDrag;
        if (isGroupDrag) {
            dragStartCenter.current = { x: data.center.x, y: data.center.y };
            // For group drag, include all group members (tiles + linearPieces).
            // For multi-select drag (no groupId), include all individually selected items.
            var affectedItems = manipulative.filter(function (item) {
                if (!item.data.id)
                    return false;
                var sameGroup = !!data.groupId && item.data.groupId === data.groupId;
                // Multi-select drag: include coloredTiles and linearPiece items in the ad-hoc
                // selection. Unioned with group membership (not exclusive) so dragging a grouped
                // tile that's ALSO part of a larger ad-hoc selection carries the whole selection
                // along, not just its group siblings.
                // LinearPieces must be included so their start positions are captured and
                // the onUp commit step writes their final center back to React state.
                // Without this, LinearPieces move via DOM during drag but snap back to their
                // old data.center when React re-renders on drop.
                // When the OTHER selected item is itself a group member, selectedTool holds
                // its GROUP's id, not its own individual id (see ColoredTilesSelectionGroup.tsx) —
                // checking only item.data.id here silently dropped every OTHER group's members
                // from this leader's affectedItems capture. Since this leader's own onEnd/onEnd-
                // sibling follower logic skips self-committing whenever the leader is itself
                // coloredTiles/linearPiece (assuming THIS capture already covers it), those
                // members never got their final center committed to state at all — they'd track
                // correctly during the drag (DOM-only) then jump back once anything re-read
                // data.center.
                var itemGroupId = item.data.groupId;
                var inAdHocSelection = (item.data.type === 'coloredTiles' || item.data.type === 'linearPiece') &&
                    (selectedTool.includes(item.data.id) ||
                        (!!itemGroupId && selectedTool.includes(itemGroupId)));
                return sameGroup || inAdHocSelection;
            });
            // B-NEW: include the dragged tile so the on-up batch save can find selfStart and
            // the dragged tile's own move ends up in the undo entry. E1: skip locked items
            // entirely so they don't get phantom saveState entries when the leader moves.
            multiSelectStartPositions.current = affectedItems
                .filter(function (item) { var _a; return !((_a = item.data.lock) === null || _a === void 0 ? void 0 : _a.positioning); })
                .map(function (item) { return ({
                id: item.data.id,
                center: __assign({}, item.data.center),
            }); });
            groupDragSnapshotRef.current = Object.fromEntries(affectedItems.map(function (item) { return [item.data.id, __assign({}, item.data.center)]; }));
            customEventHandler.dispatchEvent('multiSelectMoveStart', { sourceId: data.id });
        }
    };
    useEffect(function () {
        if (!gridDrag)
            return;
        var onMove = function (e) {
            var _a, _b, _c, _d, _e;
            if (isPreview)
                return;
            if ('touches' in e)
                e.preventDefault();
            // Don't move if position is locked
            if ((_a = dataRef.current.lock) === null || _a === void 0 ? void 0 : _a.positioning)
                return;
            // B7: a second finger starting mid-drag means the user wants to pinch-zoom.
            // Tear down drag state so the parent gesture takes over cleanly.
            if ('touches' in e && e.touches.length !== 1) {
                // Restore the leader's inline DOM to its drag-start position so the wrapper
                // doesn't stay frozen at the last drag position when the pinch-zoom takes over.
                var startCenter = initialCenterRef.current;
                var startScale = initialScaleRef.current;
                if (wrapperRef.current && startCenter) {
                    wrapperRef.current.style.left = "".concat(startCenter.x, "px");
                    wrapperRef.current.style.top = "".concat(startCenter.y, "px");
                    wrapperRef.current.style.transform = "translate(-50%, -50%) scale(".concat(startScale, ")");
                }
                leaderLatestCenterRef.current = null;
                leaderLatestScaleRef.current = null;
                leaderLatestNumberLineIdRef.current = undefined;
                setGridDrag(null);
                dragInfoRef.current = null;
                initialCenterRef.current = null;
                isGroupDragRef.current = false;
                groupDragSnapshotRef.current = {};
                return;
            }
            var x = 'clientX' in e ? e.clientX : e.touches[0].clientX;
            var y = 'clientY' in e ? e.clientY : e.touches[0].clientY;
            // Check if movement threshold exceeded (similar to NumberCharts)
            if (dragInfoRef.current && !dragInfoRef.current.hasMoved) {
                var dx = x - dragInfoRef.current.startX;
                var dy = y - dragInfoRef.current.startY;
                if (Math.sqrt(dx * dx + dy * dy) > 5) {
                    dragInfoRef.current.hasMoved = true;
                    if (dataRef.current.rows === 1 && dataRef.current.columns === 1) {
                        customEventHandler.dispatchEvent('blocksComponent', { id: 'blockDragStarted' });
                    }
                }
                else {
                    return; // Don't update position until threshold exceeded
                }
            }
            var computedCenterX = (x - gridDrag.x) / zoomLevel;
            var computedCenterY = (y - gridDrag.y) / zoomLevel;
            // ---- LEADER FAST PATH (NMP-style direct DOM, no per-frame setManipulative) ----
            // Math is identical to the previous setManipulative version; only the output
            // sink changes (DOM + refs instead of React state). Final commit happens in onUp.
            var newCenterX = computedCenterX;
            var newCenterY = computedCenterY;
            var cellSize = WORKSPACE_CELL_SIZE;
            var leaderItem = manipulativeRef.current.find(function (m) { return m.data.id === data.id; });
            var leaderData = ((_b = leaderItem === null || leaderItem === void 0 ? void 0 : leaderItem.data) !== null && _b !== void 0 ? _b : dataRef.current);
            var draggedScale = (_d = (_c = leaderLatestScaleRef.current) !== null && _c !== void 0 ? _c : leaderData.scale) !== null && _d !== void 0 ? _d : 1;
            // --- SQUARE GRID SNAPPING (STRICT FOR ALL TILES) ---
            if (playerBgPattern === 'square' && !isGroupDragRef.current) {
                var isSingle = dragInfoRef.current && dragInfoRef.current.hasMoved;
                var w = leaderData.columns * cellSize * draggedScale;
                var h = leaderData.rows * cellSize * draggedScale;
                var rawLeft = newCenterX - w / 2;
                var rawTop = newCenterY - h / 2;
                var snappedTL = snapToBackgroundGrid(rawLeft, rawTop, isSingle ? 55 : 110);
                newCenterX = snappedTL.x + w / 2;
                newCenterY = snappedTL.y + h / 2;
            }
            // --- NUMBER LINE SNAPPING (skip for group drag) ---
            var tileScale = (_e = leaderData.scale) !== null && _e !== void 0 ? _e : 1;
            var tileDims = {
                width: leaderData.columns * cellSize,
                height: leaderData.rows * cellSize,
            };
            var nlSnap = !isGroupDragRef.current
                ? snapTileToNumberLine({ x: newCenterX, y: newCenterY }, tileDims, tileScale, manipulativeRef.current, data.id, undefined, true)
                : null;
            if (nlSnap) {
                newCenterX = nlSnap.x;
                newCenterY = nlSnap.y;
            }
            if (leaderData.rows === 1 && leaderData.columns === 1) {
                var draggedCellW = cellSize * draggedScale;
                var halfDraggedCell = draggedCellW / 2;
                customEventHandler.dispatchEvent('blocksComponent', {
                    id: 'blockDragging',
                    data: {
                        x: newCenterX - halfDraggedCell,
                        y: newCenterY - halfDraggedCell,
                        w: draggedCellW,
                        h: draggedCellW,
                    },
                    sourceId: data.id,
                });
            }
            // Persist for onUp's commit + merge detection.
            leaderLatestCenterRef.current = { x: newCenterX, y: newCenterY };
            if (nlSnap) {
                leaderLatestScaleRef.current = nlSnap.scale;
                leaderLatestNumberLineIdRef.current = nlSnap.numberLineId;
            }
            // Direct DOM write — matches the followers' fast path so leader and followers
            // commit to DOM in the SAME frame with no React re-render in between.
            var wrapperEl = wrapperRef.current;
            if (wrapperEl) {
                wrapperEl.style.left = "".concat(newCenterX, "px");
                wrapperEl.style.top = "".concat(newCenterY, "px");
                if (nlSnap) {
                    wrapperEl.style.transform = "translate(-50%, -50%) scale(".concat(nlSnap.scale, ")");
                }
            }
            // Broadcast the delta so non-coloredTiles followers (LinearPiece, etc.) can move.
            if (isGroupDragRef.current) {
                customEventHandler.dispatchEvent('multiSelectMove', {
                    deltaX: computedCenterX - dragStartCenter.current.x,
                    deltaY: computedCenterY - dragStartCenter.current.y,
                    sourceId: data.id,
                    groupId: dataRef.current.groupId,
                });
            }
        };
        var onUp = function () {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
            // Remove listeners immediately so no early-return path can leave them orphaned
            // (e.g. the filledTargets early-return that reverts a drop onto an occupied cell).
            // The useEffect cleanup below remains as a safety net for component-unmount-during-drag.
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
            window.removeEventListener('touchcancel', onUp);
            // Leader's during-drag position lives in leaderLatestCenterRef (NMP fast path).
            // Commit it (and any nlSnap scale change) to React state FIRST so all the merge /
            // batch-save logic below — which reads dataRef.current — sees the final values.
            var finalLeaderCenter = leaderLatestCenterRef.current;
            var finalLeaderScale = leaderLatestScaleRef.current;
            var finalLeaderNumberLineId = leaderLatestNumberLineIdRef.current;
            if (finalLeaderCenter) {
                setManipulative(function (prev) {
                    return prev.map(function (item) {
                        if (item.data.id !== data.id)
                            return item;
                        return __assign(__assign({}, item), { data: __assign(__assign(__assign({}, item.data), { center: { x: finalLeaderCenter.x, y: finalLeaderCenter.y } }), (finalLeaderScale != null
                                ? { scale: finalLeaderScale, numberLineId: finalLeaderNumberLineId }
                                : {})) });
                    });
                });
                // Keep dataRef in sync immediately so the merge / batch-save reads below
                // (still in this same call stack) see the committed values without waiting
                // for React's re-render.
                dataRef.current = __assign(__assign(__assign({}, dataRef.current), { center: { x: finalLeaderCenter.x, y: finalLeaderCenter.y } }), (finalLeaderScale != null
                    ? { scale: finalLeaderScale, numberLineId: finalLeaderNumberLineId }
                    : {}));
            }
            leaderLatestCenterRef.current = null;
            leaderLatestScaleRef.current = null;
            leaderLatestNumberLineIdRef.current = undefined;
            // G5: collect every save entry from the single-select drag (center, optional scale,
            // unlink) into one batched saveState so a single Cmd+Z reverts the whole drag.
            var dragSaveEntries = [];
            // Check if this was a click (not a drag)
            if (dragInfoRef.current && !dragInfoRef.current.hasMoved) {
                // Already selected on mouseDown, but we can verify here if needed
            }
            else if (((_a = dragInfoRef.current) === null || _a === void 0 ? void 0 : _a.hasMoved) &&
                initialCenterRef.current &&
                selectedToolRef.current.length <= 1 &&
                !isGroupDragRef.current) {
                // Dragged (single-select, NOT group drag) - save state for undo/redo
                // Group/multi-select drags fall through to the batch-save branch and skip merge.
                var oldCenter_1 = initialCenterRef.current;
                var newCenter = dataRef.current.center;
                // CHECK FOR MERGE (Manual Mode only)
                if (dataRef.current.generationMode === 'manual' ||
                    dataRef.current.generationMode === 'outline' ||
                    dataRef.current.generationMode === 'random') {
                    var cellSize_3 = WORKSPACE_CELL_SIZE;
                    // Scale-aware: dragged tile's visual size for merge detection.
                    var droppedScale = (_b = dataRef.current.scale) !== null && _b !== void 0 ? _b : 1;
                    var w = dataRef.current.columns * cellSize_3 * droppedScale;
                    var h = dataRef.current.rows * cellSize_3 * droppedScale;
                    var left = dataRef.current.center.x - w / 2;
                    var top_1 = dataRef.current.center.y - h / 2;
                    var snapTarget = findSnapTargetManualTile({ x: left, y: top_1 }, { width: w, height: h }, manipulativeRef.current, data.id, 40, // match the drag-preview threshold so drop lands where snap ghost showed
                    cellSize_3, dataRef.current.generationMode, droppedScale);
                    if (snapTarget) {
                        var _s = snapTarget, targetItem = _s.item, mergeDir = _s.dir, offset_1 = _s.offset, targetRow_1 = _s.row, targetCol_1 = _s.col;
                        var targetId_1 = targetItem.data.id;
                        if (targetId_1) {
                            var et = targetItem.data;
                            var oldData = structuredClone(et);
                            var newData_3 = structuredClone(et);
                            var movingData = structuredClone(dataRef.current);
                            // The merged tile keeps the target's scale, so its cells render at
                            // cellSize * scale. All center-position shifts below must use this
                            // scaled cell size — otherwise a scaled tile (e.g. 0.6) jumps far
                            // from the drop point (appears not to snap / goes off-screen).
                            var scaledCell = cellSize_3 * ((_c = newData_3.scale) !== null && _c !== void 0 ? _c : 1);
                            // Explicitize existing colors for random mode to prevent shuffling
                            if (newData_3.generationMode === 'random') {
                                for (var r = 0; r < newData_3.rows; r++) {
                                    for (var c = 0; c < newData_3.columns; c++) {
                                        if (!((_d = newData_3.partColors) === null || _d === void 0 ? void 0 : _d["".concat(r, "-").concat(c)])) {
                                            newData_3.partColors = newData_3.partColors || {};
                                            newData_3.partColors["".concat(r, "-").concat(c)] = TILE_COLORS[(r * 2 + c) % 4];
                                        }
                                    }
                                }
                            }
                            if (mergeDir === 'RIGHT') {
                                // movingData is to the RIGHT of et
                                var startCol_1 = newData_3.columns;
                                newData_3.columns += movingData.columns;
                                newData_3.center.x += (movingData.columns * scaledCell) / 2;
                                // Combine colors
                                if (newData_3.generationMode === 'random') {
                                    for (var i = 0; i < newData_3.rows; i++) {
                                        for (var j = 0; j < movingData.columns; j++) {
                                            newData_3.partColors = newData_3.partColors || {};
                                            newData_3.partColors["".concat(i, "-").concat(startCol_1 + j)] = 'transparent';
                                        }
                                    }
                                }
                                // Handle vertical offset in manual/outline mode
                                if (offset_1 !== 0 &&
                                    (newData_3.generationMode === 'manual' || newData_3.generationMode === 'outline')) {
                                    if (offset_1 > 0) {
                                        // Moving block is shifted DOWN relative to target
                                        newData_3.rows = Math.max(newData_3.rows, movingData.rows + offset_1);
                                    }
                                    else {
                                        // Moving block is shifted UP relative to target
                                        var shiftY_1 = Math.abs(offset_1);
                                        newData_3.rows += shiftY_1;
                                        newData_3.center.y -= (shiftY_1 * scaledCell) / 2;
                                        // Shift existing colors down
                                        var shifted_1 = {};
                                        Object.entries(newData_3.partColors || {}).forEach(function (_a) {
                                            var k = _a[0], v = _a[1];
                                            var _b = k.split('-').map(Number), r = _b[0], c = _b[1];
                                            shifted_1["".concat(r + shiftY_1, "-").concat(c)] = v;
                                        });
                                        newData_3.partColors = shifted_1;
                                        // Shift existing linear pieces
                                        if (newData_3.leftLinearSegments)
                                            newData_3.leftLinearSegments = newData_3.leftLinearSegments.map(function (r) { return r + shiftY_1; });
                                        if (newData_3.cutRows)
                                            newData_3.cutRows = newData_3.cutRows.map(function (r) { return r + shiftY_1; });
                                    }
                                }
                                Object.entries(movingData.partColors || {}).forEach(function (_a) {
                                    var key = _a[0], color = _a[1];
                                    var _b = key.split('-').map(Number), r = _b[0], c = _b[1];
                                    newData_3.partColors = newData_3.partColors || {};
                                    var targetRowIdx = r + (offset_1 || 0) + (offset_1 < 0 ? Math.abs(offset_1) : 0);
                                    newData_3.partColors["".concat(targetRowIdx, "-").concat(c + startCol_1)] = color;
                                });
                                // Transfer linear pieces
                                if (movingData.topLinearSegments) {
                                    var shifted = movingData.topLinearSegments.map(function (c) { return c + startCol_1; });
                                    newData_3.topLinearSegments = Array.from(new Set(__spreadArray(__spreadArray([], (newData_3.topLinearSegments || []), true), shifted, true)));
                                }
                                if (movingData.leftLinearSegments) {
                                    var shiftY = offset_1 < 0 ? 0 : offset_1; // If offset < 0, we already shifted the target down
                                    var movingShiftY_1 = offset_1 < 0 ? 0 : offset_1;
                                    var shifted = movingData.leftLinearSegments.map(function (r) { return r + movingShiftY_1; });
                                    newData_3.leftLinearSegments = Array.from(new Set(__spreadArray(__spreadArray([], (newData_3.leftLinearSegments || []), true), shifted, true)));
                                }
                            }
                            else if (mergeDir === 'LEFT') {
                                // movingData is to the LEFT of et
                                var shift_1 = movingData.columns;
                                newData_3.columns += movingData.columns;
                                newData_3.center.x -= (movingData.columns * scaledCell) / 2;
                                // Shift existing colors
                                var shiftedColors_1 = {};
                                if (newData_3.generationMode === 'random') {
                                    for (var i = 0; i < shift_1; i++) {
                                        for (var j = 0; j < newData_3.columns; j++) {
                                            shiftedColors_1["".concat(i, "-").concat(j)] = 'transparent';
                                        }
                                    }
                                }
                                Object.entries(newData_3.partColors || {}).forEach(function (_a) {
                                    var key = _a[0], color = _a[1];
                                    var _b = key.split('-').map(Number), r = _b[0], c = _b[1];
                                    shiftedColors_1["".concat(r + (offset_1 < 0 ? Math.abs(offset_1) : 0), "-").concat(c + shift_1)] =
                                        color;
                                });
                                // Handle vertical offset in manual/outline mode
                                if (offset_1 !== 0 &&
                                    (newData_3.generationMode === 'manual' || newData_3.generationMode === 'outline')) {
                                    if (offset_1 > 0) {
                                        // Moving block is shifted DOWN relative to target
                                        newData_3.rows = Math.max(newData_3.rows, movingData.rows + offset_1);
                                    }
                                    else {
                                        // Moving block is shifted UP relative to target
                                        var shiftY = Math.abs(offset_1);
                                        newData_3.rows += shiftY;
                                        newData_3.center.y -= (shiftY * scaledCell) / 2;
                                        // Note: shiftedColors already shifted target items down if needed by the logic above
                                    }
                                }
                                // Add moving colors
                                Object.entries(movingData.partColors || {}).forEach(function (_a) {
                                    var key = _a[0], color = _a[1];
                                    var _b = key.split('-').map(Number), r = _b[0], c = _b[1];
                                    var targetRowIdx = r + (offset_1 > 0 ? offset_1 : 0);
                                    shiftedColors_1["".concat(targetRowIdx, "-").concat(c)] = color;
                                });
                                newData_3.partColors = shiftedColors_1;
                                // Shift auxiliary data
                                if (newData_3.cutColumns) {
                                    newData_3.cutColumns = newData_3.cutColumns.map(function (v) { return v + shift_1; });
                                }
                                if (newData_3.topLinearSegments) {
                                    newData_3.topLinearSegments = newData_3.topLinearSegments.map(function (v) { return v + shift_1; });
                                }
                                if (movingData.topLinearSegments) {
                                    newData_3.topLinearSegments = Array.from(new Set(__spreadArray(__spreadArray([], (newData_3.topLinearSegments || []), true), movingData.topLinearSegments, true)));
                                }
                                if (movingData.leftLinearSegments) {
                                    var movingShiftY_2 = offset_1 > 0 ? offset_1 : 0;
                                    var shifted = movingData.leftLinearSegments.map(function (r) { return r + movingShiftY_2; });
                                    newData_3.leftLinearSegments = Array.from(new Set(__spreadArray(__spreadArray([], (newData_3.leftLinearSegments || []), true), shifted, true)));
                                }
                                if (newData_3.leftLinearSegments && offset_1 < 0) {
                                    newData_3.leftLinearSegments = newData_3.leftLinearSegments.map(function (r) { return r + Math.abs(offset_1); });
                                }
                                if (newData_3.selectedPartKeys) {
                                    newData_3.selectedPartKeys = newData_3.selectedPartKeys.map(function (k) {
                                        if (k.startsWith('cell-')) {
                                            var _a = k.replace('cell-', '').split('-').map(Number), r = _a[0], c = _a[1];
                                            return "cell-".concat(r, "-").concat(c + shift_1);
                                        }
                                        if (k.startsWith('red-top-')) {
                                            var c = Number(k.replace('red-top-', ''));
                                            return "red-top-".concat(c + shift_1);
                                        }
                                        return k;
                                    });
                                }
                            }
                            else if (mergeDir === 'BOTTOM') {
                                // movingData is at the BOTTOM of et
                                var startRow_1 = newData_3.rows;
                                newData_3.rows += movingData.rows;
                                newData_3.center.y += (movingData.rows * scaledCell) / 2;
                                if (newData_3.generationMode === 'random') {
                                    for (var i = 0; i < movingData.rows; i++) {
                                        for (var j = 0; j < newData_3.columns; j++) {
                                            newData_3.partColors = newData_3.partColors || {};
                                            newData_3.partColors["".concat(startRow_1 + i, "-").concat(j)] = 'transparent';
                                        }
                                    }
                                }
                                // Handle horizontal offset in manual/outline mode
                                if (offset_1 !== 0 &&
                                    (newData_3.generationMode === 'manual' || newData_3.generationMode === 'outline')) {
                                    if (offset_1 > 0) {
                                        // Moving block is shifted RIGHT relative to target
                                        newData_3.columns = Math.max(newData_3.columns, movingData.columns + offset_1);
                                    }
                                    else {
                                        // Moving block is shifted LEFT relative to target
                                        var shiftX_1 = Math.abs(offset_1);
                                        newData_3.columns += shiftX_1;
                                        newData_3.center.x -= (shiftX_1 * scaledCell) / 2;
                                        // Shift existing colors right
                                        var shifted_2 = {};
                                        Object.entries(newData_3.partColors || {}).forEach(function (_a) {
                                            var k = _a[0], v = _a[1];
                                            var _b = k.split('-').map(Number), r = _b[0], c = _b[1];
                                            shifted_2["".concat(r, "-").concat(c + shiftX_1)] = v;
                                        });
                                        newData_3.partColors = shifted_2;
                                        // Shift existing linear pieces
                                        if (newData_3.topLinearSegments)
                                            newData_3.topLinearSegments = newData_3.topLinearSegments.map(function (c) { return c + shiftX_1; });
                                        if (newData_3.cutColumns)
                                            newData_3.cutColumns = newData_3.cutColumns.map(function (c) { return c + shiftX_1; });
                                    }
                                }
                                Object.entries(movingData.partColors || {}).forEach(function (_a) {
                                    var key = _a[0], color = _a[1];
                                    var _b = key.split('-').map(Number), r = _b[0], c = _b[1];
                                    newData_3.partColors = newData_3.partColors || {};
                                    var targetColIdx = c + (offset_1 || 0) + (offset_1 < 0 ? Math.abs(offset_1) : 0);
                                    newData_3.partColors["".concat(r + startRow_1, "-").concat(targetColIdx)] = color;
                                });
                                // Transfer linear pieces
                                if (movingData.topLinearSegments) {
                                    var movingShiftX_1 = offset_1 < 0 ? 0 : offset_1;
                                    var shifted = movingData.topLinearSegments.map(function (c) { return c + movingShiftX_1; });
                                    newData_3.topLinearSegments = Array.from(new Set(__spreadArray(__spreadArray([], (newData_3.topLinearSegments || []), true), shifted, true)));
                                }
                                if (movingData.leftLinearSegments) {
                                    var shifted = movingData.leftLinearSegments.map(function (r) { return r + startRow_1; });
                                    newData_3.leftLinearSegments = Array.from(new Set(__spreadArray(__spreadArray([], (newData_3.leftLinearSegments || []), true), shifted, true)));
                                }
                            }
                            else if (mergeDir === 'TOP') {
                                // movingData is at the TOP of et
                                var shift_2 = movingData.rows;
                                newData_3.rows += movingData.rows;
                                newData_3.center.y -= (movingData.rows * scaledCell) / 2;
                                var shiftedColors_2 = {};
                                if (newData_3.generationMode === 'random') {
                                    for (var i = 0; i < shift_2; i++) {
                                        for (var j = 0; j < newData_3.columns; j++) {
                                            shiftedColors_2["".concat(i, "-").concat(j)] = 'transparent';
                                        }
                                    }
                                }
                                Object.entries(newData_3.partColors || {}).forEach(function (_a) {
                                    var key = _a[0], color = _a[1];
                                    var _b = key.split('-').map(Number), r = _b[0], c = _b[1];
                                    var targetRowIdx = r + shift_2;
                                    var targetColIdx = c + (offset_1 < 0 ? Math.abs(offset_1) : 0);
                                    shiftedColors_2["".concat(targetRowIdx, "-").concat(targetColIdx)] = color;
                                });
                                // Handle horizontal offset in manual/outline mode
                                if (offset_1 !== 0 &&
                                    (newData_3.generationMode === 'manual' || newData_3.generationMode === 'outline')) {
                                    if (offset_1 > 0) {
                                        // Moving block is shifted RIGHT relative to target
                                        newData_3.columns = Math.max(newData_3.columns, movingData.columns + offset_1);
                                    }
                                    else {
                                        // Moving block is shifted LEFT relative to target
                                        var shiftX = Math.abs(offset_1);
                                        newData_3.columns += shiftX;
                                        newData_3.center.x -= (shiftX * scaledCell) / 2;
                                    }
                                }
                                Object.entries(movingData.partColors || {}).forEach(function (_a) {
                                    var key = _a[0], color = _a[1];
                                    var _b = key.split('-').map(Number), r = _b[0], c = _b[1];
                                    var targetColIdx = c + (offset_1 > 0 ? offset_1 : 0);
                                    shiftedColors_2["".concat(r, "-").concat(targetColIdx)] = color;
                                });
                                newData_3.partColors = shiftedColors_2;
                                // Shift auxiliary data
                                if (newData_3.cutRows) {
                                    newData_3.cutRows = newData_3.cutRows.map(function (v) { return v + shift_2; });
                                }
                                if (newData_3.leftLinearSegments) {
                                    newData_3.leftLinearSegments = newData_3.leftLinearSegments.map(function (v) { return v + shift_2; });
                                }
                                if (movingData.leftLinearSegments) {
                                    newData_3.leftLinearSegments = Array.from(new Set(__spreadArray(__spreadArray([], (newData_3.leftLinearSegments || []), true), movingData.leftLinearSegments, true)));
                                }
                                if (movingData.topLinearSegments) {
                                    var movingShiftX_2 = offset_1 > 0 ? offset_1 : 0;
                                    var shifted = movingData.topLinearSegments.map(function (c) { return c + movingShiftX_2; });
                                    newData_3.topLinearSegments = Array.from(new Set(__spreadArray(__spreadArray([], (newData_3.topLinearSegments || []), true), shifted, true)));
                                }
                                if (newData_3.topLinearSegments && offset_1 < 0) {
                                    newData_3.topLinearSegments = newData_3.topLinearSegments.map(function (c) { return c + Math.abs(offset_1); });
                                }
                                if (newData_3.cutColumns && offset_1 < 0) {
                                    newData_3.cutColumns = newData_3.cutColumns.map(function (c) { return c + Math.abs(offset_1); });
                                }
                                if (newData_3.selectedPartKeys) {
                                    newData_3.selectedPartKeys = newData_3.selectedPartKeys.map(function (k) {
                                        if (k.startsWith('cell-')) {
                                            var _a = k.replace('cell-', '').split('-').map(Number), r = _a[0], c = _a[1];
                                            return "cell-".concat(r + shift_2, "-").concat(c);
                                        }
                                        if (k.startsWith('red-left-')) {
                                            var r = Number(k.replace('red-left-', ''));
                                            return "red-left-".concat(r + shift_2);
                                        }
                                        return k;
                                    });
                                }
                            }
                            else if (mergeDir === 'INSIDE') {
                                // Prevent overwriting existing cells (e.g., 1x1 dropped onto another 1x1)
                                var movingEntries = Object.entries(movingData.partColors || {});
                                var filledTargets = movingEntries.some(function (_a) {
                                    var _b;
                                    var key = _a[0];
                                    var _c = key.split('-').map(Number), r = _c[0], c = _c[1];
                                    var rIdx = r + (targetRow_1 || 0);
                                    var cIdx = c + (targetCol_1 || 0);
                                    if (rIdx < 0 || rIdx >= newData_3.rows || cIdx < 0 || cIdx >= newData_3.columns)
                                        return false;
                                    var targetKey = "".concat(rIdx, "-").concat(cIdx);
                                    var existingColor = (_b = newData_3.partColors) === null || _b === void 0 ? void 0 : _b[targetKey];
                                    var isFilledInRandom = newData_3.generationMode === 'random' && existingColor !== 'transparent';
                                    var isFilledInManualOrOutline = (newData_3.generationMode === 'manual' || newData_3.generationMode === 'outline') &&
                                        !!existingColor;
                                    return isFilledInRandom || isFilledInManualOrOutline;
                                });
                                if (filledTargets) {
                                    console.log('[CT-ghost] filledTargets revert', {
                                        id: data.id.slice(-6),
                                        revertTo: oldCenter_1,
                                        'dataRef.center': { x: dataRef.current.center.x, y: dataRef.current.center.y },
                                        'dom.left': (_e = wrapperRef.current) === null || _e === void 0 ? void 0 : _e.style.left,
                                        'dom.top': (_f = wrapperRef.current) === null || _f === void 0 ? void 0 : _f.style.top,
                                    });
                                    // Cancel the drop: keep the dragged item at its original position
                                    setManipulative(function (prev) {
                                        return prev.map(function (item) {
                                            return item.data.id === data.id && item.data.type === 'coloredTiles'
                                                ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: oldCenter_1 }) }) : item;
                                        });
                                    });
                                    // React bails out of the DOM write when the reverted center equals the
                                    // pre-drag committed center (round-trip: A→B→A, vDOM unchanged). Reset
                                    // the DOM directly so the next drag starts from the correct position.
                                    if (wrapperRef.current && oldCenter_1) {
                                        wrapperRef.current.style.left = "".concat(oldCenter_1.x, "px");
                                        wrapperRef.current.style.top = "".concat(oldCenter_1.y, "px");
                                    }
                                    setGridDrag(null);
                                    dragInfoRef.current = null;
                                    initialCenterRef.current = null;
                                    return;
                                }
                                movingEntries.forEach(function (_a) {
                                    var key = _a[0], color = _a[1];
                                    var _b = key.split('-').map(Number), r = _b[0], c = _b[1];
                                    newData_3.partColors = newData_3.partColors || {};
                                    // Map moving cells into the target grid at the dropped destination
                                    var rIdx = r + (targetRow_1 || 0);
                                    var cIdx = c + (targetCol_1 || 0);
                                    if (rIdx >= 0 && rIdx < newData_3.rows && cIdx >= 0 && cIdx < newData_3.columns) {
                                        newData_3.partColors["".concat(rIdx, "-").concat(cIdx)] = color;
                                    }
                                });
                            }
                            // Post-merge trimming for manual mode to ensure no empty rows/columns
                            if (newData_3.generationMode === 'manual') {
                                var trimmed = trimManualEmptyEdgeRowsAndCols(newData_3, cellSize_3);
                                Object.assign(newData_3, trimmed);
                            }
                            newData_3.dimensions = {
                                width: newData_3.columns * cellSize_3,
                                height: newData_3.rows * cellSize_3,
                            };
                            // Save both changes as one history entry so merge undoes in a single step
                            saveState([
                                {
                                    type: targetId_1,
                                    id: targetId_1.split('_')[1],
                                    propertyName: 'completeData',
                                    old: JSON.stringify(oldData),
                                    new: JSON.stringify(newData_3),
                                },
                                {
                                    type: data.id,
                                    id: data.id.split('_')[1],
                                    propertyName: 'delete',
                                    old: JSON.stringify(movingData),
                                    new: undefined,
                                },
                            ]);
                            // Update the list: remove 'data.id', update 'targetId'
                            setManipulative(function (prev) {
                                var next = prev
                                    .filter(function (item) { return item.data.id !== data.id; })
                                    .map(function (item) { return (item.data.id === targetId_1 ? __assign(__assign({}, item), { data: newData_3 }) : item); });
                                return next;
                            });
                            setSelectedTool([targetId_1]);
                            setSelectedManipulativeId(targetId_1);
                            announce(t('text.coloredTilesMerged', { rows: newData_3.rows, columns: newData_3.columns }));
                        }
                    }
                    else {
                        // No merge, just normal move — collect into G5 batch below.
                        dragSaveEntries.push({
                            type: data.id,
                            id: data.id.split('_')[1],
                            propertyName: 'center',
                            old: JSON.stringify(oldCenter_1),
                            new: JSON.stringify(newCenter),
                        });
                    }
                }
                else {
                    // Non-manual mode - normal move save state
                    dragSaveEntries.push({
                        type: data.id,
                        id: data.id.split('_')[1],
                        propertyName: 'center',
                        old: JSON.stringify(oldCenter_1),
                        new: JSON.stringify(newCenter),
                    });
                }
                // Save scale change from NL snapping
                var currentScale = dataRef.current.scale || 1;
                if (currentScale !== initialScaleRef.current) {
                    dragSaveEntries.push({
                        type: data.id,
                        id: data.id.split('_')[1],
                        propertyName: 'scale',
                        old: JSON.stringify(initialScaleRef.current),
                        new: JSON.stringify(currentScale),
                    });
                    announce(t('text.coloredTilesNLSnap', { scale: currentScale.toFixed(1) }));
                }
                // Unlink snapped linear pieces if tile moved more than 2px (all 4 sides).
                var UNLINK_THRESHOLD = 2;
                var moveDist = Math.hypot(newCenter.x - oldCenter_1.x, newCenter.y - oldCenter_1.y);
                if (moveDist > UNLINK_THRESHOLD) {
                    var curData = dataRef.current;
                    var hadSnapped = ((_g = curData.snappedVerticalPieceIds) !== null && _g !== void 0 ? _g : []).some(function (id) { return id !== null; }) ||
                        ((_h = curData.snappedRightVerticalPieceIds) !== null && _h !== void 0 ? _h : []).some(function (id) { return id !== null; }) ||
                        ((_j = curData.snappedHorizontalPieceIds) !== null && _j !== void 0 ? _j : []).some(function (id) { return id !== null; }) ||
                        ((_k = curData.snappedBottomHorizontalPieceIds) !== null && _k !== void 0 ? _k : []).some(function (id) { return id !== null; });
                    if (hadSnapped) {
                        var oldLeftVIds = (_l = curData.snappedVerticalPieceIds) !== null && _l !== void 0 ? _l : [];
                        var oldRightVIds = (_m = curData.snappedRightVerticalPieceIds) !== null && _m !== void 0 ? _m : [];
                        var oldTopHIds = (_o = curData.snappedHorizontalPieceIds) !== null && _o !== void 0 ? _o : [];
                        var oldBottomHIds = (_p = curData.snappedBottomHorizontalPieceIds) !== null && _p !== void 0 ? _p : [];
                        var clearedV_1 = Array(curData.rows).fill(null);
                        var clearedH_1 = Array(curData.columns).fill(null);
                        setManipulative(function (prev) {
                            return prev.map(function (item) {
                                return item.data.id === data.id && item.data.type === 'coloredTiles'
                                    ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { snappedVerticalPieceIds: clearedV_1, snappedRightVerticalPieceIds: clearedV_1, snappedHorizontalPieceIds: clearedH_1, snappedBottomHorizontalPieceIds: clearedH_1 }) }) : item;
                            });
                        });
                        var baseEntry = {
                            type: data.id,
                            id: data.id.split('_')[1],
                        };
                        dragSaveEntries.push(__assign(__assign({}, baseEntry), { propertyName: 'snappedVerticalPieceIds', old: JSON.stringify(oldLeftVIds), new: JSON.stringify(clearedV_1) }), __assign(__assign({}, baseEntry), { propertyName: 'snappedRightVerticalPieceIds', old: JSON.stringify(oldRightVIds), new: JSON.stringify(clearedV_1) }), __assign(__assign({}, baseEntry), { propertyName: 'snappedHorizontalPieceIds', old: JSON.stringify(oldTopHIds), new: JSON.stringify(clearedH_1) }), __assign(__assign({}, baseEntry), { propertyName: 'snappedBottomHorizontalPieceIds', old: JSON.stringify(oldBottomHIds), new: JSON.stringify(clearedH_1) }));
                    }
                }
            }
            // G5: emit the batched single-drag entries (if any). Multi-select / group drag
            // saves its own batch below in the next branch.
            if (dragSaveEntries.length > 0) {
                saveState(dragSaveEntries);
            }
            // Multi-select / group drag: commit all follower final positions to React state in one
            // setManipulative pass (followers were moved via direct DOM during drag — see listener
            // onMove). This replaces the inline left/top styles cleanly without a flash.
            if (((_q = dragInfoRef.current) === null || _q === void 0 ? void 0 : _q.hasMoved) && isGroupDragRef.current) {
                var selfStart = multiSelectStartPositions.current.find(function (p) { return p.id === data.id; });
                if (selfStart) {
                    // Use finalLeaderCenter (this leader's just-computed final position), NOT
                    // dataRef.current.center — the setManipulative call above (finalLeaderCenter)
                    // is only just queued here, not yet applied, so dataRef.current (synced via a
                    // `dataRef.current = data` effect that runs after the NEXT render) still reads
                    // the pre-drag center. That made selfDelta ~0, snapping every group member back
                    // toward its start position at drag end despite already being visually correct.
                    var leaderCenterNow = finalLeaderCenter !== null && finalLeaderCenter !== void 0 ? finalLeaderCenter : dataRef.current.center;
                    var selfDeltaX_1 = leaderCenterNow.x - selfStart.center.x;
                    var selfDeltaY_1 = leaderCenterNow.y - selfStart.center.y;
                    var startById_1 = new Map(multiSelectStartPositions.current.map(function (p) { return [p.id, p.center]; }));
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            var _a;
                            if (item.data.id === data.id)
                                return item;
                            var start = startById_1.get(item.data.id);
                            if (!start)
                                return item;
                            if ((_a = item.data.lock) === null || _a === void 0 ? void 0 : _a.positioning)
                                return item;
                            // Commit any group/multi-select follower whose start was captured —
                            // includes linearPieces in mixed groups so their data.center catches
                            // up to the inline-style position set during drag.
                            return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: start.x + selfDeltaX_1, y: start.y + selfDeltaY_1 } }) });
                        });
                    });
                    var batchSave = multiSelectStartPositions.current.map(function (startItem) { return ({
                        type: startItem.id,
                        id: startItem.id.split('_')[1],
                        propertyName: 'center',
                        old: JSON.stringify(startItem.center),
                        new: JSON.stringify({
                            x: startItem.center.x + selfDeltaX_1,
                            y: startItem.center.y + selfDeltaY_1,
                        }),
                    }); });
                    if (!registerMultiSelectSaveEntries(data.id, batchSave))
                        saveState(batchSave);
                }
                customEventHandler.dispatchEvent('multiSelectMoveEnd', { sourceId: data.id });
            }
            isGroupDragRef.current = false;
            groupDragSnapshotRef.current = {};
            if (((_r = dragInfoRef.current) === null || _r === void 0 ? void 0 : _r.hasMoved) &&
                dataRef.current.rows === 1 &&
                dataRef.current.columns === 1) {
                customEventHandler.dispatchEvent('blocksComponent', { id: 'blockDragEnded' });
            }
            // Reset drag state
            setGridDrag(null);
            dragInfoRef.current = null;
            initialCenterRef.current = null;
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove);
        window.addEventListener('touchend', onUp);
        // E2: iOS dispatches touchcancel when a system gesture preempts the touch
        // (e.g., notification swipe). Treat it the same as touchend so drag state
        // tears down cleanly.
        window.addEventListener('touchcancel', onUp);
        return function () {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
            window.removeEventListener('touchcancel', onUp);
        };
    }, [
        gridDrag,
        zoomLevel,
        playerBgPattern,
        data.id,
        setManipulative,
        setSelectedTool,
        setSelectedManipulativeId,
        isPreview,
    ]);
    /* ================= CONTEXT-MENU SNAP-TO-TILE LISTENER ================= */
    // Keyboard-accessible "Snap to…": this tile (the source) is merged INTO the
    // chosen target by FILLING the target's empty cells row-major (left→right, then
    // the next row below) with the source's colours; overflow grows the target
    // downward. Then the source is removed and the target selected (one undo).
    useEffect(function () {
        var onSnapToTile = function (_a) {
            var _b, _c, _d, _e;
            var movingId = _a.movingId, targetId = _a.targetId;
            if (movingId !== data.id || ((_b = dataRef.current.lock) === null || _b === void 0 ? void 0 : _b.positioning))
                return;
            var targetItem = manipulativeRef.current.find(function (m) { return m.data.id === targetId && m.data.type === 'coloredTiles'; });
            if (!targetItem)
                return;
            var et = targetItem.data;
            var movingData = structuredClone(dataRef.current);
            var oldData = structuredClone(et);
            // Fill the target's EMPTY cells with the source's colours in row-major order
            // (left→right, then the next row BELOW). If the source has more colours than
            // the target has empty cells, grow the target DOWNWARD (append rows at the
            // bottom) to hold the remainder. Works for random / manual / outline.
            var cellSize = WORKSPACE_CELL_SIZE;
            var newData = structuredClone(et);
            newData.partColors = __assign({}, ((_c = newData.partColors) !== null && _c !== void 0 ? _c : {}));
            // 1. Source colours, row-major. For random, a missing cell = its default
            //    colour; an explicit 'transparent' cell contributes nothing (it's empty).
            var sourceColors = [];
            for (var r = 0; r < movingData.rows; r++) {
                for (var c = 0; c < movingData.columns; c++) {
                    var v = (_d = movingData.partColors) === null || _d === void 0 ? void 0 : _d["".concat(r, "-").concat(c)];
                    if (movingData.generationMode === 'random') {
                        if (v === 'transparent')
                            continue;
                        sourceColors.push(v || TILE_COLORS[(r * 2 + c) % 4]);
                    }
                    else if (v) {
                        sourceColors.push(v);
                    }
                }
            }
            // 2. Explicitize target random cells so existing colours don't shuffle; the
            //    remaining 'transparent' cells are the empties to fill.
            if (newData.generationMode === 'random') {
                for (var r = 0; r < newData.rows; r++) {
                    for (var c = 0; c < newData.columns; c++) {
                        if (!newData.partColors["".concat(r, "-").concat(c)]) {
                            newData.partColors["".concat(r, "-").concat(c)] = TILE_COLORS[(r * 2 + c) % 4];
                        }
                    }
                }
            }
            // 3. Target empties, row-major (top→bottom, left→right).
            var empties = [];
            for (var r = 0; r < newData.rows; r++) {
                for (var c = 0; c < newData.columns; c++) {
                    var v = newData.partColors["".concat(r, "-").concat(c)];
                    var empty = newData.generationMode === 'random' ? v === 'transparent' : !v;
                    if (empty)
                        empties.push({ r: r, c: c });
                }
            }
            // 4. Fill empties in order.
            var idx = 0;
            for (; idx < sourceColors.length && idx < empties.length; idx++) {
                var cell = empties[idx];
                newData.partColors["".concat(cell.r, "-").concat(cell.c)] = sourceColors[idx];
            }
            // 5. Overflow → grow downward (append rows at the bottom, fill left→right).
            var remaining = sourceColors.slice(idx);
            if (remaining.length > 0) {
                var cols = Math.max(1, newData.columns);
                var addedRows = Math.ceil(remaining.length / cols);
                var startRow = newData.rows;
                var scaledCell = cellSize * ((_e = newData.scale) !== null && _e !== void 0 ? _e : 1);
                newData.rows += addedRows;
                newData.center.y += (addedRows * scaledCell) / 2; // top fixed, extend down
                for (var i = 0; i < addedRows * cols; i++) {
                    var rr = startRow + Math.floor(i / cols);
                    var cc = i % cols;
                    if (i < remaining.length) {
                        newData.partColors["".concat(rr, "-").concat(cc)] = remaining[i];
                    }
                    else if (newData.generationMode === 'random') {
                        // leftover new cells in the last row stay empty (fillable later)
                        newData.partColors["".concat(rr, "-").concat(cc)] = 'transparent';
                    }
                }
            }
            // Completion — mirrors the drag onUp merge tail.
            if (newData.generationMode === 'manual') {
                Object.assign(newData, trimManualEmptyEdgeRowsAndCols(newData, WORKSPACE_CELL_SIZE));
            }
            newData.dimensions = {
                width: newData.columns * WORKSPACE_CELL_SIZE,
                height: newData.rows * WORKSPACE_CELL_SIZE,
            };
            saveState([
                {
                    type: targetId,
                    id: targetId.split('_')[1],
                    propertyName: 'completeData',
                    old: JSON.stringify(oldData),
                    new: JSON.stringify(newData),
                },
                {
                    type: data.id,
                    id: data.id.split('_')[1],
                    propertyName: 'delete',
                    old: JSON.stringify(movingData),
                    new: undefined,
                },
            ]);
            setManipulative(function (prev) {
                return prev
                    .filter(function (item) { return item.data.id !== data.id; })
                    .map(function (item) { return (item.data.id === targetId ? __assign(__assign({}, item), { data: newData }) : item); });
            });
            setSelectedTool([targetId]);
            setSelectedManipulativeId(targetId);
            announce(t('text.coloredTilesMerged', { rows: newData.rows, columns: newData.columns }));
        };
        customEventHandler.addEventListener('snapColoredTileToTile', onSnapToTile);
        return function () { return customEventHandler.removeEventListener('snapColoredTileToTile', onSnapToTile); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.id]);
    /* ================= MULTI-SELECT / GROUP LISTENER ================= */
    useEffect(function () {
        var isGroupMember = !!data.groupId;
        var isMultiSelected = selectedTool.includes(data.id) && selectedTool.length > 1;
        if (isPreview || (!isGroupMember && !isMultiSelected))
            return;
        var myStart = { x: dataRef.current.center.x, y: dataRef.current.center.y };
        var lastAppliedPos = null;
        var onStart = function (_a) {
            var sourceId = _a.sourceId;
            if (sourceId === data.id)
                return;
            myStart = { x: dataRef.current.center.x, y: dataRef.current.center.y };
            lastAppliedPos = null;
        };
        var onMove = function (_a) {
            var _b;
            var deltaX = _a.deltaX, deltaY = _a.deltaY, sourceId = _a.sourceId, srcGroupId = _a.groupId;
            if (sourceId === data.id || ((_b = dataRef.current.lock) === null || _b === void 0 ? void 0 : _b.positioning))
                return;
            var sameGroup = srcGroupId && dataRef.current.groupId === srcGroupId;
            // When this tile is part of a group, the group's own id — not each member's
            // individual id — is what's actually in selectedTool (see
            // ColoredTilesSelectionGroup.tsx's isGroupActive/onMove, which checks
            // selectedTool.includes(groupId)). A leader outside this group (e.g. a Dice or
            // deck) broadcasts multiSelectMove with no groupId payload at all, so `sameGroup`
            // is false too — checking only `data.id` here left every member motionless while
            // the group's outline (correctly checking its own groupId) still followed.
            var inMultiSel = (selectedToolRef.current.includes(data.id) ||
                (!!dataRef.current.groupId && selectedToolRef.current.includes(dataRef.current.groupId))) &&
                selectedToolRef.current.length > 1;
            if (!sameGroup && !inMultiSel)
                return;
            // NMP-style direct DOM update — avoids per-frame setManipulative for every follower.
            // Final position is committed to state in onEnd (see lastAppliedPos below).
            var el = wrapperRef.current;
            if (!el)
                return;
            var newLeft = myStart.x + deltaX;
            var newTop = myStart.y + deltaY;
            el.style.left = "".concat(newLeft, "px");
            el.style.top = "".concat(newTop, "px");
            lastAppliedPos = { x: newLeft, y: newTop };
        };
        var onEnd = function (_a) {
            var sourceId = _a.sourceId;
            if (sourceId === data.id)
                return;
            // Do NOT clear inline styles here — React will overwrite them once setManipulative
            // below commits the new data.center. Clearing first causes a 1-frame flash back to
            // the pre-drag position.
            if (!lastAppliedPos)
                return;
            // A leader that's itself a coloredTiles/linearPiece already batch-commits every
            // follower's final `center` in its own onUp handler further up in this file (see the
            // `isGroupDragRef` block) — self-committing here too would double-write and double
            // `saveState`, duplicating the undo entry. Only self-commit when the leader is some
            // OTHER type (Dice, a card deck, Shades, ...) that has no idea this tile exists, so
            // nobody else ever writes the DOM-only position above back into `data.center`. Without
            // this, state stays stale and the next drag's onStart reads the old `data.center` as
            // `myStart`, producing a visible jump back before applying the new delta.
            var sourceItem = manipulativeRef.current.find(function (m) { return m.data.id === sourceId; });
            var sourceIsSelfManaged = (sourceItem === null || sourceItem === void 0 ? void 0 : sourceItem.data.type) === 'coloredTiles' || (sourceItem === null || sourceItem === void 0 ? void 0 : sourceItem.data.type) === 'linearPiece';
            if (sourceIsSelfManaged) {
                lastAppliedPos = null;
                return;
            }
            var oldCenter = __assign({}, dataRef.current.center);
            var newCenter = __assign({}, lastAppliedPos);
            lastAppliedPos = null;
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.id === data.id
                        ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: newCenter }) }) : item;
                });
            });
            var entry = {
                type: data.id,
                id: data.id.split('_')[1],
                propertyName: 'center',
                old: JSON.stringify(oldCenter),
                new: JSON.stringify(newCenter),
            };
            if (!registerMultiSelectSaveEntries(sourceId, [entry])) {
                saveState(entry);
            }
        };
        customEventHandler.addEventListener('multiSelectMoveStart', onStart);
        customEventHandler.addEventListener('multiSelectMove', onMove);
        customEventHandler.addEventListener('multiSelectMoveEnd', onEnd);
        return function () {
            customEventHandler.removeEventListener('multiSelectMoveStart', onStart);
            customEventHandler.removeEventListener('multiSelectMove', onMove);
            customEventHandler.removeEventListener('multiSelectMoveEnd', onEnd);
        };
    }, [selectedTool, data.id, data.groupId, isPreview, setManipulative]);
    /* ================= CUT DRAG ================= */
    var _3 = useState(null), cutPreview = _3[0], setCutPreview = _3[1];
    // Ref mirror so onUp always reads the latest cutPreview without re-registering listeners
    var cutPreviewRef = useRef(cutPreview);
    cutPreviewRef.current = cutPreview;
    // Keyboard cut-creation mode (ArrayModel-style). When active, a preview cut line
    // is positioned via arrow keys before being committed with Enter. `position` is a
    // 1-based cell index in [1, limit-1] (grid mode only). null = not placing a cut.
    var _4 = useState(null), kbCutMode = _4[0], setKbCutMode = _4[1];
    var kbCutModeRef = useRef(kbCutMode);
    kbCutModeRef.current = kbCutMode;
    var cutMovePanelRef = useRef(null);
    var cutArrowRightRef = useRef(null);
    var cutArrowBottomRef = useRef(null);
    var cutDragRef = useRef(null);
    // Track which existing cut is being re-dragged (to hide it during drag)
    var _5 = useState(null), draggingExistingCut = _5[0], setDraggingExistingCut = _5[1];
    // Resize drag tracking for open mode
    var resizeDragRef = useRef(null);
    var lastUpdateDataRef = useRef(null);
    var onCutStart = function (e, type) {
        var _a;
        if (isPreview)
            return;
        e.stopPropagation();
        e.preventDefault();
        setSelectedTool([data.id]);
        setSelectedManipulativeId(data.id);
        var x = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var y = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        if (!partModeEnabled || ((_a = data.lock) === null || _a === void 0 ? void 0 : _a.editing))
            return; // Only allow cut when part mode is enabled and not locked
        cutDragRef.current = { type: type, startX: x, startY: y, divisor: scale * zoomLevel, hasMoved: false, isTouch: 'touches' in e };
        setCutPreview(null);
    };
    var onExistingCutStart = function (e, type, cutValue) {
        var _a;
        if (isPreview)
            return;
        e.stopPropagation();
        e.preventDefault();
        setSelectedTool([data.id]);
        setSelectedManipulativeId(data.id);
        var x = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var y = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        // Offset startX/startY so localX/localY equals current pixel position at drag start
        // For grid mode: cutValue is an index, convert to pixels
        // For open mode: cutValue is a percentage, convert to pixels
        var currentPixelPos = mode === 'grid' ? cutValue * cellSize : type === 'row' ? cutValue * height : cutValue * width;
        if (!partModeEnabled || ((_a = data.lock) === null || _a === void 0 ? void 0 : _a.editing))
            return;
        var dragDivisor = scale * zoomLevel;
        cutDragRef.current = {
            type: type,
            startX: type === 'col' ? x - currentPixelPos * dragDivisor : x,
            startY: type === 'row' ? y - currentPixelPos * dragDivisor : y,
            divisor: dragDivisor,
            existingCutValue: cutValue,
        };
        setCutPreview({ type: type, value: currentPixelPos });
        setDraggingExistingCut({ type: type, value: cutValue });
    };
    useEffect(function () {
        var onMove = function (e) {
            if (isPreview)
                return;
            if (!cutDragRef.current)
                return;
            cutDragRef.current.hasMoved = true;
            if ('touches' in e)
                e.preventDefault();
            var x = 'clientX' in e ? e.clientX : e.touches[0].clientX;
            var y = 'clientY' in e ? e.clientY : e.touches[0].clientY;
            var _a = cutDragRef.current, type = _a.type, startX = _a.startX, startY = _a.startY, divisor = _a.divisor;
            var localX = (x - startX) / divisor;
            var localY = (y - startY) / divisor;
            if (mode === 'grid') {
                // Allow dragging to full extent of grid for smooth preview
                if (type === 'row') {
                    var maxY = data.rows * cellSize;
                    setCutPreview({ type: type, value: Math.max(0, Math.min(maxY, localY)) });
                }
                if (type === 'col') {
                    var maxX = data.columns * cellSize;
                    setCutPreview({ type: type, value: Math.max(0, Math.min(maxX, localX)) });
                }
            }
            else {
                // Open mode - clamp to grid boundaries (0 to height/width)
                if (type === 'row') {
                    setCutPreview({ type: type, value: Math.max(0, Math.min(height, localY)) });
                }
                else {
                    setCutPreview({ type: type, value: Math.max(0, Math.min(width, localX)) });
                }
            }
        };
        var onUp = function () {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w;
            var currentCutPreview = cutPreviewRef.current;
            if (!cutDragRef.current || !currentCutPreview) {
                // Touch tap (no drag) on a new-cut arrow — enter kbCutMode for the panel flow.
                // Mouse clicks are excluded (isTouch=false) so desktop mouse users don't see the panel.
                if (cutDragRef.current && !currentCutPreview && !cutDragRef.current.hasMoved
                    && cutDragRef.current.existingCutValue == null && cutDragRef.current.isTouch) {
                    var tapType = cutDragRef.current.type;
                    var d = dataRef.current;
                    var existingCuts = tapType === 'row' ? ((_a = d.cutRows) !== null && _a !== void 0 ? _a : []) : ((_b = d.cutColumns) !== null && _b !== void 0 ? _b : []);
                    var limit = tapType === 'row' ? d.rows : d.columns;
                    if (existingCuts.length < limit - 1) {
                        setKbCutMode({ type: tapType, position: 1 });
                        setCutPreview({ type: tapType, value: 1 * cellSize });
                        announce(t('text.arrayModelCutStarted', {
                            type: t(tapType === 'row' ? 'text.row' : 'text.column'),
                            position: 1,
                        }));
                    }
                    cutDragRef.current = null;
                    return;
                }
                // Clear re-drag state even if drag was cancelled
                if (((_c = cutDragRef.current) === null || _c === void 0 ? void 0 : _c.existingCutValue) != null) {
                    setDraggingExistingCut(null);
                }
                cutDragRef.current = null;
                setCutPreview(null);
                return;
            }
            // Compute old and new data inline (don't rely on setTimeout + dataRef)
            var oldData = structuredClone(dataRef.current);
            var newData;
            var existingCutValue = cutDragRef.current.existingCutValue;
            var cutAnnouncement = '';
            if (mode === 'grid') {
                // Snap raw pixel position to nearest row/column index
                var snapped = Math.round(currentCutPreview.value / cellSize);
                var isRow = currentCutPreview.type === 'row';
                var limit = isRow ? oldData.rows : oldData.columns;
                if (existingCutValue != null) {
                    // ── Re-dragging an existing cut ──
                    var existingRows = __spreadArray([], ((_d = oldData.cutRows) !== null && _d !== void 0 ? _d : []), true);
                    var existingCols = __spreadArray([], ((_e = oldData.cutColumns) !== null && _e !== void 0 ? _e : []), true);
                    // Remove the original cut
                    if (isRow) {
                        var idx = existingRows.indexOf(existingCutValue);
                        if (idx !== -1)
                            existingRows.splice(idx, 1);
                    }
                    else {
                        var idx = existingCols.indexOf(existingCutValue);
                        if (idx !== -1)
                            existingCols.splice(idx, 1);
                    }
                    // Add at new position if valid (snapped >= 1 && < limit) and not duplicate
                    if (snapped >= 1 && snapped < limit) {
                        if (isRow && !existingRows.includes(snapped)) {
                            existingRows.push(snapped);
                        }
                        else if (!isRow && !existingCols.includes(snapped)) {
                            existingCols.push(snapped);
                        }
                        cutAnnouncement = t('text.coloredTilesCutPlaced', {
                            type: t(isRow ? 'text.row' : 'text.column'),
                            position: snapped,
                        });
                    }
                    else {
                        cutAnnouncement = t('text.coloredTilesCutRemoved', {
                            type: t(isRow ? 'text.row' : 'text.column'),
                        });
                    }
                    newData = __assign(__assign({}, oldData), { cutRows: existingRows, cutColumns: existingCols });
                    setDraggingExistingCut(null);
                    // Skip save if nothing actually changed
                    var unchanged = JSON.stringify((_f = oldData.cutRows) !== null && _f !== void 0 ? _f : []) === JSON.stringify(existingRows) &&
                        JSON.stringify((_g = oldData.cutColumns) !== null && _g !== void 0 ? _g : []) === JSON.stringify(existingCols);
                    if (unchanged) {
                        cutDragRef.current = null;
                        setCutPreview(null);
                        return;
                    }
                }
                else {
                    // ── New cut ──
                    // Validate: must be between 1 and limit-1 (cannot be at edges)
                    if (snapped < 1 || snapped >= limit) {
                        cutDragRef.current = null;
                        setCutPreview(null);
                        return;
                    }
                    var maxRowCuts = oldData.rows - 1;
                    var maxColCuts = oldData.columns - 1;
                    var existingRows = (_h = oldData.cutRows) !== null && _h !== void 0 ? _h : [];
                    var existingCols = (_j = oldData.cutColumns) !== null && _j !== void 0 ? _j : [];
                    newData = __assign(__assign({}, oldData), { cutRows: currentCutPreview.type === 'row'
                            ? existingRows.length < maxRowCuts
                                ? Array.from(new Set(__spreadArray(__spreadArray([], existingRows, true), [snapped], false)))
                                : existingRows
                            : existingRows, cutColumns: currentCutPreview.type === 'col'
                            ? existingCols.length < maxColCuts
                                ? Array.from(new Set(__spreadArray(__spreadArray([], existingCols, true), [snapped], false)))
                                : existingCols
                            : existingCols });
                    cutAnnouncement = t('text.coloredTilesCutPlaced', {
                        type: t(currentCutPreview.type === 'row' ? 'text.row' : 'text.column'),
                        position: snapped,
                    });
                }
            }
            else {
                // Open mode
                var percent_1 = currentCutPreview.type === 'row'
                    ? currentCutPreview.value / height
                    : currentCutPreview.value / width;
                // Minimum distance from edges (in pixels) to avoid arrow overlap
                var minEdgePx = 45;
                var isRow = currentCutPreview.type === 'row';
                var dimension = isRow ? height : width;
                var minPercent = minEdgePx / dimension;
                var maxPercent = 1 - minEdgePx / dimension;
                if (existingCutValue != null) {
                    // ── Re-dragging an existing cut in open mode ──
                    var existingRows = __spreadArray([], ((_l = (_k = oldData.openCuts) === null || _k === void 0 ? void 0 : _k.rows) !== null && _l !== void 0 ? _l : []), true);
                    var existingCols = __spreadArray([], ((_o = (_m = oldData.openCuts) === null || _m === void 0 ? void 0 : _m.cols) !== null && _o !== void 0 ? _o : []), true);
                    // Remove the original cut (existingCutValue is a percentage)
                    if (isRow) {
                        var idx = existingRows.findIndex(function (p) { return Math.abs(p - existingCutValue) < 0.001; });
                        if (idx !== -1)
                            existingRows.splice(idx, 1);
                    }
                    else {
                        var idx = existingCols.findIndex(function (p) { return Math.abs(p - existingCutValue) < 0.001; });
                        if (idx !== -1)
                            existingCols.splice(idx, 1);
                    }
                    // Add at new position if valid (away from edges) and not duplicate
                    if (percent_1 > minPercent && percent_1 < maxPercent) {
                        if (isRow && !existingRows.some(function (p) { return Math.abs(p - percent_1) < 0.001; })) {
                            existingRows.push(percent_1);
                        }
                        else if (!isRow && !existingCols.some(function (p) { return Math.abs(p - percent_1) < 0.001; })) {
                            existingCols.push(percent_1);
                        }
                    }
                    newData = __assign(__assign({}, oldData), { openCuts: { rows: existingRows, cols: existingCols } });
                    setDraggingExistingCut(null);
                    // Skip save if nothing actually changed
                    var unchanged = JSON.stringify((_q = (_p = oldData.openCuts) === null || _p === void 0 ? void 0 : _p.rows) !== null && _q !== void 0 ? _q : []) === JSON.stringify(existingRows) &&
                        JSON.stringify((_s = (_r = oldData.openCuts) === null || _r === void 0 ? void 0 : _r.cols) !== null && _s !== void 0 ? _s : []) === JSON.stringify(existingCols);
                    if (unchanged) {
                        cutDragRef.current = null;
                        setCutPreview(null);
                        return;
                    }
                }
                else {
                    // ── New cut in open mode ──
                    // Reject cuts too close to edges
                    if (percent_1 <= minPercent || percent_1 >= maxPercent) {
                        cutDragRef.current = null;
                        setCutPreview(null);
                        return;
                    }
                    var existingOpenRows = (_u = (_t = oldData.openCuts) === null || _t === void 0 ? void 0 : _t.rows) !== null && _u !== void 0 ? _u : [];
                    var existingOpenCols = (_w = (_v = oldData.openCuts) === null || _v === void 0 ? void 0 : _v.cols) !== null && _w !== void 0 ? _w : [];
                    var maxOpenCuts = 4;
                    newData = __assign(__assign({}, oldData), { openCuts: {
                            rows: currentCutPreview.type === 'row'
                                ? existingOpenRows.length < maxOpenCuts
                                    ? Array.from(new Set(__spreadArray(__spreadArray([], existingOpenRows, true), [percent_1], false)))
                                    : existingOpenRows
                                : existingOpenRows,
                            cols: currentCutPreview.type === 'col'
                                ? existingOpenCols.length < maxOpenCuts
                                    ? Array.from(new Set(__spreadArray(__spreadArray([], existingOpenCols, true), [percent_1], false)))
                                    : existingOpenCols
                                : existingOpenCols,
                        } });
                }
            }
            // Apply the computed new data
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.id === data.id && item.data.type === 'coloredTiles'
                        ? __assign(__assign({}, item), { data: newData }) : item;
                });
            });
            // Play cut sound when a line is placed
            playAudio('arrayCut');
            // Save state synchronously with pre-computed values
            saveState({
                type: data.id,
                id: data.id.split('_')[1],
                propertyName: 'completeData',
                old: JSON.stringify(oldData),
                new: JSON.stringify(newData),
            });
            if (cutAnnouncement)
                announce(cutAnnouncement);
            cutDragRef.current = null;
            setCutPreview(null);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove);
        window.addEventListener('touchend', onUp);
        // E2: same touchcancel coverage as the grid-drag effect.
        window.addEventListener('touchcancel', onUp);
        return function () {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
            window.removeEventListener('touchcancel', onUp);
        };
    }, [
        mode,
        scale,
        zoomLevel,
        cellSize,
        data.rows,
        data.columns,
        width,
        height,
        data.id,
        setManipulative,
        isPreview,
    ]);
    // Keyboard equivalents for cut-arrow drag (grid mode only).
    // Arrow keys move the cut by one cell; Delete/Backspace removes it.
    var moveCutByOne = function (type, currentValue, delta) {
        var _a, _b, _c, _d, _e;
        if (isPreview || !partModeEnabled || ((_a = data.lock) === null || _a === void 0 ? void 0 : _a.editing))
            return;
        if (mode !== 'grid')
            return;
        var oldData = structuredClone(dataRef.current);
        var isRow = type === 'row';
        var limit = isRow ? oldData.rows : oldData.columns;
        var newValue = currentValue + delta;
        var existing = isRow
            ? __spreadArray([], ((_b = oldData.cutRows) !== null && _b !== void 0 ? _b : []), true) : __spreadArray([], ((_c = oldData.cutColumns) !== null && _c !== void 0 ? _c : []), true);
        var idx = existing.indexOf(currentValue);
        if (idx === -1)
            return;
        if (newValue < 1 || newValue >= limit || existing.includes(newValue)) {
            announce(t('text.coloredTilesResizeBlocked'));
            return;
        }
        existing.splice(idx, 1, newValue);
        existing.sort(function (a, b) { return a - b; });
        var newData = __assign(__assign({}, oldData), { cutRows: isRow ? existing : (_d = oldData.cutRows) !== null && _d !== void 0 ? _d : [], cutColumns: !isRow ? existing : (_e = oldData.cutColumns) !== null && _e !== void 0 ? _e : [] });
        setManipulative(function (prev) {
            return prev.map(function (item) {
                return item.data.id === data.id && item.data.type === 'coloredTiles'
                    ? __assign(__assign({}, item), { data: newData }) : item;
            });
        });
        saveState({
            type: data.id,
            id: data.id.split('_')[1],
            propertyName: 'completeData',
            old: JSON.stringify(oldData),
            new: JSON.stringify(newData),
        });
        announce(t('text.coloredTilesCutPlaced', {
            type: t(isRow ? 'text.row' : 'text.column'),
            position: newValue,
        }));
    };
    var removeCut = function (type, currentValue) {
        var _a, _b, _c, _d, _e;
        if (isPreview || !partModeEnabled || ((_a = data.lock) === null || _a === void 0 ? void 0 : _a.editing))
            return;
        if (mode !== 'grid')
            return;
        var oldData = structuredClone(dataRef.current);
        var isRow = type === 'row';
        var existing = isRow
            ? __spreadArray([], ((_b = oldData.cutRows) !== null && _b !== void 0 ? _b : []), true) : __spreadArray([], ((_c = oldData.cutColumns) !== null && _c !== void 0 ? _c : []), true);
        var idx = existing.indexOf(currentValue);
        if (idx === -1)
            return;
        existing.splice(idx, 1);
        var newData = __assign(__assign({}, oldData), { cutRows: isRow ? existing : (_d = oldData.cutRows) !== null && _d !== void 0 ? _d : [], cutColumns: !isRow ? existing : (_e = oldData.cutColumns) !== null && _e !== void 0 ? _e : [] });
        setManipulative(function (prev) {
            return prev.map(function (item) {
                return item.data.id === data.id && item.data.type === 'coloredTiles'
                    ? __assign(__assign({}, item), { data: newData }) : item;
            });
        });
        saveState({
            type: data.id,
            id: data.id.split('_')[1],
            propertyName: 'completeData',
            old: JSON.stringify(oldData),
            new: JSON.stringify(newData),
        });
        announce(t('text.coloredTilesCutRemoved', {
            type: t(isRow ? 'text.row' : 'text.column'),
        }));
    };
    var handleCutKeyDown = function (e, type, cutValue) {
        var _a, _b, _c;
        if (isPreview || !partModeEnabled || ((_a = data.lock) === null || _a === void 0 ? void 0 : _a.editing))
            return;
        if (e.key === 'Delete' || e.key === 'Backspace') {
            e.preventDefault();
            e.stopPropagation();
            removeCut(type, cutValue);
            return;
        }
        // Enter / Space — pick up the cut for repositioning via the move panel (grid only).
        if ((e.key === 'Enter' || e.key === ' ') && mode === 'grid') {
            e.preventDefault();
            e.stopPropagation();
            var isRow = type === 'row';
            var oldData = structuredClone(dataRef.current);
            var existingRows = __spreadArray([], ((_b = oldData.cutRows) !== null && _b !== void 0 ? _b : []), true);
            var existingCols = __spreadArray([], ((_c = oldData.cutColumns) !== null && _c !== void 0 ? _c : []), true);
            if (isRow) {
                var idx = existingRows.indexOf(cutValue);
                if (idx !== -1)
                    existingRows.splice(idx, 1);
            }
            else {
                var idx = existingCols.indexOf(cutValue);
                if (idx !== -1)
                    existingCols.splice(idx, 1);
            }
            var newData_4 = __assign(__assign({}, oldData), { cutRows: existingRows, cutColumns: existingCols });
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.id === data.id && item.data.type === 'coloredTiles'
                        ? __assign(__assign({}, item), { data: newData_4 }) : item;
                });
            });
            saveState({
                type: data.id,
                id: data.id.split('_')[1],
                propertyName: 'completeData',
                old: JSON.stringify(oldData),
                new: JSON.stringify(newData_4),
            });
            setKbCutMode({ type: type, position: cutValue });
            return;
        }
    };
    /* ===== KEYBOARD CUT CREATION (ArrayModel-style; grid mode only) ===== */
    // Drive the existing preview line from kbCutMode. Convert the cell index to a
    // pixel value (same units the mouse-drag path uses). Don't clobber an in-progress
    // mouse drag (cutDragRef set) when clearing.
    useEffect(function () {
        if (kbCutMode) {
            setCutPreview({ type: kbCutMode.type, value: kbCutMode.position * cellSize });
        }
        else if (!cutDragRef.current) {
            setCutPreview(null);
        }
    }, [kbCutMode, cellSize]);
    // Begin placing a new cut: pick the lowest free index in [1, limit-1].
    var startKbCut = function (type) {
        var _a;
        if (isPreview || !partModeEnabled || ((_a = data.lock) === null || _a === void 0 ? void 0 : _a.editing) || mode !== 'grid')
            return;
        var isRow = type === 'row';
        var limit = isRow ? data.rows : data.columns;
        var existing = isRow ? cutRows : cutColumns;
        if (existing.length >= limit - 1) {
            announce(t('text.arrayModelCutMaxReached'));
            return;
        }
        var position = -1;
        for (var i = 1; i < limit; i++) {
            if (!existing.includes(i)) {
                position = i;
                break;
            }
        }
        if (position === -1) {
            announce(t('text.arrayModelCutMaxReached'));
            return;
        }
        setKbCutMode({ type: type, position: position });
        announce(t('text.arrayModelCutStarted', {
            type: t(isRow ? 'text.row' : 'text.column'),
            position: position,
        }));
    };
    // Commit the previewed cut into cutRows/cutColumns (mirrors the mouse onUp "new cut" path).
    var commitKbCut = function () {
        var _a, _b, _c, _d, _e;
        var current = kbCutModeRef.current;
        if (!current)
            return;
        if (isPreview || !partModeEnabled || ((_a = data.lock) === null || _a === void 0 ? void 0 : _a.editing) || mode !== 'grid') {
            setKbCutMode(null);
            return;
        }
        var type = current.type, position = current.position;
        var isRow = type === 'row';
        var oldData = structuredClone(dataRef.current);
        var limit = isRow ? oldData.rows : oldData.columns;
        var existing = isRow ? (_b = oldData.cutRows) !== null && _b !== void 0 ? _b : [] : (_c = oldData.cutColumns) !== null && _c !== void 0 ? _c : [];
        if (position < 1 || position >= limit) {
            setKbCutMode(null);
            announce(t('text.arrayModelCutCancelled'));
            return;
        }
        if (existing.includes(position)) {
            announce(t('text.arrayModelCutAlreadyExists'));
            return;
        }
        if (existing.length >= limit - 1) {
            announce(t('text.arrayModelCutMaxReached'));
            return;
        }
        var updated = Array.from(new Set(__spreadArray(__spreadArray([], existing, true), [position], false))).sort(function (a, b) { return a - b; });
        var newData = __assign(__assign({}, oldData), { cutRows: isRow ? updated : (_d = oldData.cutRows) !== null && _d !== void 0 ? _d : [], cutColumns: !isRow ? updated : (_e = oldData.cutColumns) !== null && _e !== void 0 ? _e : [] });
        setManipulative(function (prev) {
            return prev.map(function (item) {
                return item.data.id === data.id && item.data.type === 'coloredTiles'
                    ? __assign(__assign({}, item), { data: newData }) : item;
            });
        });
        playAudio('arrayCut');
        saveState({
            type: data.id,
            id: data.id.split('_')[1],
            propertyName: 'completeData',
            old: JSON.stringify(oldData),
            new: JSON.stringify(newData),
        });
        announce(t('text.coloredTilesCutPlaced', {
            type: t(isRow ? 'text.row' : 'text.column'),
            position: position,
        }));
        setKbCutMode(null);
        // Return focus to the create-part arrow if still enabled, otherwise the tile wrapper.
        requestAnimationFrame(function () {
            var _a;
            var target = isRow ? cutArrowRightRef.current : cutArrowBottomRef.current;
            if (target && target.tabIndex !== -1) {
                target.focus();
            }
            else {
                (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
        });
    };
    // When kbCutMode first activates, focus the move panel's first button on touch devices
    // so VoiceOver lands on the panel rather than drifting to the toolbar.
    var kbCutModeWasNullRef = useRef(true);
    // Auto-focus the first enabled button when the panel opens.
    useEffect(function () {
        if (kbCutMode && kbCutModeWasNullRef.current) {
            kbCutModeWasNullRef.current = false;
            requestAnimationFrame(function () {
                var _a;
                var firstEnabled = (_a = cutMovePanelRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('button:not(:disabled)');
                firstEnabled === null || firstEnabled === void 0 ? void 0 : firstEnabled.focus();
            });
        }
        else if (!kbCutMode) {
            kbCutModeWasNullRef.current = true;
        }
    }, [kbCutMode]);
    // If a panel button is focused and becomes disabled (e.g. cut reached a boundary),
    // move focus to the next enabled button so it never rests on a disabled element.
    useEffect(function () {
        if (!kbCutMode)
            return;
        var panel = cutMovePanelRef.current;
        if (!panel)
            return;
        var focused = document.activeElement;
        if ((focused === null || focused === void 0 ? void 0 : focused.disabled) && panel.contains(focused)) {
            var firstEnabled = panel.querySelector('button:not(:disabled)');
            firstEnabled === null || firstEnabled === void 0 ? void 0 : firstEnabled.focus();
        }
    }, [kbCutMode, cutRows, cutColumns]);
    var moveKbCut = useCallback(function (delta) {
        var cur = kbCutModeRef.current;
        if (!cur)
            return;
        var isRow = cur.type === 'row';
        setKbCutMode(function (prev) {
            var _a, _b;
            if (!prev)
                return prev;
            var limit = isRow ? dataRef.current.rows : dataRef.current.columns;
            var existing = isRow ? ((_a = dataRef.current.cutRows) !== null && _a !== void 0 ? _a : []) : ((_b = dataRef.current.cutColumns) !== null && _b !== void 0 ? _b : []);
            var next = prev.position;
            for (var i = prev.position + delta; i >= 1 && i < limit; i += delta) {
                if (!existing.includes(i)) {
                    next = i;
                    break;
                }
            }
            if (next === prev.position)
                return prev;
            announce(t('text.arrayModelCutPreviewRow', { position: next, total: limit }));
            return __assign(__assign({}, prev), { position: next });
        });
    }, [t]);
    // Arrow keys move the preview; Enter/Space commits; Escape cancels.
    var handleKbCutKeyDown = function (e, type) {
        if (!kbCutModeRef.current)
            return;
        var isRow = type === 'row';
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            setKbCutMode(null);
            announce(t('text.arrayModelCutCancelled'));
            return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            commitKbCut();
            return;
        }
        var movingKey = isRow
            ? e.key === 'ArrowUp' || e.key === 'ArrowDown'
            : e.key === 'ArrowLeft' || e.key === 'ArrowRight';
        if (!movingKey)
            return;
        e.preventDefault();
        e.stopPropagation();
        var delta = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : -1;
        var limit = isRow ? data.rows : data.columns;
        var existing = isRow ? cutRows : cutColumns;
        setKbCutMode(function (prev) {
            if (!prev)
                return prev;
            // Step toward the next free index, skipping positions that already have a cut.
            var next = prev.position;
            for (var i = prev.position + delta; i >= 1 && i < limit; i += delta) {
                if (!existing.includes(i)) {
                    next = i;
                    break;
                }
            }
            if (next === prev.position)
                return prev;
            announce(isRow
                ? t('text.arrayModelCutPreviewRow', { position: next, total: limit })
                : t('text.arrayModelCutPreviewCol', { position: next, total: limit }));
            return __assign(__assign({}, prev), { position: next });
        });
    };
    // Cancel any in-progress keyboard cut when part mode turns off, the tile is
    // deselected, or editing gets locked.
    useEffect(function () {
        var _a;
        if (!kbCutMode)
            return;
        if (!partModeEnabled || ((_a = data.lock) === null || _a === void 0 ? void 0 : _a.editing) || !selectedTool.includes(data.id)) {
            setKbCutMode(null);
        }
    }, [partModeEnabled, selectedTool, (_k = data.lock) === null || _k === void 0 ? void 0 : _k.editing, data.id, kbCutMode]);
    /* ================= LABELS & EXPRESSIONS (GRID ONLY) ================= */
    var colSegments = useMemo(function () {
        if (mode !== 'grid')
            return [];
        // Account for preview during dragging
        var cols = __spreadArray([], cutColumns, true);
        if (cutPreview && cutPreview.type === 'col' && draggingExistingCut) {
            // Remove the old cut being dragged
            cols = cols.filter(function (c) { return c !== draggingExistingCut.value; });
            // Add the preview position (snapped)
            var snapped = Math.round(cutPreview.value / cellSize);
            if (snapped >= 1 && snapped < data.columns && !cols.includes(snapped)) {
                cols.push(snapped);
            }
            cols.sort(function (a, b) { return a - b; });
        }
        else if (cutPreview && !draggingExistingCut && cutPreview.type === 'col') {
            // Adding a new cut - include preview
            var snapped = Math.round(cutPreview.value / cellSize);
            if (snapped >= 1 && snapped < data.columns && !cols.includes(snapped)) {
                cols.push(snapped);
                cols.sort(function (a, b) { return a - b; });
            }
        }
        var pts = __spreadArray(__spreadArray([0], cols, true), [data.columns], false);
        return pts.slice(1).map(function (v, i) { return v - pts[i]; });
    }, [mode, cutColumns, data.columns, cellSize, cutPreview, draggingExistingCut]);
    var rowSegments = useMemo(function () {
        if (mode !== 'grid')
            return [];
        // Account for preview during dragging
        var rows = __spreadArray([], cutRows, true);
        if (cutPreview && cutPreview.type === 'row' && draggingExistingCut) {
            // Remove the old cut being dragged
            rows = rows.filter(function (r) { return r !== draggingExistingCut.value; });
            // Add the preview position (snapped)
            var snapped = Math.round(cutPreview.value / cellSize);
            if (snapped >= 1 && snapped < data.rows && !rows.includes(snapped)) {
                rows.push(snapped);
            }
            rows.sort(function (a, b) { return a - b; });
        }
        else if (cutPreview && !draggingExistingCut && cutPreview.type === 'row') {
            // Adding a new cut - include preview
            var snapped = Math.round(cutPreview.value / cellSize);
            if (snapped >= 1 && snapped < data.rows && !rows.includes(snapped)) {
                rows.push(snapped);
                rows.sort(function (a, b) { return a - b; });
            }
        }
        var pts = __spreadArray(__spreadArray([0], rows, true), [data.rows], false);
        return pts.slice(1).map(function (v, i) { return v - pts[i]; });
    }, [mode, cutRows, data.rows, cellSize, cutPreview, draggingExistingCut]);
    var rowCuts = __spreadArray(__spreadArray([0], cutRows, true), [data.rows], false);
    var colCuts = __spreadArray(__spreadArray([0], cutColumns, true), [data.columns], false);
    var products = useMemo(function () {
        var list = [];
        rowSegments.forEach(function (r) { return colSegments.forEach(function (c) { return list.push(r * c); }); });
        return list;
    }, [rowSegments, colSegments]);
    var total = products.reduce(function (a, b) { return a + b; }, 0);
    var sectionSolidColors = useMemo(function () {
        var rCuts = __spreadArray(__spreadArray([0], cutRows, true), [data.rows], false);
        var cCuts = __spreadArray(__spreadArray([0], cutColumns, true), [data.columns], false);
        var result = {};
        rowSegments.forEach(function (_, rIdx) {
            colSegments.forEach(function (_, cIdx) {
                var _a, _b;
                var rStart = rCuts[rIdx], rEnd = rCuts[rIdx + 1];
                var cStart = cCuts[cIdx], cEnd = cCuts[cIdx + 1];
                var solidColor = null;
                var isUniform = true;
                outer: for (var r = rStart; r < rEnd; r++) {
                    for (var c = cStart; c < cEnd; c++) {
                        var tileColor = (_b = (_a = data.partColors) === null || _a === void 0 ? void 0 : _a["".concat(r, "-").concat(c)]) !== null && _b !== void 0 ? _b : null;
                        if (!tileColor) {
                            isUniform = false;
                            break outer;
                        }
                        if (solidColor === null)
                            solidColor = tileColor;
                        else if (solidColor !== tileColor) {
                            isUniform = false;
                            break outer;
                        }
                    }
                }
                result["".concat(rIdx, "-").concat(cIdx)] = isUniform ? solidColor : null;
            });
        });
        return result;
    }, [cutRows, cutColumns, data.rows, data.columns, data.partColors, rowSegments, colSegments]);
    var isOpen = mode === 'open';
    var rowCutPoints = useMemo(function () {
        var _a, _b;
        if (!isOpen) {
            // Grid mode - account for preview during dragging
            var rows = __spreadArray([], cutRows, true);
            if (cutPreview && cutPreview.type === 'row') {
                if (draggingExistingCut) {
                    // Remove the old cut being dragged
                    rows = rows.filter(function (r) { return r !== draggingExistingCut.value; });
                }
                // Add the preview position (convert pixels to index for positioning)
                var previewIndex = cutPreview.value / cellSize;
                if (previewIndex > 0 && previewIndex < data.rows) {
                    rows.push(previewIndex);
                    rows.sort(function (a, b) { return a - b; });
                }
            }
            return __spreadArray(__spreadArray([0], rows, true), [data.rows], false);
        }
        // For open mode, account for preview during dragging
        var rowCuts = __spreadArray([], ((_b = (_a = data.openCuts) === null || _a === void 0 ? void 0 : _a.rows) !== null && _b !== void 0 ? _b : []), true);
        if (cutPreview && draggingExistingCut) {
            var previewPercent = cutPreview.type === 'row' ? cutPreview.value / height : cutPreview.value / width;
            if (cutPreview.type === 'row') {
                // Remove the old cut being dragged
                rowCuts = rowCuts.filter(function (p) { return Math.abs(p - draggingExistingCut.value) >= 0.001; });
                // Add the preview position if valid
                if (previewPercent > 0 && previewPercent < 1) {
                    rowCuts.push(previewPercent);
                }
            }
        }
        else if (cutPreview && !draggingExistingCut && cutPreview.type === 'row') {
            // Adding a new row cut - include the preview
            var previewPercent = cutPreview.value / height;
            if (previewPercent > 0 && previewPercent < 1) {
                rowCuts.push(previewPercent);
            }
        }
        return __spreadArray(__spreadArray([0], rowCuts.sort(function (a, b) { return a - b; }), true), [1], false);
    }, [
        isOpen,
        cutRows,
        data.rows,
        data.openCuts,
        height,
        width,
        cellSize,
        cutPreview,
        draggingExistingCut,
    ]);
    var colCutPoints = useMemo(function () {
        var _a, _b;
        if (!isOpen) {
            // Grid mode - account for preview during dragging
            var cols = __spreadArray([], cutColumns, true);
            if (cutPreview && cutPreview.type === 'col') {
                if (draggingExistingCut) {
                    // Remove the old cut being dragged
                    cols = cols.filter(function (c) { return c !== draggingExistingCut.value; });
                }
                // Add the preview position (convert pixels to index for positioning)
                var previewIndex = cutPreview.value / cellSize;
                if (previewIndex > 0 && previewIndex < data.columns) {
                    cols.push(previewIndex);
                    cols.sort(function (a, b) { return a - b; });
                }
            }
            return __spreadArray(__spreadArray([0], cols, true), [data.columns], false);
        }
        // For open mode, account for preview during dragging
        var colCuts = __spreadArray([], ((_b = (_a = data.openCuts) === null || _a === void 0 ? void 0 : _a.cols) !== null && _b !== void 0 ? _b : []), true);
        if (cutPreview && draggingExistingCut) {
            var previewPercent = cutPreview.type === 'row' ? cutPreview.value / height : cutPreview.value / width;
            if (cutPreview.type === 'col') {
                // Remove the old cut being dragged
                colCuts = colCuts.filter(function (p) { return Math.abs(p - draggingExistingCut.value) >= 0.001; });
                // Add the preview position if valid
                if (previewPercent > 0 && previewPercent < 1) {
                    colCuts.push(previewPercent);
                }
            }
        }
        else if (cutPreview && !draggingExistingCut && cutPreview.type === 'col') {
            // Adding a new col cut - include the preview
            var previewPercent = cutPreview.value / width;
            if (previewPercent > 0 && previewPercent < 1) {
                colCuts.push(previewPercent);
            }
        }
        return __spreadArray(__spreadArray([0], colCuts.sort(function (a, b) { return a - b; }), true), [1], false);
    }, [
        isOpen,
        cutColumns,
        data.columns,
        data.openCuts,
        height,
        width,
        cellSize,
        cutPreview,
        draggingExistingCut,
    ]);
    var shouldShowRedLinearPieces = useMemo(function () {
        return ((((data.topLinearSegments && data.topLinearSegments.length > 0) ||
            (data.leftLinearSegments && data.leftLinearSegments.length > 0)) &&
            (mode === 'grid' || mode === 'open')) ||
            (data.generationMode === 'manual' && !isGroup));
    }, [data.topLinearSegments, data.leftLinearSegments, data.generationMode, mode]);
    // Calculate visual label data for smooth dragging (grid mode only)
    var rowLabelData = useMemo(function () {
        return rowCutPoints.slice(0, -1).map(function (start, i) {
            var end = rowCutPoints[i + 1];
            var diff = end - start;
            var pixelHeight = isOpen ? diff * height : diff * cellSize;
            var unitValue = isOpen ? Math.round(diff * data.rows) : Math.round(diff);
            return { height: pixelHeight, value: unitValue };
        });
    }, [isOpen, rowCutPoints, cellSize, height, data.rows]);
    var colLabelData = useMemo(function () {
        return colCutPoints.slice(0, -1).map(function (start, i) {
            var end = colCutPoints[i + 1];
            var diff = end - start;
            var pixelWidth = isOpen ? diff * width : diff * cellSize;
            var unitValue = isOpen ? Math.round(diff * data.columns) : Math.round(diff);
            return { width: pixelWidth, value: unitValue };
        });
    }, [isOpen, colCutPoints, cellSize, width, data.columns]);
    // Spoken (screen-reader) strings for the Expression / Product / Dimension
    // label displays. Shared with the context menu via coloredTilesAria helpers so
    // the focusable display regions and the toggle announcements stay in sync.
    var expressionAria = useMemo(function () { return buildColoredTilesExpressionAria(data, t); }, [data.rows, data.columns, data.cutRows, data.cutColumns]);
    var productAria = useMemo(function () { return buildColoredTilesProductAria(data, t); }, [data.rows, data.columns, data.cutRows, data.cutColumns]);
    var dimensionLabelsAria = useMemo(function () { return buildColoredTilesDimensionLabelsAria(data, t); }, [data.rows, data.columns, data.cutRows, data.cutColumns, data.mode, data.openCuts]);
    var openRegions = React.useMemo(function () {
        var _a, _b, _c, _d;
        if (mode !== 'open')
            return [];
        // Start with the current cuts
        var rowCuts = __spreadArray([], ((_b = (_a = data.openCuts) === null || _a === void 0 ? void 0 : _a.rows) !== null && _b !== void 0 ? _b : []), true);
        var colCuts = __spreadArray([], ((_d = (_c = data.openCuts) === null || _c === void 0 ? void 0 : _c.cols) !== null && _d !== void 0 ? _d : []), true);
        // If dragging an existing cut, update the arrays to reflect the preview position
        if (cutPreview && draggingExistingCut) {
            var previewPercent = cutPreview.type === 'row' ? cutPreview.value / height : cutPreview.value / width;
            if (cutPreview.type === 'row') {
                // Remove the old cut being dragged
                rowCuts = rowCuts.filter(function (p) { return Math.abs(p - draggingExistingCut.value) >= 0.001; });
                // Add the preview position if it's valid
                if (previewPercent > 0 && previewPercent < 1) {
                    rowCuts.push(previewPercent);
                }
            }
            else {
                // Remove the old cut being dragged
                colCuts = colCuts.filter(function (p) { return Math.abs(p - draggingExistingCut.value) >= 0.001; });
                // Add the preview position if it's valid
                if (previewPercent > 0 && previewPercent < 1) {
                    colCuts.push(previewPercent);
                }
            }
        }
        else if (cutPreview && !draggingExistingCut) {
            // Adding a new cut - include the preview position
            var previewPercent = cutPreview.type === 'row' ? cutPreview.value / height : cutPreview.value / width;
            if (cutPreview.type === 'row' && previewPercent > 0 && previewPercent < 1) {
                rowCuts.push(previewPercent);
            }
            else if (cutPreview.type === 'col' && previewPercent > 0 && previewPercent < 1) {
                colCuts.push(previewPercent);
            }
        }
        // Add boundaries and sort
        var finalRowCuts = __spreadArray(__spreadArray([0], rowCuts.sort(function (a, b) { return a - b; }), true), [1], false);
        var finalColCuts = __spreadArray(__spreadArray([0], colCuts.sort(function (a, b) { return a - b; }), true), [1], false);
        var regions = [];
        for (var r = 0; r < finalRowCuts.length - 1; r++) {
            for (var c = 0; c < finalColCuts.length - 1; c++) {
                var x = finalColCuts[c] * width;
                var y = finalRowCuts[r] * height;
                var w = (finalColCuts[c + 1] - finalColCuts[c]) * width;
                var h = (finalRowCuts[r + 1] - finalRowCuts[r]) * height;
                regions.push({
                    x: x,
                    y: y,
                    w: w,
                    h: h,
                    key: "open-".concat(r, "-").concat(c),
                });
            }
        }
        return regions;
    }, [mode, data.openCuts, width, height, cutPreview, draggingExistingCut]);
    var renderedRowCuts = useMemo(function () {
        var _a, _b;
        if (mode === 'grid') {
            return cutRows.map(function (r) { return r * cellSize; });
        }
        // Open mode - account for dragging
        var rowCuts = __spreadArray([], ((_b = (_a = data.openCuts) === null || _a === void 0 ? void 0 : _a.rows) !== null && _b !== void 0 ? _b : []), true);
        if (cutPreview && cutPreview.type === 'row' && draggingExistingCut) {
            // Remove the old cut being dragged
            rowCuts = rowCuts.filter(function (p) { return Math.abs(p - draggingExistingCut.value) >= 0.001; });
        }
        return rowCuts.map(function (p) { return p * height; });
    }, [mode, cutRows, data.openCuts, cellSize, height, cutPreview, draggingExistingCut]);
    var renderedColCuts = useMemo(function () {
        var _a, _b;
        if (mode === 'grid') {
            return cutColumns.map(function (c) { return c * cellSize; });
        }
        // Open mode - account for dragging
        var colCuts = __spreadArray([], ((_b = (_a = data.openCuts) === null || _a === void 0 ? void 0 : _a.cols) !== null && _b !== void 0 ? _b : []), true);
        if (cutPreview && cutPreview.type === 'col' && draggingExistingCut) {
            // Remove the old cut being dragged
            colCuts = colCuts.filter(function (p) { return Math.abs(p - draggingExistingCut.value) >= 0.001; });
        }
        return colCuts.map(function (p) { return p * width; });
    }, [mode, cutColumns, data.openCuts, cellSize, width, cutPreview, draggingExistingCut]);
    var onPartClick = function () {
        var _a;
        if (isPreview)
            return;
        // Read the CURRENT value from dataRef (not the `partModeEnabled` state):
        // the `bottomPanel` listener is registered once (deps [data.id, isPreview])
        // and captures a stale closure, so reading state would compute `!stale`
        // every time and only ever toggle one direction. dataRef.current is always
        // fresh (same pattern as toggleExpression).
        var current = (_a = dataRef.current.partModeEnabled) !== null && _a !== void 0 ? _a : true;
        var newPartMode = !current;
        setPartModeEnabled(newPartMode);
        if (current) {
            setSelectedPartKeys([]);
        }
        announce(t(newPartMode ? 'text.coloredTilesPartModeOn' : 'text.coloredTilesPartModeOff'));
    };
    // When partMode is turned ON (false → true), focus the cut arrow so the user can
    // immediately start placing a cut. useEffect guarantees the arrows are in the DOM.
    var prevPartModeRef = useRef(partModeEnabled);
    useEffect(function () {
        var wasOff = !prevPartModeRef.current;
        prevPartModeRef.current = partModeEnabled;
        if (!partModeEnabled || !wasOff)
            return;
        // Use setTimeout so our focus fires after iOS's own focus-restoration
        // (which runs after a context menu closes on iPad).
        var timer = setTimeout(function () {
            var _a, _b;
            var target = ((_a = cutArrowRightRef.current) === null || _a === void 0 ? void 0 : _a.tabIndex) !== -1
                ? cutArrowRightRef.current
                : ((_b = cutArrowBottomRef.current) === null || _b === void 0 ? void 0 : _b.tabIndex) !== -1
                    ? cutArrowBottomRef.current
                    : null;
            target === null || target === void 0 ? void 0 : target.focus();
        }, 100);
        return function () { return clearTimeout(timer); };
    }, [partModeEnabled]);
    var onSelectClick = function () {
        var _a;
        if (isPreview)
            return;
        // See onPartClick — read the live value from dataRef, not the stale state.
        var current = (_a = dataRef.current.selectModeEnabled) !== null && _a !== void 0 ? _a : false;
        var newSelectMode = !current;
        setSelectModeEnabled(newSelectMode);
        if (current) {
            setSelectedPartKeys([]);
        }
        announce(t(newSelectMode ? 'text.coloredTilesSelectModeOn' : 'text.coloredTilesSelectModeOff'));
    };
    // ================= RESIZE HANDLERS (OPEN MODE) =================
    var onResizeStart = function (e, corner) {
        var _a, _b;
        if (isPreview)
            return;
        e.stopPropagation();
        e.preventDefault();
        setSelectedTool([data.id]);
        setSelectedManipulativeId(data.id);
        var x = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var y = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        if (data.generationMode !== 'outline' || ((_a = data.lock) === null || _a === void 0 ? void 0 : _a.editing) || ((_b = data.lock) === null || _b === void 0 ? void 0 : _b.positioning))
            return;
        resizeDragRef.current = {
            corner: corner,
            startX: x,
            startY: y,
            startRows: data.rows,
            startCols: data.columns,
            startCenterX: data.center.x,
            startCenterY: data.center.y,
            startPartColors: structuredClone(data.partColors || {}),
            startCutRows: __spreadArray([], (data.cutRows || []), true),
            startCutCols: __spreadArray([], (data.cutColumns || []), true),
            startTopLinearSegments: __spreadArray([], (data.topLinearSegments || []), true),
            startLeftLinearSegments: __spreadArray([], (data.leftLinearSegments || []), true),
            startFullData: structuredClone(data),
        };
        lastUpdateDataRef.current = structuredClone(data);
    };
    // Set up resize drag listeners
    useEffect(function () {
        var onResizeMove = function (e) {
            var _a;
            if (isPreview)
                return;
            if (!resizeDragRef.current || ((_a = dataRef.current.lock) === null || _a === void 0 ? void 0 : _a.editing))
                return;
            if ('touches' in e)
                e.preventDefault();
            var x = 'clientX' in e ? e.clientX : e.touches[0].clientX;
            var y = 'clientY' in e ? e.clientY : e.touches[0].clientY;
            var _b = resizeDragRef.current, corner = _b.corner, startX = _b.startX, startY = _b.startY, startRows = _b.startRows, startCols = _b.startCols, startCenterX = _b.startCenterX, startCenterY = _b.startCenterY;
            // Calculate delta in pixels (canvas coordinates)
            var deltaX = (x - startX) / (scale * zoomLevel);
            var deltaY = (y - startY) / (scale * zoomLevel);
            var cellsX = deltaX / WORKSPACE_CELL_SIZE;
            var cellsY = deltaY / WORKSPACE_CELL_SIZE;
            var newRows = startRows;
            var newCols = startCols;
            if (corner === 'bottomRight') {
                newRows = Math.max(1, Math.round(startRows + cellsY));
                newCols = Math.max(1, Math.round(startCols + cellsX));
            }
            else if (corner === 'topLeft') {
                newRows = Math.max(1, Math.round(startRows - cellsY));
                newCols = Math.max(1, Math.round(startCols - cellsX));
            }
            // Adjust center so the opposite corner stays fixed
            var startW = startCols * WORKSPACE_CELL_SIZE;
            var startH = startRows * WORKSPACE_CELL_SIZE;
            var newW = newCols * WORKSPACE_CELL_SIZE;
            var newH = newRows * WORKSPACE_CELL_SIZE;
            var newCenterX = startCenterX;
            var newCenterY = startCenterY;
            if (corner === 'bottomRight') {
                // Fix top-left corner
                newCenterX = startCenterX + ((newW - startW) * scale) / 2;
                newCenterY = startCenterY + ((newH - startH) * scale) / 2;
            }
            else if (corner === 'topLeft') {
                // Fix bottom-right corner
                newCenterX = startCenterX - ((newW - startW) * scale) / 2;
                newCenterY = startCenterY - ((newH - startH) * scale) / 2;
            }
            // Update dimensions and center
            if (dataRef.current.generationMode === 'outline' && resizeDragRef.current) {
                var rowOffsetVal_1 = corner === 'topLeft' ? newRows - startRows : 0;
                var colOffsetVal_1 = corner === 'topLeft' ? newCols - startCols : 0;
                var wouldLoseContent = Object.entries(resizeDragRef.current.startPartColors || {}).some(function (_a) {
                    var key = _a[0], val = _a[1];
                    if (!val)
                        return false;
                    var _b = key.split('-').map(Number), r = _b[0], c = _b[1];
                    var nr = r + rowOffsetVal_1;
                    var nc = c + colOffsetVal_1;
                    return nr < 0 || nr >= newRows || nc < 0 || nc >= newCols;
                });
                if (wouldLoseContent)
                    return;
            }
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    var _a, _b, _c, _d, _e;
                    if (item.data.id === data.id && item.data.type === 'coloredTiles') {
                        var rowOffset_1 = corner === 'topLeft' ? newRows - startRows : 0;
                        var colOffset_1 = corner === 'topLeft' ? newCols - startCols : 0;
                        var newPartColors_1 = {};
                        Object.entries(((_a = resizeDragRef.current) === null || _a === void 0 ? void 0 : _a.startPartColors) || {}).forEach(function (_a) {
                            var key = _a[0], val = _a[1];
                            var _b = key.split('-').map(Number), r = _b[0], c = _b[1];
                            var nr = r + rowOffset_1;
                            var nc = c + colOffset_1;
                            if (nr >= 0 && nr < newRows && nc >= 0 && nc < newCols) {
                                newPartColors_1["".concat(nr, "-").concat(nc)] = val;
                            }
                        });
                        var newCutRows = (((_b = resizeDragRef.current) === null || _b === void 0 ? void 0 : _b.startCutRows) || [])
                            .map(function (v) { return v + rowOffset_1; })
                            .filter(function (v) { return v > 0 && v < newRows; });
                        var newCutCols = (((_c = resizeDragRef.current) === null || _c === void 0 ? void 0 : _c.startCutCols) || [])
                            .map(function (v) { return v + colOffset_1; })
                            .filter(function (v) { return v > 0 && v < newCols; });
                        var newTopLinearSegments = (((_d = resizeDragRef.current) === null || _d === void 0 ? void 0 : _d.startTopLinearSegments) || [])
                            .map(function (v) { return v + colOffset_1; })
                            .filter(function (v) { return v >= 0 && v < newCols; });
                        var newLeftLinearSegments = (((_e = resizeDragRef.current) === null || _e === void 0 ? void 0 : _e.startLeftLinearSegments) || [])
                            .map(function (v) { return v + rowOffset_1; })
                            .filter(function (v) { return v >= 0 && v < newRows; });
                        var newDataObj = __assign(__assign({}, item.data), { rows: newRows, columns: newCols, center: { x: newCenterX, y: newCenterY }, partColors: newPartColors_1, cutRows: newCutRows, cutColumns: newCutCols, topLinearSegments: newTopLinearSegments, leftLinearSegments: newLeftLinearSegments, dimensions: {
                                width: newCols * WORKSPACE_CELL_SIZE,
                                height: newRows * WORKSPACE_CELL_SIZE,
                            } });
                        lastUpdateDataRef.current = newDataObj;
                        return __assign(__assign({}, item), { data: newDataObj });
                    }
                    return item;
                });
            });
        };
        var onResizeUp = function () {
            if (!resizeDragRef.current)
                return;
            var oldData = resizeDragRef.current.startFullData;
            var newData = lastUpdateDataRef.current;
            // Only save state if dimensions actually changed
            if (newData && (oldData.rows !== newData.rows || oldData.columns !== newData.columns)) {
                saveState({
                    type: data.id,
                    id: data.id.split('_')[1],
                    propertyName: 'completeData',
                    old: JSON.stringify(oldData),
                    new: JSON.stringify(newData),
                });
            }
            resizeDragRef.current = null;
            lastUpdateDataRef.current = null;
        };
        window.addEventListener('mousemove', onResizeMove);
        window.addEventListener('mouseup', onResizeUp);
        window.addEventListener('touchmove', onResizeMove);
        window.addEventListener('touchend', onResizeUp);
        return function () {
            window.removeEventListener('mousemove', onResizeMove);
            window.removeEventListener('mouseup', onResizeUp);
            window.removeEventListener('touchmove', onResizeMove);
            window.removeEventListener('touchend', onResizeUp);
        };
    }, [scale, zoomLevel, cellSize, data.id, mode, setManipulative, isPreview]);
    // Keyboard equivalent of the mouse resize drag: one arrow press = one cell.
    // Mirrors onResizeMove's anchoring math so the opposite corner stays fixed.
    var resizeByOneCell = function (corner, dRows, dCols) {
        var _a, _b, _c;
        if (isPreview)
            return;
        var oldData = structuredClone(dataRef.current);
        if (oldData.generationMode !== 'outline' || ((_a = oldData.lock) === null || _a === void 0 ? void 0 : _a.editing) || ((_b = oldData.lock) === null || _b === void 0 ? void 0 : _b.positioning))
            return;
        var startRows = oldData.rows;
        var startCols = oldData.columns;
        var newRows = Math.max(1, startRows + dRows);
        var newCols = Math.max(1, startCols + dCols);
        if (newRows === startRows && newCols === startCols) {
            announce(t('text.coloredTilesResizeBlocked'));
            return;
        }
        var tileScale = (_c = oldData.scale) !== null && _c !== void 0 ? _c : 1;
        var startW = startCols * WORKSPACE_CELL_SIZE;
        var startH = startRows * WORKSPACE_CELL_SIZE;
        var newW = newCols * WORKSPACE_CELL_SIZE;
        var newH = newRows * WORKSPACE_CELL_SIZE;
        var newCenterX = corner === 'bottomRight'
            ? oldData.center.x + ((newW - startW) * tileScale) / 2
            : oldData.center.x - ((newW - startW) * tileScale) / 2;
        var newCenterY = corner === 'bottomRight'
            ? oldData.center.y + ((newH - startH) * tileScale) / 2
            : oldData.center.y - ((newH - startH) * tileScale) / 2;
        var rowOffset = corner === 'topLeft' ? newRows - startRows : 0;
        var colOffset = corner === 'topLeft' ? newCols - startCols : 0;
        var wouldLoseContent = Object.entries(oldData.partColors || {}).some(function (_a) {
            var key = _a[0], val = _a[1];
            if (!val)
                return false;
            var _b = key.split('-').map(Number), r = _b[0], c = _b[1];
            var nr = r + rowOffset;
            var nc = c + colOffset;
            return nr < 0 || nr >= newRows || nc < 0 || nc >= newCols;
        });
        if (wouldLoseContent) {
            announce(t('text.coloredTilesResizeBlocked'));
            return;
        }
        var newPartColors = {};
        Object.entries(oldData.partColors || {}).forEach(function (_a) {
            var key = _a[0], val = _a[1];
            var _b = key.split('-').map(Number), r = _b[0], c = _b[1];
            var nr = r + rowOffset;
            var nc = c + colOffset;
            if (nr >= 0 && nr < newRows && nc >= 0 && nc < newCols) {
                newPartColors["".concat(nr, "-").concat(nc)] = val;
            }
        });
        var newCutRows = (oldData.cutRows || [])
            .map(function (v) { return v + rowOffset; })
            .filter(function (v) { return v > 0 && v < newRows; });
        var newCutCols = (oldData.cutColumns || [])
            .map(function (v) { return v + colOffset; })
            .filter(function (v) { return v > 0 && v < newCols; });
        var newTopLinearSegments = (oldData.topLinearSegments || [])
            .map(function (v) { return v + colOffset; })
            .filter(function (v) { return v >= 0 && v < newCols; });
        var newLeftLinearSegments = (oldData.leftLinearSegments || [])
            .map(function (v) { return v + rowOffset; })
            .filter(function (v) { return v >= 0 && v < newRows; });
        var newData = __assign(__assign({}, oldData), { rows: newRows, columns: newCols, center: { x: newCenterX, y: newCenterY }, partColors: newPartColors, cutRows: newCutRows, cutColumns: newCutCols, topLinearSegments: newTopLinearSegments, leftLinearSegments: newLeftLinearSegments, dimensions: { width: newCols * WORKSPACE_CELL_SIZE, height: newRows * WORKSPACE_CELL_SIZE } });
        setManipulative(function (prev) {
            return prev.map(function (item) {
                return item.data.id === data.id && item.data.type === 'coloredTiles'
                    ? __assign(__assign({}, item), { data: newData }) : item;
            });
        });
        saveState({
            type: data.id,
            id: data.id.split('_')[1],
            propertyName: 'completeData',
            old: JSON.stringify(oldData),
            new: JSON.stringify(newData),
        });
        announce(t('text.coloredTilesGridResized', { rows: newRows, columns: newCols }));
    };
    // Note: the resize handle <div>s are no longer keyboard-focusable (tabIndex
    // -1 + aria-hidden). Keyboard resize happens via the context-menu items
    // dispatched through the coloredTilesResize listener above. The mouse
    // drag path still uses onMouseDown / onTouchStart on the handles.
    var addRow = function () {
        var _a, _b, _c, _d;
        if (isPreview)
            return;
        if (mode !== 'grid')
            return;
        var oldData = structuredClone(dataRef.current);
        var cellSize = WORKSPACE_CELL_SIZE;
        var newData = __assign(__assign({}, oldData), { rows: oldData.rows + 1, center: __assign(__assign({}, oldData.center), { y: oldData.center.y + cellSize / 2 }), dimensions: {
                width: oldData.columns * cellSize,
                height: (oldData.rows + 1) * cellSize,
            } });
        // DUPLICATE LAST ROW
        if (oldData.generationMode === 'manual' ||
            oldData.generationMode === 'random' ||
            oldData.generationMode === 'outline') {
            var lastRowIdx = oldData.rows - 1;
            var newRowIdx = oldData.rows;
            newData.partColors = __assign({}, (oldData.partColors || {}));
            for (var c = 0; c < oldData.columns; c++) {
                var color_1 = ((_a = oldData.partColors) === null || _a === void 0 ? void 0 : _a["".concat(lastRowIdx, "-").concat(c)]) ||
                    (oldData.generationMode === 'random' ? TILE_COLORS[(lastRowIdx * 2 + c) % 4] : undefined);
                if (color_1) {
                    newData.partColors["".concat(newRowIdx, "-").concat(c)] = color_1;
                }
            }
        }
        var tileScale = (_b = oldData.scale) !== null && _b !== void 0 ? _b : 1;
        var tileLeft = oldData.center.x - (oldData.columns * cellSize * tileScale) / 2;
        var tileTop = oldData.center.y - (oldData.rows * cellSize * tileScale) / 2;
        var oldVIds = (_c = oldData.snappedVerticalPieceIds) !== null && _c !== void 0 ? _c : Array(oldData.rows).fill(null);
        var oldRightVIds = (_d = oldData.snappedRightVerticalPieceIds) !== null && _d !== void 0 ? _d : Array(oldData.rows).fill(null);
        var hasSnappedVertical = oldVIds.some(function (id) { return id !== null; });
        var newPieceForRow = null;
        var newPieceId = null;
        if (hasSnappedVertical) {
            newPieceId = "linearPiece_".concat(uuidv4().replace(/-/g, ''));
            newPieceForRow = {
                type: 'linearPiece',
                widgetId: "linearPiece:".concat(uuidv4()),
                data: {
                    id: newPieceId,
                    type: 'linearPiece',
                    orientation: 'vertical',
                    center: {
                        x: tileLeft - 22 * tileScale,
                        y: tileTop + oldData.rows * cellSize * tileScale + (cellSize * tileScale) / 2,
                    },
                    scale: tileScale,
                    dimensions: { width: 10, height: 102 },
                    lock: { positioning: false, editing: false },
                },
            };
        }
        var updatedVIds = __spreadArray(__spreadArray([], oldVIds, true), [newPieceId], false);
        newData.snappedVerticalPieceIds = updatedVIds;
        // Keep right-side array length in sync with rows; new row defaults to no piece on right.
        newData.snappedRightVerticalPieceIds = __spreadArray(__spreadArray([], oldRightVIds, true), [null], false);
        updateArray(function () { return newData; });
        var tileStateEntry = {
            type: data.id,
            id: data.id.split('_')[1],
            propertyName: 'completeData',
            old: JSON.stringify(oldData),
            new: JSON.stringify(newData),
        };
        if (newPieceForRow) {
            setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev, true), [newPieceForRow], false); });
            saveState([
                tileStateEntry,
                {
                    type: newPieceId,
                    id: newPieceId.split('_')[1],
                    propertyName: 'create',
                    old: JSON.stringify(newPieceForRow),
                },
            ]);
        }
        else {
            saveState(tileStateEntry);
        }
        announce(t('text.coloredTilesGridResized', { rows: newData.rows, columns: newData.columns }));
    };
    var addColumn = function () {
        var _a, _b, _c, _d;
        if (isPreview)
            return;
        if (mode !== 'grid')
            return;
        var oldData = structuredClone(dataRef.current);
        var cellSize = WORKSPACE_CELL_SIZE;
        var newData = __assign(__assign({}, oldData), { columns: oldData.columns + 1, center: __assign(__assign({}, oldData.center), { x: oldData.center.x + cellSize / 2 }), dimensions: {
                width: (oldData.columns + 1) * cellSize,
                height: oldData.rows * cellSize,
            } });
        // DUPLICATE LAST COLUMN (Manual, Random, Outline Modes)
        if (oldData.generationMode === 'manual' ||
            oldData.generationMode === 'random' ||
            oldData.generationMode === 'outline') {
            var lastColIdx = oldData.columns - 1;
            var newColIdx = oldData.columns;
            newData.partColors = __assign({}, (oldData.partColors || {}));
            for (var r = 0; r < oldData.rows; r++) {
                var color_2 = ((_a = oldData.partColors) === null || _a === void 0 ? void 0 : _a["".concat(r, "-").concat(lastColIdx)]) ||
                    (oldData.generationMode === 'random' ? TILE_COLORS[(r * 2 + lastColIdx) % 4] : undefined);
                if (color_2) {
                    newData.partColors["".concat(r, "-").concat(newColIdx)] = color_2;
                }
            }
        }
        var tileScaleC = (_b = oldData.scale) !== null && _b !== void 0 ? _b : 1;
        var tileLeft = oldData.center.x - (oldData.columns * cellSize * tileScaleC) / 2;
        var tileTop = oldData.center.y - (oldData.rows * cellSize * tileScaleC) / 2;
        var oldHIds = (_c = oldData.snappedHorizontalPieceIds) !== null && _c !== void 0 ? _c : Array(oldData.columns).fill(null);
        var oldBottomHIds = (_d = oldData.snappedBottomHorizontalPieceIds) !== null && _d !== void 0 ? _d : Array(oldData.columns).fill(null);
        var hasSnappedHorizontal = oldHIds.some(function (id) { return id !== null; });
        var newPieceForCol = null;
        var newPieceColId = null;
        if (hasSnappedHorizontal) {
            newPieceColId = "linearPiece_".concat(uuidv4().replace(/-/g, ''));
            newPieceForCol = {
                type: 'linearPiece',
                widgetId: "linearPiece:".concat(uuidv4()),
                data: {
                    id: newPieceColId,
                    type: 'linearPiece',
                    orientation: 'horizontal',
                    center: {
                        x: tileLeft + oldData.columns * cellSize * tileScaleC + (cellSize * tileScaleC) / 2,
                        y: tileTop - 21 * tileScaleC,
                    },
                    scale: tileScaleC,
                    dimensions: { width: 102, height: 10 },
                    lock: { positioning: false, editing: false },
                },
            };
        }
        var updatedHIds = __spreadArray(__spreadArray([], oldHIds, true), [newPieceColId], false);
        newData.snappedHorizontalPieceIds = updatedHIds;
        // Keep bottom-side array length in sync with columns; new column defaults to no piece on bottom.
        newData.snappedBottomHorizontalPieceIds = __spreadArray(__spreadArray([], oldBottomHIds, true), [null], false);
        updateArray(function () { return newData; });
        var tileStateEntry = {
            type: data.id,
            id: data.id.split('_')[1],
            propertyName: 'completeData',
            old: JSON.stringify(oldData),
            new: JSON.stringify(newData),
        };
        if (newPieceForCol) {
            setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev, true), [newPieceForCol], false); });
            saveState([
                tileStateEntry,
                {
                    type: newPieceColId,
                    id: newPieceColId.split('_')[1],
                    propertyName: 'create',
                    old: JSON.stringify(newPieceForCol),
                },
            ]);
        }
        else {
            saveState(tileStateEntry);
        }
        announce(t('text.coloredTilesGridResized', { rows: newData.rows, columns: newData.columns }));
    };
    var removeRow = function () {
        var _a, _b, _c, _d;
        if (isPreview)
            return;
        if (mode !== 'grid')
            return;
        var oldData = structuredClone(dataRef.current);
        if (oldData.rows <= 1)
            return;
        var cellSize = WORKSPACE_CELL_SIZE;
        var newData = __assign(__assign({}, oldData), { rows: oldData.rows - 1, center: __assign(__assign({}, oldData.center), { y: oldData.center.y - cellSize / 2 }), dimensions: {
                width: oldData.columns * cellSize,
                height: (oldData.rows - 1) * cellSize,
            } });
        var removedRowIdx = oldData.rows - 1;
        // Prune partColors
        if (newData.partColors) {
            Object.keys(newData.partColors).forEach(function (key) {
                var r = key.split('-').map(Number)[0];
                if (r === removedRowIdx) {
                    delete newData.partColors[key];
                }
            });
        }
        // Prune leftLinearSegments
        if (newData.leftLinearSegments) {
            newData.leftLinearSegments = newData.leftLinearSegments.filter(function (s) { return s < removedRowIdx; });
        }
        // Update cutRows
        if (newData.cutRows) {
            newData.cutRows = newData.cutRows.filter(function (r) { return r < newData.rows; });
        }
        // Prune selectedPartKeys
        if (newData.selectedPartKeys) {
            newData.selectedPartKeys = newData.selectedPartKeys.filter(function (key) {
                if (key.startsWith('cell-')) {
                    var r = key.replace('cell-', '').split('-').map(Number)[0];
                    return r < newData.rows;
                }
                if (key.startsWith('red-left-')) {
                    var r = Number(key.replace('red-left-', ''));
                    return r < newData.rows;
                }
                return true;
            });
        }
        var oldVIdsR = (_a = oldData.snappedVerticalPieceIds) !== null && _a !== void 0 ? _a : [];
        var oldRightVIdsR = (_b = oldData.snappedRightVerticalPieceIds) !== null && _b !== void 0 ? _b : [];
        var leftPieceToDelete = (_c = oldVIdsR[removedRowIdx]) !== null && _c !== void 0 ? _c : null;
        var rightPieceToDelete = (_d = oldRightVIdsR[removedRowIdx]) !== null && _d !== void 0 ? _d : null;
        newData.snappedVerticalPieceIds = oldVIdsR.filter(function (_, i) { return i !== removedRowIdx; });
        newData.snappedRightVerticalPieceIds = oldRightVIdsR.filter(function (_, i) { return i !== removedRowIdx; });
        // Capture full piece manipulatives BEFORE we filter them out so the saveState
        // delete entries hold the data needed to restore them on undo.
        var fullLeftPieceToDelete = leftPieceToDelete
            ? manipulativeRef.current.find(function (m) { return m.data.id === leftPieceToDelete; })
            : undefined;
        var fullRightPieceToDelete = rightPieceToDelete
            ? manipulativeRef.current.find(function (m) { return m.data.id === rightPieceToDelete; })
            : undefined;
        updateArray(function () { return newData; });
        var idsToRemove = new Set();
        if (leftPieceToDelete)
            idsToRemove.add(leftPieceToDelete);
        if (rightPieceToDelete)
            idsToRemove.add(rightPieceToDelete);
        if (idsToRemove.size > 0) {
            setManipulative(function (prev) { return prev.filter(function (item) { return !idsToRemove.has(item.data.id); }); });
        }
        var tileStateSave = {
            type: data.id,
            id: data.id.split('_')[1],
            propertyName: 'completeData',
            old: JSON.stringify(oldData),
            new: JSON.stringify(newData),
        };
        var batch = [tileStateSave];
        if (leftPieceToDelete && fullLeftPieceToDelete) {
            batch.push({
                type: leftPieceToDelete,
                id: leftPieceToDelete.split('_')[1],
                propertyName: 'delete',
                old: JSON.stringify(fullLeftPieceToDelete),
            });
        }
        if (rightPieceToDelete && fullRightPieceToDelete) {
            batch.push({
                type: rightPieceToDelete,
                id: rightPieceToDelete.split('_')[1],
                propertyName: 'delete',
                old: JSON.stringify(fullRightPieceToDelete),
            });
        }
        saveState(batch.length > 1 ? batch : tileStateSave);
        announce(t('text.coloredTilesGridResized', { rows: newData.rows, columns: newData.columns }));
    };
    var removeColumn = function () {
        var _a, _b, _c, _d;
        if (isPreview)
            return;
        if (mode !== 'grid')
            return;
        var oldData = structuredClone(dataRef.current);
        if (oldData.columns <= 1)
            return;
        var cellSize = WORKSPACE_CELL_SIZE;
        var newData = __assign(__assign({}, oldData), { columns: oldData.columns - 1, center: __assign(__assign({}, oldData.center), { x: oldData.center.x - cellSize / 2 }), dimensions: {
                width: (oldData.columns - 1) * cellSize,
                height: oldData.rows * cellSize,
            } });
        var removedColIdx = oldData.columns - 1;
        // Prune partColors
        if (newData.partColors) {
            Object.keys(newData.partColors).forEach(function (key) {
                var _a = key.split('-').map(Number), c = _a[1];
                if (c === removedColIdx) {
                    delete newData.partColors[key];
                }
            });
        }
        // Prune topLinearSegments
        if (newData.topLinearSegments) {
            newData.topLinearSegments = newData.topLinearSegments.filter(function (s) { return s < removedColIdx; });
        }
        // Update cutColumns
        if (newData.cutColumns) {
            newData.cutColumns = newData.cutColumns.filter(function (c) { return c < newData.columns; });
        }
        // Prune selectedPartKeys
        if (newData.selectedPartKeys) {
            newData.selectedPartKeys = newData.selectedPartKeys.filter(function (key) {
                if (key.startsWith('cell-')) {
                    var _a = key.replace('cell-', '').split('-').map(Number), c = _a[1];
                    return c < newData.columns;
                }
                if (key.startsWith('red-top-')) {
                    var c = Number(key.replace('red-top-', ''));
                    return c < newData.columns;
                }
                return true;
            });
        }
        var oldHIdsC = (_a = oldData.snappedHorizontalPieceIds) !== null && _a !== void 0 ? _a : [];
        var oldBottomHIdsC = (_b = oldData.snappedBottomHorizontalPieceIds) !== null && _b !== void 0 ? _b : [];
        var topPieceToDelete = (_c = oldHIdsC[removedColIdx]) !== null && _c !== void 0 ? _c : null;
        var bottomPieceToDelete = (_d = oldBottomHIdsC[removedColIdx]) !== null && _d !== void 0 ? _d : null;
        newData.snappedHorizontalPieceIds = oldHIdsC.filter(function (_, i) { return i !== removedColIdx; });
        newData.snappedBottomHorizontalPieceIds = oldBottomHIdsC.filter(function (_, i) { return i !== removedColIdx; });
        var fullTopPieceToDelete = topPieceToDelete
            ? manipulativeRef.current.find(function (m) { return m.data.id === topPieceToDelete; })
            : undefined;
        var fullBottomPieceToDelete = bottomPieceToDelete
            ? manipulativeRef.current.find(function (m) { return m.data.id === bottomPieceToDelete; })
            : undefined;
        updateArray(function () { return newData; });
        var idsToRemoveC = new Set();
        if (topPieceToDelete)
            idsToRemoveC.add(topPieceToDelete);
        if (bottomPieceToDelete)
            idsToRemoveC.add(bottomPieceToDelete);
        if (idsToRemoveC.size > 0) {
            setManipulative(function (prev) { return prev.filter(function (item) { return !idsToRemoveC.has(item.data.id); }); });
        }
        var tileStateSaveC = {
            type: data.id,
            id: data.id.split('_')[1],
            propertyName: 'completeData',
            old: JSON.stringify(oldData),
            new: JSON.stringify(newData),
        };
        var batchC = [tileStateSaveC];
        if (topPieceToDelete && fullTopPieceToDelete) {
            batchC.push({
                type: topPieceToDelete,
                id: topPieceToDelete.split('_')[1],
                propertyName: 'delete',
                old: JSON.stringify(fullTopPieceToDelete),
            });
        }
        if (bottomPieceToDelete && fullBottomPieceToDelete) {
            batchC.push({
                type: bottomPieceToDelete,
                id: bottomPieceToDelete.split('_')[1],
                propertyName: 'delete',
                old: JSON.stringify(fullBottomPieceToDelete),
            });
        }
        saveState(batchC.length > 1 ? batchC : tileStateSaveC);
        announce(t('text.coloredTilesGridResized', { rows: newData.rows, columns: newData.columns }));
    };
    var toggleExpression = function () {
        var _a;
        if (isPreview)
            return;
        var oldData = structuredClone(dataRef.current);
        var newData = __assign(__assign({}, oldData), { showExpression: !((_a = oldData.showExpression) !== null && _a !== void 0 ? _a : true) });
        updateArray(function (d) { var _a; return (__assign(__assign({}, d), { showExpression: !((_a = d.showExpression) !== null && _a !== void 0 ? _a : true) })); });
        saveState({
            type: data.id,
            id: data.id.split('_')[1],
            propertyName: 'completeData',
            old: JSON.stringify(oldData),
            new: JSON.stringify(newData),
        });
    };
    var toggleProduct = function () {
        var _a;
        if (isPreview)
            return;
        var oldData = structuredClone(dataRef.current);
        var newData = __assign(__assign({}, oldData), { showProduct: !((_a = oldData.showProduct) !== null && _a !== void 0 ? _a : true) });
        updateArray(function (d) { var _a; return (__assign(__assign({}, d), { showProduct: !((_a = d.showProduct) !== null && _a !== void 0 ? _a : true) })); });
        saveState({
            type: data.id,
            id: data.id.split('_')[1],
            propertyName: 'completeData',
            old: JSON.stringify(oldData),
            new: JSON.stringify(newData),
        });
    };
    var toggleDimensionLabels = function () {
        if (isPreview)
            return;
        var oldData = structuredClone(dataRef.current);
        var newData = __assign(__assign({}, oldData), { dimensionLabels: !oldData.dimensionLabels });
        updateArray(function (d) { return (__assign(__assign({}, d), { dimensionLabels: !d.dimensionLabels })); });
        saveState({
            type: data.id,
            id: data.id.split('_')[1],
            propertyName: 'completeData',
            old: JSON.stringify(oldData),
            new: JSON.stringify(newData),
        });
    };
    var openRowCutsCount = ((_m = (_l = data.openCuts) === null || _l === void 0 ? void 0 : _l.rows) !== null && _m !== void 0 ? _m : []).length;
    var openColCutsCount = ((_p = (_o = data.openCuts) === null || _o === void 0 ? void 0 : _o.cols) !== null && _p !== void 0 ? _p : []).length;
    var rowCutsMaxed = mode === 'grid' ? cutRows.length >= data.rows - 1 : mode === 'open' && openRowCutsCount >= 4;
    var colCutsMaxed = mode === 'grid'
        ? cutColumns.length >= data.columns - 1
        : mode === 'open' && openColCutsCount >= 4;
    // Disabled states for the cut move panel buttons.
    // kbCutMode position ranges from 1 to limit-1 (grid-only — startKbCut guards mode!=='grid').
    var cutPanelDisabled = (function () {
        if (!kbCutMode)
            return { back: true, fwd: true, confirm: true };
        var isRow = kbCutMode.type === 'row';
        var limit = isRow ? data.rows : data.columns;
        var existing = isRow ? cutRows : cutColumns;
        var pos = kbCutMode.position;
        var canBack = false;
        for (var i = pos - 1; i >= 1; i--) {
            if (!existing.includes(i)) {
                canBack = true;
                break;
            }
        }
        var canFwd = false;
        for (var i = pos + 1; i < limit; i++) {
            if (!existing.includes(i)) {
                canFwd = true;
                break;
            }
        }
        return {
            back: !canBack,
            fwd: !canFwd,
            confirm: existing.includes(pos),
        };
    })();
    var handleCellDragStart = function (e, r, c, color) {
        var _a, _b, _c, _d;
        if (isPreview ||
            data.isGroup === true ||
            isGroup ||
            (data.rows === 1 && data.columns === 1) ||
            ((_a = data.lock) === null || _a === void 0 ? void 0 : _a.editing) ||
            ((_b = data.lock) === null || _b === void 0 ? void 0 : _b.positioning))
            return;
        // Cell handler stops propagation, so the parent grid's onGridMouseDown never fires.
        // Always run tile selection through selectTool here — the helper handles
        // already-selected (no-op via allowRemove=false), single-click transfer from
        // another manipulative, and modifier-key multi-select. Gating on
        // selectedTool.length === 0 used to block selection-transfer from e.g. a
        // selected linear piece to a tile cell.
        var selectId = (_c = data.groupId) !== null && _c !== void 0 ? _c : data.id;
        setSelectedTool(selectTool(e, selectId, selectedTool, false));
        setSelectedManipulativeId(data.id);
        e.stopPropagation();
        // Prevent dragging empty cells in outline mode
        if (data.generationMode === 'outline' && !color)
            return;
        var dragStartFullData = structuredClone(dataRef.current);
        var cellKey = "".concat(r, "-").concat(c);
        var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        var ghost = null;
        var canvasGhost = null;
        var hasMovedLocal = false;
        // Cell-extraction only moves THIS cell's ghost — it never touches data.center, so
        // other selected items never move on their own. Broadcast the drag delta so the
        // rest of the multi-selection follows along (via the same multiSelectMove event the
        // tile-level and other widgets' drags already use), while this cell keeps
        // extracting/merging independently as before.
        var isMultiSelectCellDrag = selectedTool.length > 1 && selectedTool.includes(data.id);
        // Followers only get a DOM-only visual nudge during the drag (see the shared
        // multiSelectMove listener) — their data.center never updates on its own. Mirror the
        // tile-level group-drag pattern (onGridMouseDown/onUp above) by capturing every OTHER
        // affected item's start position now, then committing their final centers ourselves
        // on drop — otherwise the followers snap back to their old position on the next
        // re-render since nothing ever wrote their new data.center.
        var cellDragFollowerStarts = isMultiSelectCellDrag
            ? manipulative
                .filter(function (item) {
                var _a;
                if (!item.data.id || item.data.id === data.id)
                    return false;
                if ((_a = item.data.lock) === null || _a === void 0 ? void 0 : _a.positioning)
                    return false;
                var sameGroup = !!data.groupId && item.data.groupId === data.groupId;
                var inAdHocSelection = (item.data.type === 'coloredTiles' || item.data.type === 'linearPiece') &&
                    selectedTool.includes(item.data.id);
                return sameGroup || inAdHocSelection;
            })
                .map(function (item) { return ({ id: item.data.id, center: __assign({}, item.data.center) }); })
            : [];
        // The dragged cell belongs to this tile, so its visual size is cellSize * tile scale.
        // All snap-detection coords below must use this scaled cell — otherwise a scaled
        // tile (e.g. 0.6) computes the wrong target row/col (lands on the wrong cell).
        var dragScale = (_d = dataRef.current.scale) !== null && _d !== void 0 ? _d : 1;
        var scaledCell = cellSize * dragScale;
        var scaledCellSize = scaledCell * zoomLevel;
        var halfScaledCellSize = scaledCellSize / 2;
        var canvasContainer = document.querySelector('.whiteboardScrollbar .customScrollbarContent');
        var onMove = function (moveEvent) {
            if ('touches' in moveEvent)
                moveEvent.preventDefault();
            var moveX = 'clientX' in moveEvent ? moveEvent.clientX : moveEvent.touches[0].clientX;
            var moveY = 'clientY' in moveEvent ? moveEvent.clientY : moveEvent.touches[0].clientY;
            var dx = moveX - clientX;
            var dy = moveY - clientY;
            if (!hasMovedLocal && Math.sqrt(dx * dx + dy * dy) > 5) {
                hasMovedLocal = true;
                // Perform extraction only after threshold
                updateArray(function (prev) {
                    var _a;
                    var newPartColors = __assign({}, prev.partColors);
                    if (data.generationMode === 'random') {
                        newPartColors[cellKey] = 'transparent';
                    }
                    else {
                        delete newPartColors[cellKey];
                    }
                    var newSelectedPartKeys = (_a = prev.selectedPartKeys) !== null && _a !== void 0 ? _a : [];
                    newSelectedPartKeys = newSelectedPartKeys.filter(function (k) { return k !== "cell-".concat(r, "-").concat(c); });
                    return __assign(__assign({}, prev), { partColors: newPartColors, selectedPartKeys: newSelectedPartKeys });
                });
                ghost = document.createElement('div');
                ghost.className = 'block-ghost';
                ghost.style.backgroundColor = color;
                ghost.style.width = "".concat(scaledCellSize, "px");
                ghost.style.height = "".concat(scaledCellSize, "px");
                ghost.style.border = '1px solid white';
                ghost.style.position = 'fixed';
                ghost.style.zIndex = '9999';
                ghost.style.pointerEvents = 'none';
                document.body.appendChild(ghost);
                customEventHandler.dispatchEvent('blocksComponent', { id: 'blockDragStarted' });
                canvasGhost = document.createElement('div');
                canvasGhost.className = 'block-canvas-ghost';
                canvasGhost.style.display = 'none';
                canvasGhost.style.width = "".concat(scaledCellSize, "px");
                canvasGhost.style.height = "".concat(scaledCellSize, "px");
                if (canvasContainer) {
                    canvasContainer.appendChild(canvasGhost);
                }
                if (isMultiSelectCellDrag) {
                    customEventHandler.dispatchEvent('multiSelectMoveStart', { sourceId: data.id });
                }
            }
            if (!hasMovedLocal)
                return;
            if (isMultiSelectCellDrag) {
                customEventHandler.dispatchEvent('multiSelectMove', {
                    sourceId: data.id,
                    deltaX: dx / zoomLevel,
                    deltaY: dy / zoomLevel,
                });
            }
            if (ghost) {
                ghost.style.left = "".concat(moveX - halfScaledCellSize, "px");
                ghost.style.top = "".concat(moveY - halfScaledCellSize, "px");
            }
            if (canvasContainer && canvasGhost) {
                var rect = canvasContainer.getBoundingClientRect();
                if (moveX >= rect.left &&
                    moveX <= rect.right &&
                    moveY >= rect.top &&
                    moveY <= rect.bottom) {
                    var px = (moveX - rect.left + canvasContainer.scrollLeft) / zoomLevel;
                    var py = (moveY - rect.top + canvasContainer.scrollTop) / zoomLevel;
                    var rawDropX = px - scaledCell / 2;
                    var rawDropY = py - scaledCell / 2;
                    var snapResult = detectManualTileSnap({ x: rawDropX, y: rawDropY }, { width: scaledCell, height: scaledCell }, manipulative, undefined, 40, cellSize, dataRef.current.generationMode, dragScale);
                    var snapX = snapResult.x !== rawDropX || snapResult.y !== rawDropY
                        ? snapResult.x
                        : Math.round(rawDropX / scaledCell) * scaledCell;
                    var snapY = snapResult.x !== rawDropX || snapResult.y !== rawDropY
                        ? snapResult.y
                        : Math.round(rawDropY / scaledCell) * scaledCell;
                    canvasGhost.style.left = "".concat(snapX, "px");
                    canvasGhost.style.top = "".concat(snapY, "px");
                    canvasGhost.style.display = 'block';
                    customEventHandler.dispatchEvent('blocksComponent', {
                        id: 'blockDragging',
                        data: { x: snapX, y: snapY },
                        sourceId: dataRef.current.id,
                    });
                }
                else {
                    canvasGhost.style.display = 'none';
                }
            }
        };
        var onUp = function (upEvent) {
            var _a, _b, _c;
            var upX = 'clientX' in upEvent ? upEvent.clientX : upEvent.changedTouches[0].clientX;
            var upY = 'clientY' in upEvent ? upEvent.clientY : upEvent.changedTouches[0].clientY;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
            if (ghost === null || ghost === void 0 ? void 0 : ghost.parentNode)
                ghost.parentNode.removeChild(ghost);
            if (canvasGhost === null || canvasGhost === void 0 ? void 0 : canvasGhost.parentNode)
                canvasGhost.parentNode.removeChild(canvasGhost);
            if (!hasMovedLocal)
                return;
            if (isMultiSelectCellDrag && cellDragFollowerStarts.length > 0) {
                var finalDx_1 = (upX - clientX) / zoomLevel;
                var finalDy_1 = (upY - clientY) / zoomLevel;
                var startById_2 = new Map(cellDragFollowerStarts.map(function (s) { return [s.id, s.center]; }));
                setManipulative(function (prev) {
                    return prev.map(function (item) {
                        var start = item.data.id ? startById_2.get(item.data.id) : undefined;
                        if (!start)
                            return item;
                        return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: start.x + finalDx_1, y: start.y + finalDy_1 } }) });
                    });
                });
                var cellDragFollowerEntries = cellDragFollowerStarts.map(function (s) { return ({
                    type: s.id,
                    id: s.id.split('_')[1],
                    propertyName: 'center',
                    old: JSON.stringify(s.center),
                    new: JSON.stringify({ x: s.center.x + finalDx_1, y: s.center.y + finalDy_1 }),
                }); });
                if (!registerMultiSelectSaveEntries(data.id, cellDragFollowerEntries)) {
                    saveState(cellDragFollowerEntries);
                }
            }
            if (isMultiSelectCellDrag) {
                customEventHandler.dispatchEvent('multiSelectMoveEnd', { sourceId: data.id });
            }
            customEventHandler.dispatchEvent('blocksComponent', { id: 'blockDragEnded' });
            if (canvasContainer) {
                var rect = canvasContainer.getBoundingClientRect();
                if (upX >= rect.left && upX <= rect.right && upY >= rect.top && upY <= rect.bottom) {
                    var px = (upX - rect.left + canvasContainer.scrollLeft) / zoomLevel;
                    var py = (upY - rect.top + canvasContainer.scrollTop) / zoomLevel;
                    var rawDropX = px - scaledCell / 2;
                    var rawDropY = py - scaledCell / 2;
                    // If dropping INSIDE an existing tile cell that is already occupied, cancel the drop and
                    // restore the extracted cell to its original position.
                    var snapTarget = findSnapTargetManualTile({ x: rawDropX, y: rawDropY }, { width: scaledCell, height: scaledCell }, manipulative, undefined, 40, cellSize, dataRef.current.generationMode, dragScale);
                    var shouldExtract = false;
                    if (snapTarget) {
                        var _d = snapTarget, targetItem = _d.item, mergeDir = _d.dir, targetRow = _d.row, targetCol = _d.col;
                        // NOTE: previously a guard here reverted the extraction when the
                        // drop snapped to the SAME tile's outer edge (LEFT/RIGHT/TOP/BOTTOM),
                        // intended to cancel "no-op" extractions. That guard also blocked
                        // the legitimate case of extracting a cell and dropping at the
                        // tile's own edge to extend its shape (the "extreme left of L"
                        // flow that worked before 2026-04-30). Removed so EDGE drops on
                        // own-tile edges are honored as extensions.
                        if (mergeDir === 'INSIDE') {
                            var targetData = targetItem.data;
                            var existingColor = (_a = targetData.partColors) === null || _a === void 0 ? void 0 : _a["".concat(targetRow, "-").concat(targetCol)];
                            var isOccupied = (targetData.generationMode === 'random' && existingColor !== 'transparent') ||
                                ((targetData.generationMode === 'manual' ||
                                    targetData.generationMode === 'outline') &&
                                    !!existingColor);
                            if (isOccupied) {
                                // Revert extraction if dropped on occupied cell
                                updateArray(function () { return dragStartFullData; });
                                return;
                            }
                            shouldExtract = true;
                        }
                        else {
                            // Edge snapping (LEFT, RIGHT, TOP, BOTTOM)
                            shouldExtract = true;
                        }
                    }
                    else {
                        // No snap target — check if it's a "clean" drop on the whiteboard or something else
                        // If it's too close to a grid but not snapped, we might want to revert.
                        // But detectManualTileSnap already returns a snapped-to-grid position if no tile snap.
                        // For now, let's allow "free" drops but revert if it's "neither on edge nor inside valid".
                        // Implementation of "return to previous position" on invalid drop:
                        var snapToGrid = detectManualTileSnap({ x: rawDropX, y: rawDropY }, { width: scaledCell, height: scaledCell }, manipulative, undefined, 40, cellSize, dataRef.current.generationMode, dragScale);
                        shouldExtract = true;
                    }
                    if (!shouldExtract) {
                        updateArray(function () { return dragStartFullData; });
                        return;
                    }
                    var snapResult = detectManualTileSnap({ x: rawDropX, y: rawDropY }, { width: scaledCell, height: scaledCell }, manipulative, undefined, 40, cellSize, dataRef.current.generationMode, dragScale);
                    // Save the data to remove the cell permanently from the source grid
                    var oldDataClone = structuredClone(dragStartFullData);
                    var newDataClone = structuredClone(dragStartFullData);
                    if (!newDataClone.partColors)
                        newDataClone.partColors = {};
                    if (newDataClone.generationMode === 'random') {
                        newDataClone.partColors[cellKey] = 'transparent';
                    }
                    else {
                        delete newDataClone.partColors[cellKey];
                    }
                    if (newDataClone.selectedPartKeys) {
                        newDataClone.selectedPartKeys = newDataClone.selectedPartKeys.filter(function (k) { return k !== "cell-".concat(r, "-").concat(c); });
                    }
                    var finalDataClone_1 = newDataClone;
                    if (data.generationMode === 'random') {
                        finalDataClone_1 = trimRandomEmptyEdgeRowsAndCols(newDataClone, cellSize);
                    }
                    else if (data.generationMode === 'manual') {
                        finalDataClone_1 = trimManualEmptyEdgeRowsAndCols(newDataClone, cellSize);
                    }
                    var isEmpty = data.generationMode === 'random'
                        ? Object.values(finalDataClone_1.partColors || {}).every(function (c) { return c === 'transparent'; })
                        : Object.keys(finalDataClone_1.partColors || {}).length === 0;
                    if (isEmpty && data.generationMode !== 'random' && data.generationMode !== 'outline') {
                        setManipulative(function (prev) { return prev.filter(function (item) { return item.data.id !== data.id; }); });
                        saveState({
                            type: data.id,
                            id: data.id.split('_')[1],
                            propertyName: 'delete',
                            old: JSON.stringify(oldDataClone),
                            new: undefined,
                        });
                    }
                    else {
                        updateArray(function () { return finalDataClone_1; });
                        saveState({
                            type: data.id,
                            id: data.id.split('_')[1],
                            propertyName: 'completeData',
                            old: JSON.stringify(oldDataClone),
                            new: JSON.stringify(finalDataClone_1),
                        });
                    }
                    // Dispatch creation of cell at new location.
                    // Pass `sourceLatestData` so the handler reads the post-trim bounds for snap
                    // detection — manipulativeRef there is stale (React hasn't committed the
                    // updateArray() above yet), and using the un-trimmed bounds caused the
                    // "magnet" effect where drops > threshold from the visible source edge still
                    // snapped because the stale bounds were larger.
                    customEventHandler.dispatchEvent('bottomPanel', {
                        id: 'createColoredTile',
                        data: {
                            color: color,
                            x: snapResult.x,
                            y: snapResult.y,
                            scale: dragScale,
                            sourceId: data.id,
                            sourceLatestData: finalDataClone_1,
                            topLinearSegments: ((_b = dataRef.current.topLinearSegments) === null || _b === void 0 ? void 0 : _b.includes(c)) ? [0] : [],
                            leftLinearSegments: ((_c = dataRef.current.leftLinearSegments) === null || _c === void 0 ? void 0 : _c.includes(r)) ? [0] : [],
                        },
                    });
                }
                else {
                    // Revert extraction if dropped outside container
                    updateArray(function () { return dragStartFullData; });
                }
            }
            else {
                // Revert extraction
                updateArray(function () { return dragStartFullData; });
            }
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove, { passive: false });
        window.addEventListener('touchend', onUp);
    };
    /* ================= ARIA CELL LABEL HELPER ================= */
    var getCellAriaLabel = useCallback(function (r, c) {
        var _a, _b;
        var rawColor = (_b = (_a = data.partColors) === null || _a === void 0 ? void 0 : _a["".concat(r, "-").concat(c)]) !== null && _b !== void 0 ? _b : (data.generationMode === 'random' ? TILE_COLORS[(r * 2 + c) % 4] : '');
        // Resolve the colour name for ANY palette colour (the colour picker offers
        // 11, not just the 4 block colours) — case-insensitive hex match against
        // ColorEnum, then the shared `text.color_<KEY>` name. Falls back to "empty"
        // for transparent / uncoloured cells. Without the full map, cells painted
        // brown/orange/purple/etc. were read as "empty" (blank) during navigation.
        var norm = String(rawColor).toLowerCase();
        var colorKey = Object.keys(ColorEnum).find(function (k) { return ColorEnum[k].toLowerCase() === norm; });
        if (colorKey) {
            return t('text.coloredTilesCellColored', {
                row: r + 1,
                col: c + 1,
                color: t("text.color_".concat(colorKey)),
            });
        }
        return t('text.coloredTilesCellEmpty', { row: r + 1, col: c + 1 });
    }, [data.partColors, data.generationMode]);
    /* ================= P4: CELL KEYBOARD NAVIGATION (select mode only) ================= */
    // aria-activedescendant pattern: grid container holds DOM focus, arrow keys update
    // rovingCell (which drives aria-activedescendant). VO/JAWS read the pointed cell.
    // This means VO cursor and DOM focus stay in sync without roving tabindex on cells.
    var handleSelectGridKeyDown = useCallback(function (e) {
        var _a, _b;
        if (!selectModeEnabled || isPreview || ((_a = data.lock) === null || _a === void 0 ? void 0 : _a.editing))
            return;
        if (!rovingCell)
            return;
        var rows = dataRef.current.rows;
        var cols = dataRef.current.columns;
        var r = rovingCell.r, c = rovingCell.c;
        var moveTo = function (nr, nc) {
            setRovingCell({
                r: Math.max(0, Math.min(rows - 1, nr)),
                c: Math.max(0, Math.min(cols - 1, nc)),
            });
        };
        switch (e.key) {
            case 'ArrowRight':
                e.preventDefault();
                moveTo(r, c + 1);
                break;
            case 'ArrowLeft':
                e.preventDefault();
                moveTo(r, c - 1);
                break;
            case 'ArrowDown':
                e.preventDefault();
                moveTo(r + 1, c);
                break;
            case 'ArrowUp':
                e.preventDefault();
                moveTo(r - 1, c);
                break;
            case 'Home':
                e.preventDefault();
                if (e.ctrlKey)
                    moveTo(0, 0);
                else
                    moveTo(r, 0);
                break;
            case 'End':
                e.preventDefault();
                if (e.ctrlKey)
                    moveTo(rows - 1, cols - 1);
                else
                    moveTo(r, cols - 1);
                break;
            case ' ':
            case 'Enter': {
                e.preventDefault();
                var cellKey = "cell-".concat(r, "-").concat(c);
                var isNowSelected = !selectedPartKeys.includes(cellKey);
                toggleSelectedPartKey(cellKey);
                announce(t(isNowSelected
                    ? 'text.coloredTilesCellSelected'
                    : 'text.coloredTilesCellDeselected', { row: r + 1, col: c + 1 }));
                break;
            }
            case 'Escape':
                e.preventDefault();
                setRovingCell(null);
                (_b = controllerBtnRef.current) === null || _b === void 0 ? void 0 : _b.focus();
                break;
            default:
                break;
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectModeEnabled, isPreview, (_q = data.lock) === null || _q === void 0 ? void 0 : _q.editing, rovingCell, selectedPartKeys, toggleSelectedPartKey, t]);
    // Keep cell-level handler as fallback for when a cell itself receives DOM focus
    // (e.g. programmatic focus or non-VO keyboard users who Tab into a cell).
    var handleCellKeyDown = useCallback(function (e, r, c) {
        var _a, _b;
        if (!selectModeEnabled || isPreview || ((_a = data.lock) === null || _a === void 0 ? void 0 : _a.editing))
            return;
        // Delegate to the grid-level handler using a synthetic event shape.
        // Directly replicate the switch so we don't need to synthesize a React event.
        var rows = dataRef.current.rows;
        var cols = dataRef.current.columns;
        var moveTo = function (nr, nc) {
            setRovingCell({
                r: Math.max(0, Math.min(rows - 1, nr)),
                c: Math.max(0, Math.min(cols - 1, nc)),
            });
        };
        switch (e.key) {
            case 'ArrowRight':
                e.preventDefault();
                moveTo(r, c + 1);
                break;
            case 'ArrowLeft':
                e.preventDefault();
                moveTo(r, c - 1);
                break;
            case 'ArrowDown':
                e.preventDefault();
                moveTo(r + 1, c);
                break;
            case 'ArrowUp':
                e.preventDefault();
                moveTo(r - 1, c);
                break;
            case 'Home':
                e.preventDefault();
                if (e.ctrlKey)
                    moveTo(0, 0);
                else
                    moveTo(r, 0);
                break;
            case 'End':
                e.preventDefault();
                if (e.ctrlKey)
                    moveTo(rows - 1, cols - 1);
                else
                    moveTo(r, cols - 1);
                break;
            case ' ':
            case 'Enter': {
                e.preventDefault();
                var cellKey = "cell-".concat(r, "-").concat(c);
                var isNowSelected = !selectedPartKeys.includes(cellKey);
                toggleSelectedPartKey(cellKey);
                announce(t(isNowSelected
                    ? 'text.coloredTilesCellSelected'
                    : 'text.coloredTilesCellDeselected', { row: r + 1, col: c + 1 }));
                break;
            }
            case 'Escape':
                e.preventDefault();
                setRovingCell(null);
                (_b = controllerBtnRef.current) === null || _b === void 0 ? void 0 : _b.focus();
                break;
            default:
                break;
        }
    }, [selectModeEnabled, isPreview, (_r = data.lock) === null || _r === void 0 ? void 0 : _r.editing, selectedPartKeys, toggleSelectedPartKey]);
    /* ================= KEYBOARD CONTROLLER ================= */
    var handleControllerKeyDown = function (e) {
        var _a, _b, _c;
        if (isGroup || panningEnable)
            return;
        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
            e.preventDefault();
            e.stopPropagation();
            if ((_a = data.lock) === null || _a === void 0 ? void 0 : _a.positioning)
                return;
            var step = (e.shiftKey ? 20 : 5) / zoomLevel;
            var delta = {
                ArrowUp: { x: 0, y: -step },
                ArrowDown: { x: 0, y: step },
                ArrowLeft: { x: -step, y: 0 },
                ArrowRight: { x: step, y: 0 },
            };
            var d = delta[e.key];
            var groupId = dataRef.current.groupId;
            var curPos = (_b = keyMoveCurPosRef.current) !== null && _b !== void 0 ? _b : dataRef.current.center;
            if (!keyMoveStartRef.current) {
                keyMoveStartRef.current = __assign({}, curPos);
                // First arrow press of this session: tell group followers to capture
                // their start positions so they can mirror the leader's delta live.
                if (groupId) {
                    customEventHandler.dispatchEvent('multiSelectMoveStart', { sourceId: data.id });
                }
            }
            var newPos = { x: curPos.x + d.x, y: curPos.y + d.y };
            keyMoveCurPosRef.current = newPos;
            if (wrapperRef.current) {
                wrapperRef.current.style.left = "".concat(newPos.x, "px");
                wrapperRef.current.style.top = "".concat(newPos.y, "px");
            }
            // Group move: broadcast the cumulative delta so followers DOM-move in step.
            // Reuses the same listener the mouse group-drag uses (moves on `sameGroup`).
            if (groupId) {
                customEventHandler.dispatchEvent('multiSelectMove', {
                    deltaX: newPos.x - keyMoveStartRef.current.x,
                    deltaY: newPos.y - keyMoveStartRef.current.y,
                    sourceId: data.id,
                    groupId: groupId,
                });
            }
            if (keyMoveSaveTimer.current)
                clearTimeout(keyMoveSaveTimer.current);
            keyMoveSaveTimer.current = setTimeout(function () {
                if (keyMoveStartRef.current && keyMoveCurPosRef.current) {
                    var oldPos = keyMoveStartRef.current;
                    var newP_1 = keyMoveCurPosRef.current;
                    if (newP_1.x !== oldPos.x || newP_1.y !== oldPos.y) {
                        var moveGroupId_1 = dataRef.current.groupId;
                        if (moveGroupId_1) {
                            // Commit every group member (tiles + linearPieces) by the leader's
                            // total delta, in one batched saveState. Skip locked members.
                            var selfDeltaX_2 = newP_1.x - oldPos.x;
                            var selfDeltaY_2 = newP_1.y - oldPos.y;
                            var members = manipulativeRef.current.filter(function (m) {
                                var _a;
                                return m.data.groupId === moveGroupId_1 &&
                                    !((_a = m.data.lock) === null || _a === void 0 ? void 0 : _a.positioning);
                            });
                            var batchSave = members.map(function (m) {
                                var c = m.data.center;
                                return {
                                    type: m.data.id,
                                    id: m.data.id.split('_')[1],
                                    propertyName: 'center',
                                    old: JSON.stringify(c),
                                    new: JSON.stringify({ x: c.x + selfDeltaX_2, y: c.y + selfDeltaY_2 }),
                                };
                            });
                            var memberIds_1 = new Set(members.map(function (m) { return m.data.id; }));
                            setManipulative(function (prev) {
                                return prev.map(function (item) {
                                    if (!memberIds_1.has(item.data.id))
                                        return item;
                                    var c = item.data.center;
                                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: c.x + selfDeltaX_2, y: c.y + selfDeltaY_2 } }) });
                                });
                            });
                            if (batchSave.length > 0 &&
                                !registerMultiSelectSaveEntries(data.id, batchSave)) {
                                saveState(batchSave);
                            }
                            customEventHandler.dispatchEvent('multiSelectMoveEnd', { sourceId: data.id });
                        }
                        else {
                            saveState({
                                type: dataRef.current.id,
                                id: dataRef.current.id.split('_')[1],
                                propertyName: 'center',
                                old: JSON.stringify(oldPos),
                                new: JSON.stringify(newP_1),
                            });
                            updateArray(function (prev) { return (__assign(__assign({}, prev), { center: newP_1 })); });
                        }
                    }
                    keyMoveStartRef.current = null;
                    keyMoveCurPosRef.current = null;
                }
            }, 500);
            return;
        }
        if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
            e.preventDefault();
            e.stopPropagation();
            if (!selectedTool.includes(data.id))
                setSelectedTool([data.id]);
            var rect = (_c = controllerBtnRef.current) === null || _c === void 0 ? void 0 : _c.getBoundingClientRect();
            if (rect)
                setContextMenu({ x: rect.right, y: rect.top, manipulativeId: data.id });
        }
    };
    /* ================= RENDER ================= */
    if (data.isInvisible)
        return null;
    return (_jsxs("div", __assign({ ref: wrapperRef, className: "coloredTilesWrapper".concat(isPreview ? ' isPreview' : ''), "data-manipulative-id": data.id }, (data.groupId ? { 'data-group-id': data.groupId } : {}), { "data-generation-mode": data.generationMode, "aria-hidden": isPreview ? true : undefined, onFocus: function (e) {
            var _a, _b;
            try {
                lastFocusWasScreenReader.current = !!((_b = (_a = e.target).matches) === null || _b === void 0 ? void 0 : _b.call(_a, ':focus-visible'));
            }
            catch (_c) {
                lastFocusWasScreenReader.current = false;
            }
            // Drive the focus highlight: when any descendant (controller button,
            // cell, cut arrow, resize handle) receives focus via Tab, mark this
            // tile selected so the boxShadow ring appears.
            // When grouped, the group overlay owns selection — never reassign to the
            // individual tile id (that would deselect the group and break scale).
            if (isPreview || isGroup || panningEnable || data.groupId)
                return;
            if (!selectedTool.includes(data.id))
                setSelectedTool([data.id]);
        }, onBlur: function () {
            lastFocusWasScreenReader.current = false;
        }, onKeyDown: function (e) {
            var _a, _b;
            // Catches Shift+F10 / ContextMenu from any focusable descendant
            // (cells, cut arrows, resize handles) whose own handlers don't
            // process it. Controller button handles it inline above.
            if (panningEnable || isGroup)
                return;
            if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
                e.preventDefault();
                e.stopPropagation();
                if (!selectedTool.includes(data.id))
                    setSelectedTool([data.id]);
                var active = document.activeElement;
                var anchorEl = active && ((_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.contains(active)) && active !== wrapperRef.current
                    ? active
                    : (_b = controllerBtnRef.current) !== null && _b !== void 0 ? _b : wrapperRef.current;
                var rect = anchorEl === null || anchorEl === void 0 ? void 0 : anchorEl.getBoundingClientRect();
                if (rect)
                    setContextMenu({ x: rect.right, y: rect.top, manipulativeId: data.id });
            }
        }, style: __assign(__assign({ position: isPreview ? 'relative' : 'absolute', left: isPreview ? undefined : data.center.x, top: isPreview ? undefined : data.center.y }, (isPreview ? {} : { transform: "translate(-50%, -50%) scale(".concat(scale, ")") })), { boxShadow: !isPreview &&
                !data.groupId &&
                isFocused &&
                (data.generationMode === 'random' || data.generationMode === 'outline')
                ? '0 0 20px 0 #5063AE'
                : 'none' }), children: [_jsx(ButtonComponent, { reference: controllerBtnRef, avoidDefaultStyle: true, defaultCSS: "coloredTilesController", isParentRelative: true, noAudio: true, tabIndex: isGroup || isPreview || data.groupId ? -1 : 0, ariaHidden: isGroup || isPreview || data.groupId ? true : undefined, ariaLabelTranslated: effectiveControllerLabel, clickHandler: function (e) {
                    var _a;
                    if (isGroup || panningEnable)
                        return;
                    var isKeyboard = (isTouchPrimary && lastFocusWasScreenReader.current) ||
                        (!isTouchPrimary && e.detail === 0);
                    if (isKeyboard) {
                        if (!selectedTool.includes(data.id))
                            setSelectedTool([data.id]);
                        var rect = (_a = controllerBtnRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                        if (rect)
                            setContextMenu({ x: rect.right, y: rect.top, manipulativeId: data.id });
                    }
                }, onKeyDown: handleControllerKeyDown }), data.dimensionLabels && (_jsxs(_Fragment, { children: [_jsx("div", { className: "coloredTilesDimLabelsY", style: { height: height }, tabIndex: isPreview ? -1 : 0, role: "img", "aria-label": dimensionLabelsAria, children: rowLabelData.map(function (labelData, i) { return (_jsx("div", { className: "coloredTilesDimLabelCell", style: { height: labelData.height }, children: labelData.value }, i)); }) }), _jsx("div", { className: "coloredTilesDimLabelsX", style: { width: width }, "aria-hidden": "true", children: colLabelData.map(function (labelData, i) { return (_jsx("div", { className: "coloredTilesDimLabelCell is-bottom", style: { width: labelData.width }, children: labelData.value }, i)); }) })] })), data.generationMode === 'outline' && !isPreview && (_jsxs(_Fragment, { children: [_jsx("div", { className: "resizeHandle topLeft", "aria-hidden": true, onMouseDown: function (e) { return onResizeStart(e, 'topLeft'); }, onTouchStart: function (e) { return onResizeStart(e, 'topLeft'); } }), _jsx("div", { className: "resizeHandle bottomRight", "aria-hidden": true, onMouseDown: function (e) { return onResizeStart(e, 'bottomRight'); }, onTouchStart: function (e) { return onResizeStart(e, 'bottomRight'); } })] })), _jsxs("div", { className: "coloredTiles", "aria-hidden": !isPreview && !selectModeEnabled ? true : undefined, onMouseDown: onGridMouseDown, onTouchStart: onGridMouseDown, style: {
                    width: width,
                    height: height,
                    position: 'relative',
                    display: mode === 'grid' ? 'grid' : 'block',
                    gridTemplateColumns: mode === 'grid' ? "repeat(".concat(data.columns, ", ").concat(cellSize, "px)") : undefined,
                    gridTemplateRows: mode === 'grid' ? "repeat(".concat(data.rows, ", ").concat(cellSize, "px)") : undefined,
                    boxSizing: 'content-box',
                    filter: 'none',
                }, children: [rowCutPoints.slice(0, -1).map(function (rStart, rIdx) {
                        return colCutPoints.slice(0, -1).map(function (cStart, cIdx) {
                            var _a;
                            var rEnd = rowCutPoints[rIdx + 1];
                            var cEnd = colCutPoints[cIdx + 1];
                            var key = "".concat(rIdx, "-").concat(cIdx);
                            var bg = (_a = data.partColors) === null || _a === void 0 ? void 0 : _a[key];
                            var left = isOpen ? cStart * width : cStart * cellSize;
                            var top = isOpen ? rStart * height : rStart * cellSize;
                            var w = isOpen ? (cEnd - cStart) * width : (cEnd - cStart) * cellSize;
                            var h = isOpen ? (rEnd - rStart) * height : (rEnd - rStart) * cellSize;
                            var isSelected = selectedPartKeys.includes(key);
                            var isManual = data.generationMode === 'manual' || data.generationMode === 'outline';
                            var isRandom = data.generationMode === 'random';
                            var previewLeft = isOpen ? "".concat(cStart * 100, "%") : "".concat((cStart / data.columns) * 100, "%");
                            var previewTop = isOpen ? "".concat(rStart * 100, "%") : "".concat((rStart / data.rows) * 100, "%");
                            var previewW = isOpen
                                ? "".concat((cEnd - cStart) * 100, "%")
                                : "".concat(((cEnd - cStart) / data.columns) * 100, "%");
                            var previewH = isOpen
                                ? "".concat((rEnd - rStart) * 100, "%")
                                : "".concat(((rEnd - rStart) / data.rows) * 100, "%");
                            return (_jsx("div", { onClick: function (e) {
                                    if (isPreview || !selectModeEnabled)
                                        return;
                                    if (data.generationMode === 'outline')
                                        return;
                                    e.stopPropagation();
                                    // Select this part (or deselect if already selected)
                                    toggleSelectedPartKey(key);
                                }, style: {
                                    position: 'absolute',
                                    left: isPreview ? previewLeft : left,
                                    top: isPreview ? previewTop : top,
                                    width: isPreview ? previewW : w,
                                    height: isPreview ? previewH : h,
                                    backgroundColor: isManual || isRandom ? 'transparent' : bg,
                                    opacity: 1,
                                    cursor: selectModeEnabled && data.generationMode !== 'outline' ? 'pointer' : 'default',
                                    zIndex: 1,
                                    display: isRandom || (isManual && !bg) ? 'none' : 'block',
                                } }, key));
                        });
                    }), data.generationMode === 'outline' &&
                        openRegions.map(function (region) { return (_jsx("div", { style: {
                                position: 'absolute',
                                left: isPreview ? "".concat((region.x / width) * 100, "%") : region.x,
                                top: isPreview ? "".concat((region.y / height) * 100, "%") : region.y,
                                width: isPreview ? "".concat((region.w / width) * 100, "%") : region.w,
                                height: isPreview ? "".concat((region.h / height) * 100, "%") : region.h,
                                boxSizing: 'border-box',
                                cursor: 'pointer',
                            }, onClick: function () {
                                if (isPreview)
                                    return;
                                // later: apply selected color
                            } }, region.key)); }), Array.from({ length: data.rows }).map(function (_, r) { return (_jsx("div", { role: "row", "aria-rowindex": r + 1, style: { display: 'contents' }, children: Array.from({ length: data.columns }).map(function (_, c) {
                            var _a;
                            var cellColor = (_a = data.partColors) === null || _a === void 0 ? void 0 : _a["".concat(r, "-").concat(c)];
                            var cellKey = "cell-".concat(r, "-").concat(c);
                            var isSelected = selectedPartKeys.includes(cellKey);
                            return (_jsx("div", { ref: function (el) {
                                    if (!cellRefs.current[r])
                                        cellRefs.current[r] = [];
                                    cellRefs.current[r][c] = el;
                                }, className: "cell", role: selectModeEnabled ? 'button' : 'gridcell', "aria-rowindex": r + 1, "aria-colindex": c + 1, "aria-pressed": selectModeEnabled ? selectedPartKeys.includes("cell-".concat(r, "-").concat(c)) : undefined, "aria-label": selectModeEnabled ? "".concat(getCellAriaLabel(r, c)).concat(selectedPartKeys.includes("cell-".concat(r, "-").concat(c)) ? ", ".concat(t('text.selected')) : '') : undefined, "aria-hidden": selectModeEnabled ? undefined : true, tabIndex: selectModeEnabled ? 0 : -1, onKeyDown: function (e) { return handleCellKeyDown(e, r, c); }, onMouseDown: function (e) {
                                    // Skip extract for tiles in a multi-tile group (data.groupId) — let the
                                    // event bubble to the wrapper's onGridMouseDown for group drag.
                                    if (data.isGroup !== true && !data.groupId) {
                                        // Part of an ad-hoc multi-selection: move the WHOLE tile (and the rest
                                        // of the selection) together, same as grabbing any other manipulative —
                                        // don't extract a single cell out from under a group drag.
                                        if (selectedTool.length > 1 && selectedTool.includes(data.id)) {
                                            e.stopPropagation();
                                            onGridMouseDown(e);
                                            return;
                                        }
                                        var cColor = data.generationMode === 'random'
                                            ? (cellColor !== null && cellColor !== void 0 ? cellColor : TILE_COLORS[(r * 2 + c) % 4])
                                            : (data.generationMode === 'manual' || data.generationMode === 'outline') &&
                                                cellColor
                                                ? cellColor
                                                : undefined;
                                        if (cColor) {
                                            handleCellDragStart(e, r, c, cColor);
                                        }
                                    }
                                }, onTouchStart: function (e) {
                                    if (data.isGroup !== true && !data.groupId) {
                                        if (selectedTool.length > 1 && selectedTool.includes(data.id)) {
                                            e.stopPropagation();
                                            onGridMouseDown(e);
                                            return;
                                        }
                                        var cColor = data.generationMode === 'random'
                                            ? (cellColor !== null && cellColor !== void 0 ? cellColor : TILE_COLORS[(r * 2 + c) % 4])
                                            : (data.generationMode === 'manual' || data.generationMode === 'outline') &&
                                                cellColor
                                                ? cellColor
                                                : undefined;
                                        if (cColor) {
                                            handleCellDragStart(e, r, c, cColor);
                                        }
                                    }
                                }, onClick: function (e) {
                                    var _a;
                                    if (isPreview || ((_a = data.lock) === null || _a === void 0 ? void 0 : _a.editing))
                                        return;
                                    e.stopPropagation();
                                    if (selectModeEnabled) {
                                        var isNowSelected = !selectedPartKeys.includes(cellKey);
                                        toggleSelectedPartKey(cellKey);
                                        announce(t(isNowSelected
                                            ? 'text.coloredTilesCellSelected'
                                            : 'text.coloredTilesCellDeselected', { row: r + 1, col: c + 1 }));
                                    }
                                }, style: __assign({ visibility: ((data.generationMode === 'manual' || data.generationMode === 'outline') &&
                                        !cellColor &&
                                        !isExternalBlockDragging &&
                                        !isSelected) ||
                                        (data.generationMode === 'random' &&
                                            cellColor === 'transparent' &&
                                            !isExternalBlockDragging)
                                        ? 'hidden'
                                        : 'visible', position: 'relative', zIndex: 3, pointerEvents: (data.generationMode === 'outline' &&
                                        !selectModeEnabled &&
                                        (data.isGroup !== false || isGroup)) ||
                                        isPreview
                                        ? 'none'
                                        : 'auto', cursor: !isPreview ? 'pointer' : 'default', border: isExternalBlockDragging &&
                                        (data.generationMode === 'outline' ||
                                            (data.generationMode === 'random' && cellColor === 'transparent'))
                                        ? '1px solid rgba(14, 154, 178, 0.5)'
                                        : 'none', boxSizing: 'border-box', boxShadow: 'inset 0 0 0 1px white', willChange: 'transform, filter', transform: 'translateZ(0)', WebkitTransform: 'translate(0)', backgroundColor: data.generationMode === 'random'
                                        ? (cellColor !== null && cellColor !== void 0 ? cellColor : TILE_COLORS[(r * 2 + c) % 4])
                                        : (data.generationMode === 'manual' || data.generationMode === 'outline') &&
                                            cellColor
                                            ? cellColor
                                            : 'transparent' }, ((dragHoverCell === null || dragHoverCell === void 0 ? void 0 : dragHoverCell.r) === r && (dragHoverCell === null || dragHoverCell === void 0 ? void 0 : dragHoverCell.c) === c
                                    ? {
                                        backgroundColor: 'rgba(14, 154, 178, 0.3)',
                                        border: '2px solid #0E9AB2',
                                        boxShadow: 'none',
                                    }
                                    : {})), children: _jsx("span", { style: {
                                        position: 'absolute',
                                        width: '1px',
                                        height: '1px',
                                        padding: 0,
                                        margin: '-1px',
                                        overflow: 'hidden',
                                        clip: 'rect(0, 0, 0, 0)',
                                        clipPath: 'inset(50%)',
                                        whiteSpace: 'nowrap',
                                        border: 0,
                                        // Zero the TEXT layout extent (not just the rendering) so
                                        // VoiceOver's cursor doesn't size itself to the label text and
                                        // spill across neighbouring cells — it falls back to the cell.
                                        fontSize: 0,
                                        lineHeight: 0,
                                    }, children: getCellAriaLabel(r, c) }) }, c));
                        }) }, r)); }), _jsx("div", { className: "gridBorderOverlay", style: __assign({ position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none' }, ((data.generationMode !== 'manual' || isPreview) && {
                            outline: '5px solid #0c9bb3',
                            outlineOffset: '1.2px',
                            borderRadius: 1,
                        })) }), data.generationMode === 'manual' && isFocused && !isPreview && focusOutlinePath && (_jsx("svg", { className: "manualFocusOutline", style: {
                            position: 'absolute',
                            inset: -5, // Provide buffer for 5px stroke
                            width: width + 10,
                            height: height + 10,
                            zIndex: 10,
                            pointerEvents: 'none',
                            overflow: 'visible',
                        }, viewBox: "-5 -5 ".concat(width + 10, " ").concat(height + 10), children: _jsx("path", { d: focusOutlinePath, stroke: "#0c9bb3", strokeWidth: "5", fill: "none", strokeLinejoin: "round", strokeLinecap: "round" }) })), shouldShowRedLinearPieces && (_jsxs(_Fragment, { children: [data.topLinearSegments &&
                                data.topLinearSegments.length > 0 &&
                                Array.from({ length: Math.ceil(data.columns) }).map(function (_, i) {
                                    var _a, _b;
                                    var show = (_a = data.topLinearSegments) === null || _a === void 0 ? void 0 : _a.includes(i);
                                    if (!show)
                                        return null;
                                    // Ensure "Linear Pieces" are not applied to empty blocks
                                    var cellColor = (_b = data.partColors) === null || _b === void 0 ? void 0 : _b["0-".concat(i)];
                                    if (data.generationMode === 'manual') {
                                        if (!cellColor && !isPreview)
                                            return null;
                                    }
                                    else if (data.generationMode === 'random') {
                                        if (cellColor === 'transparent' && !isPreview)
                                            return null;
                                    }
                                    var isLastPartial = i === Math.floor(data.columns) && data.columns % 1 > 0;
                                    var segmentWidth = isLastPartial ? (data.columns % 1) * cellSize : cellSize;
                                    var key = "red-top-".concat(i);
                                    var isSelected = selectedPartKeys.includes(key);
                                    return (_jsx("div", { className: "redLinearPiece top", onClick: function (e) {
                                            if (isPreview || !selectModeEnabled)
                                                return;
                                            e.stopPropagation();
                                            toggleSelectedPartKey(key);
                                        }, style: {
                                            left: i * cellSize + LINEAR_PIECE_MARGIN,
                                            width: segmentWidth - LINEAR_PIECE_MARGIN * 2,
                                            cursor: selectModeEnabled ? 'pointer' : 'default',
                                            border: isSelected ? '5px solid #293273' : '1.4px solid #000',
                                            boxSizing: 'border-box',
                                            pointerEvents: selectModeEnabled ? 'auto' : 'none',
                                        } }, key));
                                }), data.leftLinearSegments &&
                                data.leftLinearSegments.length > 0 &&
                                Array.from({ length: Math.ceil(data.rows) }).map(function (_, i) {
                                    var _a, _b;
                                    var show = (_a = data.leftLinearSegments) === null || _a === void 0 ? void 0 : _a.includes(i);
                                    if (!show)
                                        return null;
                                    // Ensure "Linear Pieces" are not applied to empty blocks
                                    var cellColor = (_b = data.partColors) === null || _b === void 0 ? void 0 : _b["".concat(i, "-0")];
                                    if (data.generationMode === 'manual') {
                                        if (!cellColor && !isPreview)
                                            return null;
                                    }
                                    else if (data.generationMode === 'random') {
                                        if (cellColor === 'transparent' && !isPreview)
                                            return null;
                                    }
                                    var isLastPartial = i === Math.floor(data.rows) && data.rows % 1 > 0;
                                    var segmentHeight = isLastPartial ? (data.rows % 1) * cellSize : cellSize;
                                    var key = "red-left-".concat(i);
                                    var isSelected = selectedPartKeys.includes(key);
                                    return (_jsx("div", { className: "redLinearPiece left", onClick: function (e) {
                                            if (isPreview || !selectModeEnabled)
                                                return;
                                            e.stopPropagation();
                                            toggleSelectedPartKey(key);
                                        }, style: {
                                            top: i * cellSize + LINEAR_PIECE_MARGIN,
                                            height: segmentHeight - LINEAR_PIECE_MARGIN * 2,
                                            cursor: selectModeEnabled ? 'pointer' : 'default',
                                            border: isSelected ? '5px solid #293273' : '1.4px solid #000',
                                            boxSizing: 'border-box',
                                            pointerEvents: selectModeEnabled ? 'auto' : 'none',
                                        } }, key));
                                })] })), properRectangle &&
                        !isPreview &&
                        renderedRowCuts.map(function (y, i) {
                            // Get the actual cut value (index for grid, percentage for open)
                            // For open mode, derive from pixel position since renderedRowCuts is already filtered
                            var cutValue = mode === 'grid' ? cutRows[i] : y / height;
                            // Hide the cut being re-dragged (it becomes the preview)
                            if ((draggingExistingCut === null || draggingExistingCut === void 0 ? void 0 : draggingExistingCut.type) === 'row') {
                                if (mode === 'grid' && cutRows[i] === draggingExistingCut.value)
                                    return null;
                            }
                            return (_jsxs(React.Fragment, { children: [_jsx("div", { className: "cutLine horizontal", "aria-hidden": "true", style: {
                                            top: y - 2.5,
                                        } }), _jsx("div", { className: "cutArrowWrapper row", role: "button", tabIndex: !isPreview && partModeEnabled ? 0 : -1, "aria-label": t('text.cutArrowRowLabel'), "aria-hidden": !partModeEnabled || isPreview || selectModeEnabled ? true : undefined, style: {
                                            top: y - 17,
                                            pointerEvents: !isPreview && partModeEnabled ? 'auto' : 'none',
                                            cursor: !isPreview && partModeEnabled ? 'ns-resize' : 'default',
                                        }, onMouseDown: function (e) { return onExistingCutStart(e, 'row', cutValue); }, onTouchStart: function (e) { return onExistingCutStart(e, 'row', cutValue); }, onKeyDown: function (e) { return handleCutKeyDown(e, 'row', cutValue); }, children: _jsx("svg", { width: "35", height: "35", viewBox: "0 0 24 24", children: _jsx("path", { d: "M17 4c1 0 1 1 1 2v12c0 1 0 2-1 2-.5 0-1-.3-2-1.1l-6.5-5.4c-.7-.6-.7-2.4 0-3L15 5.1c1-.8 1.5-1.1 2-1.1", fill: "currentColor" }) }) })] }, "row-cut-".concat(i)));
                        }), properRectangle &&
                        renderedColCuts.map(function (x, i) {
                            // Get the actual cut value (index for grid, percentage for open)
                            // For open mode, derive from pixel position since renderedColCuts is already filtered
                            var cutValue = mode === 'grid' ? cutColumns[i] : x / width;
                            // Hide the cut being re-dragged (it becomes the preview)
                            if ((draggingExistingCut === null || draggingExistingCut === void 0 ? void 0 : draggingExistingCut.type) === 'col') {
                                if (mode === 'grid' && cutColumns[i] === draggingExistingCut.value)
                                    return null;
                            }
                            return (_jsxs(React.Fragment, { children: [_jsx("div", { className: "cutLine vertical", "aria-hidden": "true", style: {
                                            left: x - 2.5,
                                        } }), _jsx("div", { className: "cutArrowWrapper col", role: "button", tabIndex: !isPreview && partModeEnabled ? 0 : -1, "aria-label": t('text.cutArrowColLabel'), "aria-hidden": !partModeEnabled || isPreview || selectModeEnabled ? true : undefined, style: {
                                            left: x - 17,
                                            pointerEvents: !isPreview && partModeEnabled ? 'auto' : 'none',
                                            cursor: !isPreview && partModeEnabled ? 'ew-resize' : 'default',
                                        }, onMouseDown: function (e) { return onExistingCutStart(e, 'col', cutValue); }, onTouchStart: function (e) { return onExistingCutStart(e, 'col', cutValue); }, onKeyDown: function (e) { return handleCutKeyDown(e, 'col', cutValue); }, children: _jsx("svg", { width: "35", height: "35", viewBox: "0 0 24 24", children: _jsx("path", { d: "M4 17c0 1 1 1 2 1h12c1 0 2 0 2-1 0-.5-.3-1-1.1-2l-5.4-6.5c-.6-.7-2.4-.7-3 0L5.1 15C4.3 16 4 16.5 4 17", fill: "currentColor" }) }) })] }, "col-cut-".concat(i)));
                        }), selectModeEnabled && !isPreview && (_jsxs(_Fragment, { children: [rowCutPoints.slice(0, -1).map(function (rStart, rIdx) {
                                return colCutPoints.slice(0, -1).map(function (cStart, cIdx) {
                                    var key = "".concat(rIdx, "-").concat(cIdx);
                                    if (!selectedPartKeys.includes(key))
                                        return null;
                                    var rEnd = rowCutPoints[rIdx + 1];
                                    var cEnd = colCutPoints[cIdx + 1];
                                    var left = isOpen ? cStart * width : cStart * cellSize;
                                    var top = isOpen ? rStart * height : rStart * cellSize;
                                    var w = isOpen ? (cEnd - cStart) * width : (cEnd - cStart) * cellSize;
                                    var h = isOpen ? (rEnd - rStart) * height : (rEnd - rStart) * cellSize;
                                    return (_jsx("div", { style: {
                                            position: 'absolute',
                                            left: left,
                                            top: top,
                                            width: w,
                                            height: h,
                                            zIndex: 6,
                                            outline: '5px solid #293273',
                                            outlineOffset: '-3px',
                                            pointerEvents: 'none',
                                            boxSizing: 'border-box',
                                        } }, "sel-section-".concat(key)));
                                });
                            }), Array.from({ length: data.rows * data.columns }).map(function (_, i) {
                                var r = Math.floor(i / data.columns);
                                var c = i % data.columns;
                                var cellKey = "cell-".concat(r, "-").concat(c);
                                if (!selectedPartKeys.includes(cellKey))
                                    return null;
                                return (_jsx("div", { style: {
                                        position: 'absolute',
                                        left: c * cellSize,
                                        top: r * cellSize,
                                        width: cellSize,
                                        height: cellSize,
                                        zIndex: 6,
                                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                        border: '5px solid #293273',
                                        boxSizing: 'border-box',
                                        pointerEvents: 'none',
                                    } }, "sel-cell-".concat(cellKey)));
                            })] })), cutPreview && (_jsxs(_Fragment, { children: [_jsx("div", { className: "previewLine ".concat(cutPreview.type === 'row' ? 'horizontal' : 'vertical'), style: {
                                    top: cutPreview.type === 'row' ? cutPreview.value - 2.5 : 0,
                                    left: cutPreview.type === 'col' ? cutPreview.value - 2.5 : 0,
                                } }), cutPreview.type === 'row' && (_jsx("div", { className: "previewTriangleWrapper row", style: {
                                    top: cutPreview.value - 17,
                                }, children: _jsx("svg", { width: "35", height: "35", viewBox: "0 0 24 24", children: _jsx("path", { d: "M17 4c1 0 1 1 1 2v12c0 1 0 2-1 2-.5 0-1-.3-2-1.1l-6.5-5.4c-.7-.6-.7-2.4 0-3L15 5.1c1-.8 1.5-1.1 2-1.1Z", fill: "#ff9800", stroke: "#ff9800" }) }) })), cutPreview.type === 'col' && (_jsx("div", { className: "previewTriangleWrapper col", style: {
                                    left: cutPreview.value - 17,
                                }, children: _jsx("svg", { width: "35", height: "35", viewBox: "0 0 24 24", children: _jsx("path", { d: "M4 17c0 1 1 1 2 1h12c1 0 2 0 2-1 0-.5-.3-1-1.1-2l-5.4-6.5c-.6-.7-2.4-.7-3 0L5.1 15C4.3 16 4 16.5 4 17Z", fill: "#ff9800", stroke: "#ff9800" }) }) }))] }))] }), !isPreview && partModeEnabled && (_jsxs(_Fragment, { children: [_jsxs("div", { ref: cutArrowRightRef, className: "cutArrow cutArrowRight ".concat(rowCutsMaxed || !properRectangle ? 'disabled' : ''), role: "button", tabIndex: isPreview || rowCutsMaxed || !properRectangle ? -1 : 0, "aria-label": t('text.coloredTilesCutArrowRow'), "aria-disabled": rowCutsMaxed || !properRectangle ? true : undefined, "aria-hidden": rowCutsMaxed || !properRectangle ? true : undefined, onMouseDown: function (e) { return !rowCutsMaxed && properRectangle && onCutStart(e, 'row'); }, onTouchStart: function (e) { return !rowCutsMaxed && properRectangle && onCutStart(e, 'row'); }, onKeyDown: function (e) {
                            var _a;
                            if (isPreview || rowCutsMaxed || !properRectangle)
                                return;
                            if (((_a = kbCutModeRef.current) === null || _a === void 0 ? void 0 : _a.type) === 'row') {
                                handleKbCutKeyDown(e, 'row');
                            }
                            else if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                startKbCut('row');
                            }
                        }, onMouseEnter: function () { return !rowCutsMaxed && properRectangle && setHoveredArrow('row'); }, onMouseLeave: function () { return setHoveredArrow(null); }, children: [_jsx("svg", { width: "35", height: "35", viewBox: "0 0 24 24", children: _jsx("path", { d: "M17 4c1 0 1 1 1 2v12c0 1 0 2-1 2-.5 0-1-.3-2-1.1l-6.5-5.4c-.7-.6-.7-2.4 0-3L15 5.1c1-.8 1.5-1.1 2-1.1", fill: "currentColor" }) }), hoveredArrow === 'row' && _jsx("div", { className: "cutArrowTooltip", children: "Create Parts" })] }), _jsxs("div", { ref: cutArrowBottomRef, className: "cutArrow cutArrowBottom ".concat(colCutsMaxed || !properRectangle ? 'disabled' : ''), role: "button", tabIndex: isPreview || colCutsMaxed || !properRectangle ? -1 : 0, "aria-label": t('text.coloredTilesCutArrowCol'), "aria-disabled": colCutsMaxed || !properRectangle ? true : undefined, "aria-hidden": colCutsMaxed || !properRectangle ? true : undefined, onMouseDown: function (e) { return !colCutsMaxed && properRectangle && onCutStart(e, 'col'); }, onTouchStart: function (e) { return !colCutsMaxed && properRectangle && onCutStart(e, 'col'); }, onKeyDown: function (e) {
                            var _a;
                            if (isPreview || colCutsMaxed || !properRectangle)
                                return;
                            if (((_a = kbCutModeRef.current) === null || _a === void 0 ? void 0 : _a.type) === 'col') {
                                handleKbCutKeyDown(e, 'col');
                            }
                            else if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                startKbCut('col');
                            }
                        }, onMouseEnter: function () { return !colCutsMaxed && properRectangle && setHoveredArrow('col'); }, onMouseLeave: function () { return setHoveredArrow(null); }, children: [_jsx("svg", { width: "35", height: "35", viewBox: "0 0 24 24", children: _jsx("path", { d: "M4 17c0 1 1 1 2 1h12c1 0 2 0 2-1 0-.5-.3-1-1.1-2l-5.4-6.5c-.6-.7-2.4-.7-3 0L5.1 15C4.3 16 4 16.5 4 17", fill: "currentColor" }) }), hoveredArrow === 'col' && _jsx("div", { className: "cutArrowTooltip", children: "Create Parts" })] })] })), !isPreview && kbCutMode && (_jsxs("div", { ref: cutMovePanelRef, className: "cutMovePanel", role: "group", "aria-label": kbCutMode.type === 'row'
                    ? t('text.arrayModelMoveCutRow', { defaultValue: 'Move horizontal cut' })
                    : t('text.arrayModelMoveCutCol', { defaultValue: 'Move vertical cut' }), children: [kbCutMode.type === 'row' ? (_jsxs(_Fragment, { children: [_jsx("button", { className: "cutMoveBtn", "aria-label": t('text.moveUp', { defaultValue: 'Move up' }), disabled: cutPanelDisabled.back, onPointerDown: function (e) { return e.stopPropagation(); }, onClick: function (e) { e.stopPropagation(); moveKbCut(-1); }, children: "\u25B2" }), _jsx("button", { className: "cutMoveBtn", "aria-label": t('text.moveDown', { defaultValue: 'Move down' }), disabled: cutPanelDisabled.fwd, onPointerDown: function (e) { return e.stopPropagation(); }, onClick: function (e) { e.stopPropagation(); moveKbCut(1); }, children: "\u25BC" })] })) : (_jsxs(_Fragment, { children: [_jsx("button", { className: "cutMoveBtn", "aria-label": t('text.moveLeft', { defaultValue: 'Move left' }), disabled: cutPanelDisabled.back, onPointerDown: function (e) { return e.stopPropagation(); }, onClick: function (e) { e.stopPropagation(); moveKbCut(-1); }, children: "\u25C0" }), _jsx("button", { className: "cutMoveBtn", "aria-label": t('text.moveRight', { defaultValue: 'Move right' }), disabled: cutPanelDisabled.fwd, onPointerDown: function (e) { return e.stopPropagation(); }, onClick: function (e) { e.stopPropagation(); moveKbCut(1); }, children: "\u25B6" })] })), _jsx("button", { className: "cutMoveBtn cutMoveBtnConfirm", "aria-label": t('text.confirm', { defaultValue: 'Confirm' }), disabled: cutPanelDisabled.confirm, onPointerDown: function (e) { return e.stopPropagation(); }, onClick: function (e) { e.stopPropagation(); commitKbCut(); }, children: "\u2713" }), _jsx("button", { className: "cutMoveBtn cutMoveBtnCancel", "aria-label": t('text.cancel', { defaultValue: 'Cancel' }), onPointerDown: function (e) { return e.stopPropagation(); }, onClick: function (e) { e.stopPropagation(); setKbCutMode(null); setCutPreview(null); }, children: "\u2715" })] })), mode === 'grid' &&
                !isPreview &&
                (properRectangle || data.generationMode === 'outline') &&
                (showProduct || showExpression) && (_jsxs("div", { className: "resultDisplayContainer", style: {
                    top: height + 70,
                    left: width / 2,
                    width: width + width / 3,
                }, children: [showExpression && (_jsx("div", { className: "expressionContainer", style: { width: width + width / 3 }, tabIndex: isPreview ? -1 : 0, role: "img", "aria-label": expressionAria, children: _jsx("div", { className: "expressionInner", children: (function () {
                                var parts = [];
                                var idx = 0;
                                rowSegments.forEach(function (r, rIdx) {
                                    colSegments.forEach(function (c, cIdx) {
                                        var _a;
                                        var termColor = (_a = sectionSolidColors["".concat(rIdx, "-").concat(cIdx)]) !== null && _a !== void 0 ? _a : EXPRESSION_COLORS[idx % EXPRESSION_COLORS.length];
                                        parts.push(_jsxs("span", { "aria-hidden": "true", style: { color: termColor, whiteSpace: 'nowrap' }, children: ["(", r, " \u00D7 ", c, ")"] }, "expr-".concat(rIdx, "-").concat(cIdx)));
                                        if (idx < rowSegments.length * colSegments.length - 1) {
                                            parts.push(_jsxs("span", { "aria-hidden": "true", children: [' ', "+", ' '] }, "plus-".concat(rIdx, "-").concat(cIdx)));
                                        }
                                        idx++;
                                    });
                                });
                                return (_jsxs(_Fragment, { children: [parts, _jsxs("span", { "aria-hidden": "true", style: { whiteSpace: 'nowrap' }, children: [' ', "=", ' ', _jsxs("b", { children: ["(", data.rows, " \u00D7 ", data.columns, ")"] })] })] }));
                            })() }) })), showProduct && (_jsx("div", { className: "productContainer".concat(showExpression ? ' margin-top' : ''), style: { textAlign: 'center' }, tabIndex: isPreview ? -1 : 0, role: "img", "aria-label": productAria, children: _jsx("div", { style: {
                                display: 'inline-block',
                                maxWidth: '100%',
                                padding: '10px 17px',
                                border: '1px solid #5063ae',
                                fontWeight: 700,
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                borderRadius: 5,
                            }, children: (function () {
                                var parts = [];
                                var flatIdx = 0;
                                rowSegments.forEach(function (_, rIdx) {
                                    colSegments.forEach(function (_, cIdx) {
                                        var _a;
                                        var termColor = (_a = sectionSolidColors["".concat(rIdx, "-").concat(cIdx)]) !== null && _a !== void 0 ? _a : EXPRESSION_COLORS[flatIdx % EXPRESSION_COLORS.length];
                                        parts.push(_jsx("span", { "aria-hidden": "true", style: { color: termColor }, children: products[flatIdx] }, "prod-".concat(rIdx, "-").concat(cIdx)));
                                        if (flatIdx < products.length - 1) {
                                            parts.push(_jsxs("span", { "aria-hidden": "true", children: [' ', "+", ' '] }, "prodplus-".concat(rIdx, "-").concat(cIdx)));
                                        }
                                        flatIdx++;
                                    });
                                });
                                return (_jsxs(_Fragment, { children: [parts, _jsxs("span", { "aria-hidden": "true", children: [' ', "= ", _jsx("b", { children: total })] })] }));
                            })() }) }))] }))] })));
};
export default ColoredTiles;
